import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RUN_SCENARIOS_QUERY_KEY } from 'lib/evaluation-case/query-keys';
import {
  runCaseScenarioId,
  stampedScenarioId,
} from 'lib/evaluation-case/run-scenario';
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  hashKey,
  keepPreviousData,
  type InfiniteData,
  type QueryClient,
  type QueryObserverOptions,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useForm, useStore } from '@tanstack/react-form';
import { useHistory } from 'react-router-dom';
import type { Expression } from 'mapbox-gl';
import moment from 'moment';
import { debounce, isEqual } from 'lodash-es';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { sessionResourceAtom } from 'atoms/root';
import type {
  DataTableSelectionState,
  TableState,
} from 'components/data-table/types';
import type { BulkEditAfeOptions } from 'components/nri-table/bulk-edit-slots';
import type {
  FilterValueOption,
  FilterValueSearch,
  VolumeFilterBy,
  VolumeForecastType,
  VolumeSourcePickerData,
} from 'components/volume-source-picker';
import type { AfeCatalogResult } from 'pages/afe-catalog/types';
import { HTTPError } from 'ky';
import {
  ApiEnvelopeError,
  readErrorEnvelope,
  throwTypedEnvelopeError,
} from 'lib/evaluation-case/api-envelope';
import { calculatingInterval } from 'lib/evaluation-case/polling';
import {
  caseQueryOptions,
  retryUnlessClientError,
  type RetrySetting,
} from 'lib/evaluation-case/case-query';
import { api } from 'lib/request/api';
import { subscribe } from 'lib/pusher';
import { useJobCompletion } from 'lib/hooks/useJobCompletion';
import {
  canSeedQuery,
  seedQueryFromPending,
} from 'lib/react-query/seed-query-from-pending';
import { downloadFromUrl } from 'utils/download-blob';
import {
  subtractCoverageLabel,
  withSubtractCoverage,
} from 'utils/subtract-coverage';
import type { ExportImageOptions } from 'components/chart-export';
import type { BBox } from 'components/query-map/query-map';
import type {
  AggregatedFreeCashFlowResultResponse,
  AvailableAfe,
  AvailableAfesResponse,
  AvailableForecastsResponse,
  ScenariosResponse,
} from 'types/api-responses';
import type {
  PackageCompareRunsResponse,
  PackageResponse,
} from 'types/packages-api/responses';
import { useEval3Page, useOptionalEval3Page } from './context';
import {
  ScheduleCalculateRejectedError,
  parseRejections,
} from './schedule-dialog/edit-ops';
import {
  typeCurveScenarioIdAtom,
  typeCurveOverridesAtom,
  inventoryPackageOverridesAtom,
  pendingTypeCurveWireAtom,
  scheduleOverrideRejectionsAtom,
  chartGridAtom,
  chartsAtom,
  DEFAULT_CHART_GRID,
  locatedRowAtom,
  mapHighlightAtom,
  mapScopeAtom,
  interestModelableOnlyAtom,
  pinResetSignalAtom,
  scopeViewAtom,
  serverFiltersAtom,
  serverPopulationModeAtom,
  type ChildHighlight,
} from './atoms';
import {
  adaptMasterDataResponse,
  adaptRollupOptions,
  adaptValueRollupPage,
  buildContentExportFormData,
  type ContentExportChartBlob,
  buildValueRollupRequest,
  buildVisualSummaryRequest,
  calcModeField,
  ltdSlotHighlightFilter,
  scenarioField,
  toTypeCurveOverrideWire,
  toInventoryPackageOverrideWire,
  CASE_DOMAIN_ENDPOINTS,
  defaultWorkingConfig,
  deriveSort,
  deserializeInterestItems,
  EMPTY_CASE_OVERRIDES,
  encodeTypeCurveOverrides,
  encodeInventoryPackageOverrides,
  eval3EvalPath,
  eval3SavedEvalsPath,
  eval3WorkspacePath,
  evalDisplayName,
  hasAreaOverrides,
  interestItemsDiverged,
  mergeColorElements,
  polygonHighlightFilter,
  rollupBinParams,
  rollupHasGrouping,
  slotHighlightFilter,
  slotLateralFilters,
  toColorGroups,
  toSlotOverridesWire,
  typeCurveOverridesFromLog,
  inventoryPackageOverridesFromLog,
  runScenarioOverrides,
  seedPriceDeckId,
  withoutAreaOverrides,
  type RowVolumeSourcePair,
} from './utils';
import { makeCarryGroup } from './interest-modeling-carry';
import {
  electionGroupsOf,
  makeElectionGroup,
  makeElectionSeed,
} from './interest-modeling-election';
import type {
  AggregationKey,
  AssetRow,
  CalcDetail,
  CalcStatus,
  CalculateRequest,
  CalculateResponse,
  CarryGroup,
  ElectionGroup,
  CarryWell,
  CaseCoverageListResponse,
  CaseDomain,
  CashflowPeriod,
  CashflowSort,
  CashflowsByMonthPage,
  ColorElements,
  ColorGroup,
  ColorRule,
  ContentExport202Response,
  ContentExportPayload,
  ContentExportResult,
  CreateWorkspaceRequest,
  DcaScenarioItem,
  DomainScenarioOption,
  ElectionVariant,
  EvaluationCaseDetail,
  RunScenariosResponse,
  EvaluationWorkspace,
  EvaluationWorkspacesResponse,
  ExecutiveSummaryRequest,
  ExecutiveSummaryResponse,
  FilterOptionsResponse,
  InterestItem,
  MapElements,
  MapElementsRequest,
  MapScope,
  MasterDataRequest,
  MasterDataResponse,
  MasterDataSort,
  MasterDataView,
  PriceDeckOption,
  RollupBin,
  RollupOptions,
  RollupOptionsResponse,
  RollupPage,
  RollupSort,
  ServerFilter,
  TableFilter,
  WarehouseColumnFilter,
  SlotAllocationRow,
  SlotOverrideLogEntry,
  SlotOverrideOperation,
  SlotRow,
  UncoveredResult,
  AffectedSlotsResponse,
  SavedEvaluationsResponse,
  UpdateWorkspaceRequest,
  ValueRollupResponse,
  VisualSummaryResponse,
  VisualSummaryRow,
  WorkingConfig,
  WorkspaceCalculation,
  WorkspaceCalculationsResponse,
  WorkspaceEvalRow,
  TypeCurveOverride,
  InventoryPackageOverride,
  TypeCurveOverrideWire,
  InventoryPackageOverrideWire,
  TypeCurveScenarioOption,
  TypeCurveSummaryResponse,
  PartialTractsResponse,
} from './types';
import type {
  Chart,
  ChartCatalogEntry,
  ChartCatalogRequest,
  ChartCatalogResponse,
  ChartConfig,
  ChartFieldOptionsRequest,
  ChartFieldOptionsResponse,
  ChartInstanceConfig,
  ChartSeriesRequest,
  ChartSeriesResponse,
  ChartSpec,
} from './custom-chart/types';

/**
 * Sentinels for the once-per-eval Type Curve overrides seed (see useWorkingConfigForm).
 * UNSEEDED is a unique reference so "never seeded" can't collide with any real eval
 * identity; DRAFT_SEED_KEY names the draft state, whose calculationId is absent.
 */
const UNSEEDED = Symbol('unseeded');
const DRAFT_SEED_KEY = Symbol('draft');

/**
 * TEMPORARY — DECREASE LATER (back toward ky's 30s default). Several eval3-new
 * endpoints (`filter_options`, `master_data`, the package fetch) run the heavy
 * slot-allocation builder and exceed ky's 30s default for large packages, so the
 * page can't load in dev. Raised to 300s for every eval3-new request as a stopgap
 * while the BE optimizes those queries. NOT a real fix — prod's rack-timeout caps
 * the server regardless; drop this back down once the queries are fast.
 */
const EVAL3_REQUEST_TIMEOUT_MS = 300_000;

/**
 * Ceiling for a single chart image capture during content export. A capture normally takes
 * well under a second; this is a safety net so one wedged chart can never hang the whole
 * export (the capture loop is sequential). On timeout the capture is treated as failed — that
 * chart is skipped and logged — the rest still export. The abandoned work isn't cancelled.
 */
const CHART_CAPTURE_TIMEOUT_MS = 20_000;

/**
 * Races a chart capture against {@link CHART_CAPTURE_TIMEOUT_MS} so a stalled capture rejects
 * instead of hanging the export loop. The timer is cleared when the capture wins.
 *
 * @param {Promise<Blob | null>} capturePromise - The in-flight `exportToBlob` result.
 * @param {string} key - The chart key, for the timeout error message.
 * @return {Promise<Blob | null>} The capture result, or a rejection if it times out.
 * @throws {Error} When the capture doesn't settle within the timeout.
 */
function withCaptureTimeout(
  capturePromise: Promise<Blob | null>,
  key: string
): Promise<Blob | null> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new Error(
            `chart "${key}" capture timed out after ${CHART_CAPTURE_TIMEOUT_MS}ms`
          )
        ),
      CHART_CAPTURE_TIMEOUT_MS
    );
  });
  return Promise.race([capturePromise, timeout]).finally(() =>
    clearTimeout(timer)
  );
}

/** Stable prefix for every master-data query key (enables scoped invalidation). */
export const MASTER_DATA_QUERY_KEY = 'eval3-new:master-data' as const;

/**
 * Endpoint path for the master-data table source. No leading slash — the `api`
 * (ky) instance sets `prefixUrl`, so paths must be relative.
 */
export const MASTER_DATA_URL = 'queries/packages/master_data';

/** Query-key prefix for the package fetch (its bbox drives the map's default framing). */
export const PACKAGE_QUERY_KEY = 'eval3-new:package' as const;

/**
 * The shared `GET /packages/:id` fetch. Every package-scoped selector below —
 * {@link usePackageBounds} (the map's default framing) and {@link usePackageName}
 * (the Evals-tree header) — reads from this ONE query: they pass the same `queryKey`
 * + `queryFn`, so React Query dedupes to a single request and cache entry per page.
 * The package is session-stable, so the query never goes stale (`staleTime: Infinity`).
 *
 * @param {number} packageId - The package to fetch.
 * @return {UseQueryResult<PackageResponse, Error>} The package query result.
 */
function usePackageQuery(
  packageId: number
): UseQueryResult<PackageResponse, Error> {
  return useQuery<PackageResponse, Error>({
    queryKey: [PACKAGE_QUERY_KEY, packageId],
    queryFn: ({ signal }) =>
      api
        .get(`packages/${packageId}`, {
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<PackageResponse>(),
    staleTime: Infinity,
  });
}

/**
 * Returns the package's bounding box as a flat `BBox`
 * (`[minLng, minLat, maxLng, maxLat]`), or `null` when the package has none.
 * `PackageMap` uses this as the map's default framing when no row is pinned, so the
 * map opens on the package extent. Reads from the shared {@link usePackageQuery}.
 *
 * @param {number} packageId - The package whose bounding box to load.
 * @return {BBox | null} The package's flat bbox, or `null` when it has none / is still loading.
 */
export function usePackageBounds(packageId: number): BBox | null {
  const query = usePackageQuery(packageId);
  // React Query keeps `data` referentially stable, so memoizing on `boundingBox`
  // gives a stable flat-bbox identity — the map's fitToBounds memo won't churn.
  // The BE returns corner pairs (`[[minLng,minLat],[maxLng,maxLat]]`); flatten to
  // the `BBox` shape (`[minLng,minLat,maxLng,maxLat]`) `fitToBounds` expects.
  const boundingBox = query.data?.boundingBox;
  return useMemo(() => {
    if (!boundingBox) {
      return null;
    }
    const [[minLng, minLat], [maxLng, maxLat]] = boundingBox;
    return [minLng, minLat, maxLng, maxLat];
  }, [boundingBox]);
}

/** Query-key prefix for the package's saved Package Compares (enables scoped invalidation). */
export const PACKAGE_COMPARE_RUNS_QUERY_KEY =
  'eval3-new:package-compare-runs' as const;

/**
 * Co-locates the saved Package Compares' key and fetcher. Keyed by package so
 * switching packages refetches.
 * @param {number} packageId - The open package id (from the eval3 page context).
 * @return The queryOptions for the package's saved Package Compares.
 */
const packageCompareRunsQueryOptions = (packageId: number) =>
  queryOptions({
    queryKey: [PACKAGE_COMPARE_RUNS_QUERY_KEY, packageId],
    queryFn: ({ signal }) =>
      api
        .get(`packages/${packageId}/compare/runs`, { signal })
        .json<PackageCompareRunsResponse>(),
  });

/**
 * The package's saved Package Compares, most-recently-run first (INS-12510). Compare
 * opens and re-runs in a NEW tab (`window.open(..., '_blank')`), so when the user runs
 * one and returns to this tab the panel must pick up the new/updated row without a full
 * reload — `refetchOnWindowFocus` does that here even though it is off app-wide. No
 * Pusher: a saved compare has no live lifecycle; the list only needs to be fresh on
 * focus and mount.
 *
 * @param {number} packageId - The open package id (from the eval3 page context).
 * @return {UseQueryResult<PackageCompareRunsResponse, Error>} The React Query result.
 */
export function usePackageCompareRuns(
  packageId: number
): UseQueryResult<PackageCompareRunsResponse, Error> {
  return useQuery({
    ...packageCompareRunsQueryOptions(packageId),
    refetchOnWindowFocus: true,
  });
}

/**
 * Returns the package's display name from the shared {@link usePackageQuery} (so it
 * adds no request — it reuses the same cache entry as {@link usePackageBounds}), or
 * `''` while loading / when the package has no name. Surfaced above the left-pane
 * Evals tree so the tree reads as scoped to its named package.
 *
 * @param {number} packageId - The package whose name to load.
 * @return {string} The package name, or `''` when loading / unnamed.
 */
export function usePackageName(packageId: number): string {
  const { data } = usePackageQuery(packageId);
  return data?.name ?? '';
}

/**
 * Returns the package's `type` from the shared {@link usePackageQuery} (so it adds
 * no request — it reuses the same cache entry as {@link usePackageName}). An empty
 * string means an Insights-native package; any non-empty value marks a
 * Salesforce/legacy package, which the edit wizard cannot bootstrap (its edit-mode
 * resolver returns `'unsupported'` and silently bounces to `/packages`). The header
 * uses this to guard the "Edit package" affordance. Returns `''` while loading —
 * callers treat unknown as editable so the guard never blocks a real edit on a slow
 * fetch (the wizard's own bounce remains the backstop).
 *
 * @param {number} packageId - The package whose type to load.
 * @return {string} The package type (`''` when Insights-native / loading).
 */
export function usePackageType(packageId: number): string {
  const { data } = usePackageQuery(packageId);
  return data?.type ?? '';
}

/** Query-key prefix for the per-package filter-options catalog fetch. */
export const FILTER_OPTIONS_QUERY_KEY = 'eval3-new:filter-options' as const;

/**
 * Endpoint path for the attribute-filter catalog. No leading slash — the `api`
 * (ky) instance sets `prefixUrl`, so paths must be relative.
 */
export const FILTER_OPTIONS_URL = 'queries/packages/filter_options';

/**
 * The FE-side Type Curve divergence test — since INS-11129 Phase 2, the BANNER's only
 * dependency. The population reads no longer null their calc id on divergence: the id
 * rides every request and the BE serves frozen or hybrid rows by making this same fold
 * comparison server-side (Query::Packages::HybridSlotSource). This hook survives so the
 * banner can flip without waiting on a response round-trip.
 *
 * One comparison, computed here and nowhere else on the FE: the WORKING TC config (the
 * effective scenario — explicit pick ?? the case's own — plus the reconciled override
 * fold) against the eval's STORED config (same fallback chain, same fold functions).
 * Matching → the real calc id (frozen). Diverged → null (the banner's cue). While the
 * calc detail (or the case fallback it needs) is loading, the answer is FROZEN: the
 * committed-log atom seed waits for the same detail, so both sides materialize together
 * and the banner cannot flash on load.
 *
 * @param {number | null} calculationId - The page's eval-calc id, or null (draft).
 * @return {number | null} The id while the folds match — null means diverged (or draft).
 */
export function usePopulationCalculationId(
  calculationId: number | null
): number | null {
  const { data: calcDetail } = useCalcDetail(calculationId);
  const workingScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const workingOverrides = useAtomValue(typeCurveOverridesAtom);
  const workingPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const stored = calcDetail?.configurations;
  const storedCaseId = stored?.evaluation_case_id ?? null;
  // The run's stamped Type Curve, read by the same rule as the shown run's
  // (runCaseScenarioId): a stamped null is Legacy, and only a run with no stamp at
  // all falls back to its case's current Type Curve.
  const stampedScenario = stampedScenarioId(stored, 'type_curve_scenario_id');
  const needsCaseFallback =
    stampedScenario === undefined && storedCaseId !== null;
  const { data: storedCaseDetail } = useEvaluationCaseDetail(
    needsCaseFallback ? storedCaseId : null
  );

  if (calculationId === null) {
    return null;
  }
  if (!stored || (needsCaseFallback && storedCaseDetail === undefined)) {
    return calculationId;
  }

  const storedScenarioId =
    stampedScenario !== undefined
      ? stampedScenario
      : needsCaseFallback
        ? (storedCaseDetail?.typeCurveScenarioId ?? null)
        : null;
  const storedFold =
    encodeTypeCurveOverrides(
      typeCurveOverridesFromLog(stored.slot_overrides)
    ) ?? null;
  const workingFold = encodeTypeCurveOverrides([...workingOverrides]) ?? null;
  // The spacing half diverges the same way (INS-10669): a package choice changes which
  // places exist, so a calc frozen under a different package fold no longer describes
  // the working population.
  const storedPackageFold =
    encodeInventoryPackageOverrides(
      inventoryPackageOverridesFromLog(stored.slot_overrides)
    ) ?? null;
  const workingPackageFold =
    encodeInventoryPackageOverrides([...workingPackageOverrides]) ?? null;
  const diverged =
    (workingScenarioId ?? null) !== (storedScenarioId ?? null) ||
    workingFold !== storedFold ||
    workingPackageFold !== storedPackageFold;
  return diverged ? null : calculationId;
}

/**
 * Everything a filter-options read depends on — the request body and, minus the pending
 * wire, the cache key. One shape so `useFilterOptions` and the master_data read that can
 * carry the same catalogs (INS-12696) build the key and the body from the same values;
 * two hand-built keys would drift, and a key that differs by one field fires the request
 * the bundle exists to avoid.
 */
export interface FilterOptionsInputs {
  packageId: number;
  calculationId: number | null;
  filters: ServerFilter[];
  mapScope: MapScope | undefined;
  typeCurveScenarioId: number | null;
  typeCurveOverrides: readonly TypeCurveOverride[];
  inventoryPackageOverrides: readonly InventoryPackageOverride[];
  /** The session's pending TC Manager entries — body only, never the key (see below). */
  pendingTcWire: readonly (
    TypeCurveOverrideWire | InventoryPackageOverrideWire
  )[];
  interestModelableOnly: boolean;
}

/**
 * The filter-options React Query key. Shared by `useFilterOptions` and the catalog
 * `useMasterData` seeds, so a seeded entry is the one the hook reads.
 *
 * The TC Manager's choices are encoded rather than the raw arrays, so a reordering
 * cannot mint a second cache entry. The pending wire entries ride the body only; the
 * folds above already describe them for the cache.
 *
 * @param {FilterOptionsInputs} inputs - The catalog's inputs.
 * @return {unknown[]} The React Query key.
 */
export function filterOptionsQueryKey(inputs: FilterOptionsInputs) {
  return [
    FILTER_OPTIONS_QUERY_KEY,
    inputs.packageId,
    inputs.calculationId ?? null,
    {
      filters: inputs.filters,
      mapScope: inputs.mapScope ?? null,
      typeCurveScenarioId: inputs.typeCurveScenarioId,
      typeCurveOverrides:
        encodeTypeCurveOverrides(inputs.typeCurveOverrides) ?? null,
      inventoryPackageOverrides:
        encodeInventoryPackageOverrides(inputs.inventoryPackageOverrides) ??
        null,
      interestModelableOnly: inputs.interestModelableOnly,
    },
  ];
}

/**
 * `POST /queries/packages/filter_options` for one set of inputs.
 *
 * @param {FilterOptionsInputs} inputs - The catalog's inputs.
 * @param {AbortSignal | undefined} signal - The query's abort signal.
 * @return {Promise<FilterOptionsResponse>} The two catalogs.
 * @throws {ApiEnvelopeError} When the BE answers with an error envelope.
 */
async function fetchFilterOptions(
  inputs: FilterOptionsInputs,
  signal: AbortSignal | undefined
): Promise<FilterOptionsResponse> {
  try {
    return await api
      .post(FILTER_OPTIONS_URL, {
        json: {
          packageId: inputs.packageId,
          ...calcModeField(inputs.calculationId),
          ...scenarioField(inputs.typeCurveScenarioId),
          ...(inputs.filters.length ? { filters: inputs.filters } : {}),
          ...(inputs.mapScope ? { mapScope: inputs.mapScope } : {}),
          // The session's PENDING TC entries — deltas and tombstones — on the
          // slot_overrides channel, exactly as master_data sends its pending log. The
          // BE folds committed (via the calc id) + these, so the catalog resolves the
          // same population the grid shows, deletions included.
          ...(inputs.pendingTcWire.length
            ? { slot_overrides: inputs.pendingTcWire }
            : {}),
          ...(inputs.interestModelableOnly
            ? { interestModelableOnly: true }
            : {}),
        },
        signal,
        timeout: EVAL3_REQUEST_TIMEOUT_MS,
      })
      .json<FilterOptionsResponse>();
  } catch (err) {
    // INS-12433 — types the envelope so the accordion can tell a permanent failure
    // (a calc whose snapshot was never written) from a transient one, and drop a
    // Retry that cannot work. Cancellations rethrow untouched; see the helper.
    return throwTypedEnvelopeError(err);
  }
}

/**
 * Fetches the attribute-filter catalogs (`POST /queries/packages/filter_options`)
 * — `{ wells, units, assets }`, each an attribute → `FilterOption` map that seeds
 * the `AttributeFilterModal`'s value editors (categorical values / numeric +
 * date bounds). Identity is `packageId` only (the BE resolves the latest completed
 * calc, as `master_data` does). The catalog is session-stable, so it's fetched
 * once and never refetched (`staleTime: Infinity`).
 *
 * On the page's first load the catalogs usually arrive with a Master Data read
 * instead (INS-12696): that read builds the same slot population, so asking for them
 * there saves the server a second build. See `useMasterData` for the hand-off.
 *
 * @param {number} packageId - The package whose attribute catalogs to load.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null (package mode).
 * @return {object} The React Query result for the three dataset catalogs.
 */
export function useFilterOptions(
  packageId: number,
  calculationId: number | null
): UseQueryResult<FilterOptionsResponse, Error> {
  const inputs = useFilterOptionsInputs(packageId, calculationId);
  const { canFetch } = useCalcLifecycle(calculationId);
  const queryKey = filterOptionsQueryKey(inputs);

  // A new key waits one commit before it may fetch (INS-12696). A Master Data read that
  // is carrying these catalogs claims the key from its query function, which React Query
  // runs in the same effect pass that would start this fetch — in whichever order the
  // components happen to sit in the tree. Holding this side back a commit lets that claim
  // land first, so this observer joins the in-flight read instead of racing it with a
  // second filter_options request. Without a claim it simply fetches one commit later.
  // Held against the first commit that COULD fetch, not the first render: in calc mode the
  // key is stable while the calc detail loads, and both reads enable together once it lands.
  const keyHash = hashKey(queryKey);
  const fetchableKeyHash = canFetch ? keyHash : null;
  const [committedKeyHash, setCommittedKeyHash] = useState<string | null>(null);
  useEffect(() => {
    setCommittedKeyHash(fetchableKeyHash);
  }, [fetchableKeyHash]);

  // INS-11129 Phase 2 — the calc id rides EVERY read; the BE decides the universe
  // (frozen blob, or blob + TC-replaced pairs when the fold in the body diverges from
  // the stored one). The FE no longer nulls the id on divergence.
  return useQuery<FilterOptionsResponse, Error>({
    queryKey,
    queryFn: ({ signal }) => fetchFilterOptions(inputs, signal),
    // calc-mode data exists only for completed calcs (P5 rule)
    enabled: canFetch && committedKeyHash === keyHash,
    // Cache per (calc mode × filter state); keep the prior options visible through a
    // refetch so the dropdowns don't flash while a new narrow resolves.
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
}

/**
 * Reads the filter-options inputs from the page atoms — the one reader, so the catalog
 * hook and the Master Data read that can carry the catalogs cannot read different ones.
 *
 * @param {number} packageId - The package.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null (package mode).
 * @return {FilterOptionsInputs} The catalog's inputs.
 */
function useFilterOptionsInputs(
  packageId: number,
  calculationId: number | null
): FilterOptionsInputs {
  // The active dataset filters (in insertion order) + spatial scope drive the
  // ordered cascade (INS-10376 Bug 13): the response carries `cascade` (each
  // attribute narrowed by the filters ABOVE it — the Add-Filter pickers) and
  // `colorBy` (every attribute narrowed by all filters — the color pickers + legend).
  //
  // INS-10669 — the TC Manager's choices change which places exist, so the catalogs must
  // re-resolve on Done; the PENDING wire entries (deltas + tombstones) travel in the body,
  // since overlaying survivors can't DELETE a committed choice — only a tombstone can.
  //
  // Asset-picker scope: inside the picker's fresh Jotai Provider the catalog narrows to
  // modelable tracts — and caches APART from the page's (the flag is in the key;
  // staleTime: Infinity would otherwise serve the page's full catalog to the picker).
  return {
    packageId,
    calculationId,
    filters: useAtomValue(serverFiltersAtom),
    mapScope: useAtomValue(mapScopeAtom),
    typeCurveScenarioId: useAtomValue(typeCurveScenarioIdAtom),
    typeCurveOverrides: useAtomValue(typeCurveOverridesAtom),
    inventoryPackageOverrides: useAtomValue(inventoryPackageOverridesAtom),
    pendingTcWire: useAtomValue(pendingTypeCurveWireAtom),
    interestModelableOnly: useAtomValue(interestModelableOnlyAtom),
  };
}

/** Maps a view discriminator to its row shape, so the hook return narrows by view. */
type RowForView<V extends MasterDataView> = V extends 'assets'
  ? AssetRow
  : V extends 'slots'
    ? SlotRow
    : V extends 'allocations'
      ? SlotAllocationRow
      : never;

/**
 * How long a Master Data page stays fresh, and the Tracts held in part with it, whose counts its
 * rows carry (`usePartialTracts`).
 */
const MASTER_DATA_STALE_TIME_MS = 180_000;

/**
 * Fetches one page of the Master Data table for a given view. Single React Query
 * hook backing all three view components; the return type narrows to the row
 * shape for the requested `view`.
 *
 * Callers pass the full wire payload (`MasterDataRequest`, the BE shape).
 *
 * The query key is `[KEY, packageId, view, { volatile params }]`: the positional
 * prefix forms an invalidation hierarchy (invalidate one view, one package, or
 * everything by prefix), while the trailing object holds the volatile params,
 * normalized (`search ?? ''`, sort `?? null`) so equivalent requests share a
 * cache entry instead of thrashing on `undefined` vs `''`.
 *
 * Identity is `packageId` only — the BE rejects `evaluationId` (an Eval 2 concept)
 * and resolves the package's latest completed calc from `packageId` alone.
 *
 * @param {MasterDataRequest} params - Full request payload (incl. packageId + optional calculationId); identity drives the cache key.
 * @return {object} The React Query result for the view's paginated response.
 */
export function useMasterData<V extends MasterDataView>(
  params: MasterDataRequest & {
    view: V;
    /**
     * Reads the pending log at the moment the request is sent. An invalidation refetches
     * with the query function of the last render, so an override appended since then
     * (a revert, most visibly) was missing from the body and the grid repainted the
     * committed value. Falls back to `slotOverrides` when absent.
     */
    readSlotOverrides?: () => SlotOverrideLogEntry[];
    /**
     * The filter-options inputs this read may carry the catalogs for (INS-12696); absent ⇒
     * it never asks. Body-only, like `readSlotOverrides`: the rows do not depend on it.
     */
    filterOptionsInputs?: FilterOptionsInputs;
  }
): UseQueryResult<MasterDataResponse<RowForView<V>>, Error> {
  const { canFetch } = useCalcLifecycle(params.calculationId);
  const queryClient = useQueryClient();
  // INS-11129 Phase 2 — the calc id rides EVERY read. The BE decides the universe: fold
  // in the body matches the stored one ⇒ pure frozen blob; diverged ⇒ blob rows with the
  // TC-affected pairs replaced by fresh scenario places (the hybrid). Drafts still have
  // no id ⇒ live. The FE no longer nulls the id on divergence; the fold in the query key
  // still drives repaints on Done.
  return useQuery<MasterDataResponse<RowForView<V>>, Error>({
    queryKey: masterDataQueryKey(params),
    queryFn: async ({ signal }) => {
      // `params` is the BE wire shape already (incl. packageId); only the per-row
      // field shape is adapted on the way back. Send the trimmed search so the body
      // matches the (trimmed) query key — otherwise a whitespace-only difference
      // reuses a cache entry minted for a different request body. Omit `filters` when
      // empty (matches the normalized `[]` key). Drop `calculationId` from the spread
      // and re-add only when set (calc mode), so a null never reaches the wire.
      // `slotOverrides` rides the BODY (as `slot_overrides`) but is kept OUT of the query
      // key (destructured from `rest`) — an edit must not refetch; cross-page freshness is
      // handled by the optimistic write + invalidate in the edit handler (P2).
      // `typeCurveOverrides` keys the cache (Done must repaint) and is NEVER its own body
      // field — master_data has no `type_curve_overrides` param (strict-params 503).
      // In PREVIEW mode its entries join `slot_overrides` below, the one channel the
      // server folds TC choices from.
      //
      // `interestModelableOnly` is destructured out too, then re-added ONLY when true (like
      // filters/tableFilters) so an unflagged request stays byte-identical to before.
      const {
        calculationId,
        slotOverrides,
        readSlotOverrides,
        typeCurveScenarioId,
        interestModelableOnly,
        filterOptionsInputs,
        // Destructured ONLY to keep them out of `...rest` (the request body) — see above.
        typeCurveOverrides,
        inventoryPackageOverrides,
        ...rest
      } = params;
      // The override entries for the body: this session's PENDING ops only. The calc id
      // is always on the wire now, so the BE loads the committed history itself in every
      // mode — frozen, hybrid, and (id-less) draft alike. The committed-log threading the
      // old id-less preview needed is gone with the mode it served (INS-11129 Phase 2).
      const overrideEntries = toSlotOverridesWire(
        readSlotOverrides?.() ?? slotOverrides ?? []
      );
      const bundleInputs = filterOptionsToBundle(
        queryClient,
        filterOptionsInputs,
        overrideEntries
      );
      const payload = {
        ...rest,
        search: params.search?.trim() || undefined,
        filters: params.filters?.length ? params.filters : undefined,
        // INS-11272 — per-table COLUMN filters; omitted when empty so an
        // unfiltered request stays byte-identical to before (matches the key).
        tableFilters: params.tableFilters?.length
          ? params.tableFilters
          : undefined,
        ...(overrideEntries.length ? { slot_overrides: overrideEntries } : {}),
        ...(interestModelableOnly ? { interestModelableOnly: true } : {}),
        ...calcModeField(calculationId),
        ...scenarioField(typeCurveScenarioId),
        ...(bundleInputs ? { includeFilterOptions: true } : {}),
      };
      // TEMPORARY timeout (see EVAL3_REQUEST_TIMEOUT_MS) — slots master_data runs
      // >30s for large packages; decrease back toward ky's 30s once the BE is fast.
      const request = api
        .post(MASTER_DATA_URL, {
          json: payload,
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<MasterDataResponse<Record<string, unknown>>>();
      if (bundleInputs) {
        // Sent in the same tick as the request, which is before useFilterOptions may fetch
        // (see its one-commit hold), so its observer joins this seed instead of POSTing.
        // One-shot: a later invalidation of the catalogs asks filter_options rather than
        // replaying this response (see the helper). If master_data fails or answers without
        // the catalogs, the fallback asks filter_options too.
        void seedQueryFromPending<FilterOptionsResponse>(queryClient, {
          queryKey: filterOptionsQueryKey(bundleInputs),
          pending: request.then((raw) => raw.filterOptions),
          fallback: ({ signal }) => fetchFilterOptions(bundleInputs, signal),
          staleTime: Infinity,
        });
      }
      try {
        // The catalogs went to their own cache entry (the seed above); this one keeps
        // the envelope it always had.
        const { filterOptions, ...raw } = await request;
        return adaptMasterDataResponse(raw, params.view) as MasterDataResponse<
          RowForView<V>
        >;
      } catch (err) {
        // INS-12433 — the table's error state renders `error.message` verbatim
        // (components/data-table → table-body), so without this a snapshot-less calc
        // showed "Unprocessable Content" instead of the BE's message naming the missing
        // snapshot. There is no Retry on this surface to gate — DataTable's error state
        // is text only — so this types the message and nothing more.
        return throwTypedEnvelopeError(err);
      }
    },
    enabled: canFetch, // calc-mode data exists only for completed calcs (P5 rule)
    placeholderData: keepPreviousData, // smooths pagination/sort transitions
    // 3 min. Slot overrides mutate this query via the body (INS-10777); cross-page
    // freshness is handled by commitSlotOverride's invalidate({refetchType:'none'})
    // (edited pages refetch on next activation), not by this staleness window.
    staleTime: MASTER_DATA_STALE_TIME_MS,
  });
}

/**
 * Whether a Master Data read should carry the filter catalogs (INS-12696): the inputs to
 * bundle, or null. Only when the catalog key is unclaimed (`canSeedQuery`: the hook has
 * rendered for these inputs, and its entry has no data, no fetch of its own and no error — a
 * failed catalog is left to its Retry), and nothing is pending — master_data sends the whole
 * pending log and filter_options only the TC Manager's part, so with entries pending the two
 * could fold different Type Curve choices.
 *
 * @param {QueryClient} queryClient - The page's React Query client.
 * @param {FilterOptionsInputs | undefined} inputs - The catalog's inputs, if the read may carry them.
 * @param {unknown[]} overrideEntries - The pending log this master_data read sends.
 * @return {FilterOptionsInputs | null} The inputs to bundle, or null.
 */
function filterOptionsToBundle(
  queryClient: QueryClient,
  inputs: FilterOptionsInputs | undefined,
  overrideEntries: readonly unknown[]
): FilterOptionsInputs | null {
  if (!inputs || overrideEntries.length || inputs.pendingTcWire.length) {
    return null;
  }
  return canSeedQuery(queryClient, filterOptionsQueryKey(inputs))
    ? inputs
    : null;
}

/** Page size every Master Data view opens on (one of DataTable's PAGE_SIZES). */
const INITIAL_PAGE_SIZE = 100;

/**
 * Controller for a Master Data view: owns the pagination / search / sort state,
 * reads the page's dataset filters (`serverFiltersAtom`) + spatial scope
 * (`mapScopeAtom`) and passes them so the BE narrows the view, fetches via
 * `useMasterData`, and exposes the three DataTable change handlers. Extracted so all
 * three views (Assets / Slots / Slot Allocations) share one implementation. Identity
 * is `packageId` only (the BE resolves the latest completed calc; it rejects
 * `evaluationId`).
 *
 * A filter or scope change resets the view to page 1 (P1 rule) via a render-time state
 * adjustment — done in place (no view remount) so React Query keeps the previous rows
 * through the refetch (`keepPreviousData`) and the in-flight request isn't aborted. The
 * returned `pagination` is handed to the DataTable as `controlledState.pagination`, so
 * this hook is the single page owner and the reset lands on the pager display too — the
 * old paginated-then-filtered pager lag (no controlled-page API) is closed.
 *
 * @param {object} params - The view discriminator + the page identity (from context, passed by the view).
 * @param {string} params.view - Which master-data view to fetch.
 * @param {number} params.packageId - The package to scope to.
 * @param {number | null} params.calculationId - The eval-calc id (calc mode) or null.
 * @return {object} Rows, totalCount, fetch state, the controlled `pagination` ({ page, pageSize }), and the DataTable change handlers.
 */
export function useMasterDataView<V extends MasterDataView>({
  view,
  packageId,
  calculationId,
  slotOverrides,
  readSlotOverrides,
  initialTableFilters,
  resetTableFiltersOnCalcChange = false,
}: {
  view: V;
  packageId: number;
  calculationId: number | null;
  /** Pending override operations (slots view). Sent in the read body; NOT in the page-reset deps. */
  slotOverrides?: SlotOverrideLogEntry[];
  /** Reads the live pending log when a request is sent; see `useMasterData`. */
  readSlotOverrides?: () => SlotOverrideLogEntry[];
  /**
   * Column filters to SEED the view with on mount (INS-12871 — Properties opens with
   * Valued = Yes pre-applied on a run evaluation). The view pairs this wire seed with the
   * DataTable's `initialTableState.columnFilters` so the server read and the inline filter
   * selection agree from the first load (the table is server-driven, so the selection alone
   * would show a filter the BE never applied). The inline filter row is hidden until the user
   * opens it (`isColumnFilterActive` defaults to false), so the seeded selection is behind the
   * filter toggle — the grid can open already filtered with no chip on screen. Seeded on mount
   * and, when `resetTableFiltersOnCalcChange` is set, re-seeded on a calc-identity change;
   * otherwise the user owns it thereafter via `onColumnFilterChange`.
   */
  initialTableFilters?: TableFilter[];
  /**
   * INS-12871 — when true, re-seed the inline column filters (to `initialTableFilters ?? []`)
   * whenever `calculationId` changes IN PLACE. This resets only the WIRE half; the opted-in
   * view must ALSO re-key its `<DataTable key={calculationId ?? 'draft'}>` so the table's own
   * column-filter state re-seeds to match (the two halves reset together — the hook owns the
   * wire read, the DataTable owns the visible selection). Views with an eval-scoped filterable
   * column (Properties, Property Allocations) opt in, so a stale `valued` filter can't ride a
   * run eval → package/draft switch (422 `requires_eval_calc`). Views without one (Assets)
   * leave it off and keep their column filters across a calc change.
   */
  resetTableFiltersOnCalcChange?: boolean;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<MasterDataSort | null>(null);
  // INS-11272 — per-table COLUMN filters (the inline filter row). Local to this
  // view (unlike the page-level dataset `filters`/`mapScope` atoms below), owned
  // here and set via `onColumnFilterChange`. Seeded from `initialTableFilters`
  // (INS-12871) so a view can open with a default filter pre-applied.
  const [tableFilters, setTableFilters] = useState<TableFilter[]>(
    initialTableFilters ?? []
  );
  // The page atoms this view reads are the filter-options inputs, taken from their one
  // reader so the catalogs this view's first read may carry (INS-12696, below) are built
  // from the very values the rows are, with one subscription per atom.
  const filterOptionsInputs = useFilterOptionsInputs(packageId, calculationId);
  const {
    filters,
    mapScope,
    typeCurveScenarioId,
    // INS-10669 — part of the master_data query KEY (see masterDataQueryKey), so pressing
    // Done in the TC Manager re-asks instead of leaving the grid on the previous curve.
    typeCurveOverrides,
    inventoryPackageOverrides,
    // Asset-picker scope: true inside the picker's fresh Jotai Provider,
    // false on the page. Rides the fetch params + key like filters/mapScope. Not in
    // the page-1 reset deps — the flag never changes within one mounted view.
    interestModelableOnly,
  } = filterOptionsInputs;

  // Reset to page 1 when the dataset filters OR the map scope change (FR#4). This is the
  // React-documented "adjust state during render" pattern — the previous filters/scope are
  // tracked in STATE (not a ref) and compared here, calling `setPage(1)` on a real change.
  //
  // STATE, not a ref, is essential: under React 17 StrictMode the render function is
  // double-invoked, and a ref mutated during render (`prevFilters.current = ...`) would be
  // seen as already-updated by the second invocation — so it would skip the reset and
  // compute `effectivePage` from the STALE page (the "filter change doesn't reset the page"
  // bug). State isn't mutated mid-render, so both invocations compare against the same
  // previous value and stay consistent; React applies the queued setState once.
  //
  // We compute `effectivePage` INLINE (not just `setPage(1)`) and feed THAT to the fetch +
  // the controlled pager, so the query key is page 1 immediately — no transient request for
  // the old page (the bailed reset render would otherwise observe stale-page + new-filters),
  // and the controlled pager lands on page 1 without a frame of stale display.
  //
  // INS-10669 — the Type Curve scenario and the TC Manager's choices reset the page for the
  // same reason filters do: they change which rows EXIST, so the row count can shrink under
  // a reader who is not on page 1. Switching a curve that removes 16 of 60 places takes the
  // grid from three pages to two, and a reader sitting on page 3 would otherwise be left on
  // an out-of-range page showing nothing. Unreachable until Done began refetching at all.
  const [prevFilters, setPrevFilters] = useState(filters);
  const [prevMapScope, setPrevMapScope] = useState(mapScope);
  const [prevScenarioId, setPrevScenarioId] = useState(typeCurveScenarioId);
  const [prevTcOverrides, setPrevTcOverrides] = useState(typeCurveOverrides);
  // The package fold resets the page for the same reason the curve fold does: an
  // inventory_package choice changes WHICH SLOTS EXIST, so a package-only pick could
  // strand the user on an out-of-range page (PR #7365 review).
  const [prevPackageOverrides, setPrevPackageOverrides] = useState(
    inventoryPackageOverrides
  );
  let effectivePage = page;
  if (
    prevFilters !== filters ||
    prevMapScope !== mapScope ||
    prevScenarioId !== typeCurveScenarioId ||
    prevTcOverrides !== typeCurveOverrides ||
    prevPackageOverrides !== inventoryPackageOverrides
  ) {
    setPrevFilters(filters);
    setPrevMapScope(mapScope);
    setPrevScenarioId(typeCurveScenarioId);
    setPrevTcOverrides(typeCurveOverrides);
    setPrevPackageOverrides(inventoryPackageOverrides);
    effectivePage = 1;
    setPage(1);
  }

  // INS-12871 — re-seed the inline column filters when the eval changes IN PLACE (a run eval
  // → package/draft, or a draft's Calculate that mints a calculationId), for the opted-in
  // views (`resetTableFiltersOnCalcChange`). `tableFilters` is local state that would otherwise
  // survive the switch, and the Valued = Yes default is eval-scoped: carrying it into
  // package/draft (no calculationId) 422s (`requires_eval_calc`), and a stale filter from the
  // old eval shouldn't ride into the next one. Same render-time "adjust state" pattern as the
  // resets above, and we compute `effectiveTableFilters` INLINE so THIS render's fetch already
  // uses the seed (no transient request with the old filter + new calc).
  //
  // This resets only the WIRE half (the hook's `tableFilters`). The DataTable keeps its own
  // column-filter state, so the opted-in views also re-key their `<DataTable key={calculationId
  // ?? 'draft'}>`: remounting re-seeds its `initialTableState.columnFilters` to match this
  // reset, so the request and the visible selection can't disagree (a stale `valued` left in
  // the table would otherwise ride the next filter edit and 422). A DataTable remount keeps
  // `keepPreviousData` (it lives in the query) and the controlled pagination/selection; it is
  // NOT a key on the whole VIEW, which would drop those. `prevCalculationId` is tracked even
  // when the reset is off, so Assets (no eval-scoped column) keeps its filters across a calc
  // change and passes no key either.
  let effectiveTableFilters = tableFilters;
  const [prevCalculationId, setPrevCalculationId] = useState(calculationId);
  if (prevCalculationId !== calculationId) {
    setPrevCalculationId(calculationId);
    if (resetTableFiltersOnCalcChange) {
      const seed = initialTableFilters ?? [];
      setTableFilters(seed);
      effectiveTableFilters = seed;
      effectivePage = 1;
      setPage(1);
    }
  }

  // INS-12696 — the view's FIRST read carries the filter catalogs, so the page's first
  // load builds the slot population once. Later reads (a filter, a page, a sort) leave the
  // catalogs to filter_options, as before. "First" is per page identity, so switching evals
  // in place bundles again.
  const identity = `${packageId}:${calculationId ?? ''}`;
  const [settledIdentity, setSettledIdentity] = useState<string | null>(null);
  const hasSettled = settledIdentity === identity;

  const query = useMasterData({
    view,
    packageId,
    calculationId,
    typeCurveScenarioId,
    typeCurveOverrides,
    inventoryPackageOverrides,
    filterOptionsInputs: hasSettled ? undefined : filterOptionsInputs,
    page: effectivePage,
    pageSize,
    search: search || undefined,
    sort: sort ?? undefined,
    filters,
    tableFilters: effectiveTableFilters,
    mapScope,
    slotOverrides,
    readSlotOverrides,
    interestModelableOnly,
  });

  // `isPlaceholderData` excluded: across an eval switch the kept data reports the OLD
  // eval's success, which would mark the new one settled before its first read lands.
  const querySettled =
    (query.isSuccess && !query.isPlaceholderData) || query.isError;
  useEffect(() => {
    if (querySettled) {
      setSettledIdentity(identity);
    }
  }, [querySettled, identity]);

  // Publish the server's population-mode verdict for the divergence banner (the field's
  // one consumer — PR #7365 review). Keyed by calculationId so an old eval's verdict
  // cannot describe the next one.
  const setServerPopulationMode = useSetAtom(serverPopulationModeAtom);
  const responseMode = query.data?.populationMode;
  useEffect(() => {
    if (responseMode) {
      setServerPopulationMode({ calculationId, mode: responseMode });
    }
  }, [responseMode, calculationId, setServerPopulationMode]);

  // Each DataTable callback delivers the full post-change snapshot; on sort and
  // search the snapshot's `page` is already reset to 1, so we just mirror it.
  const onPaginationChange = useCallback((state: TableState) => {
    setPage(state.page);
    setPageSize(state.pageSize);
  }, []);

  // `deriveSort` translates the column id to the BE row key for this view.
  const onSortChange = useCallback(
    (state: TableState) => {
      setSort(deriveSort(state.sorting, view));
      setPage(state.page);
    },
    [view]
  );

  const onGlobalSearchChange = useCallback((state: TableState) => {
    setSearch(state.globalFilter);
    setPage(state.page);
  }, []);

  // Inline column-filter change (INS-11272). The DataTable reports the full
  // ColumnFiltersState in its snapshot; the VIEW translates it to the wire
  // `tableFilters` (it owns the columns + their meta) and hands us the predicates
  // + the reset page (the snapshot's `page` is already 1 — same contract as
  // sort/search). Kept a plain setter pair so the view stays the single translator.
  const onColumnFilterChange = useCallback(
    (next: TableFilter[], nextPage: number) => {
      setTableFilters(next);
      setPage(nextPage);
    },
    []
  );

  // Stable empty array across renders so DataTable's row-model memo isn't busted.
  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);

  // The CONTROLLED pagination handed to the DataTable (`controlledState.pagination`). This
  // hook is the single owner of page/pageSize — the table reflects this value rather than
  // keeping its own copy, so a filter-driven reset to page 1 can't desync the pager. Uses
  // `effectivePage` so the pager matches the fetch within the reset render (see above).
  const pagination = useMemo(
    () => ({ page: effectivePage, pageSize }),
    [effectivePage, pageSize]
  );

  // The unified loading umbrella: this view's OWN master_data fetch OR the shared
  // calc-lifecycle wait (detail resolving / BE processing) — a gated query is idle
  // during a run, so `query.isFetching` alone would read as "settled" over the
  // frozen rows.
  const { isRunning } = useCalcLifecycle(calculationId);

  // INS-10777 — the current read's query key (server params only) + the active filter,
  // exposed so SlotView can optimistically write the current page and snapshot a filter op.
  // Memoized so both keep a STABLE identity across renders — they feed SlotView's
  // `commitSlotOverride` / `handleBulkEditSave` (and thus the `columns` memo), which would
  // otherwise rebuild every render. Same inputs as the fetch's own key (minus `slotOverrides`).
  const queryKey = useMemo(
    () =>
      masterDataQueryKey({
        view,
        packageId,
        calculationId,
        page: effectivePage,
        pageSize,
        search: search || undefined,
        sort: sort ?? undefined,
        filters,
        tableFilters: effectiveTableFilters,
        mapScope,
        // INS-10669 — these two key the fetch's cache entry (they change which rows
        // exist). Omitting them here built a key with `null`s that no query ever used,
        // so `commitSlotOverride`'s setQueryData painted a dead entry and an edit only
        // showed up after something else (search, page) minted a new key.
        typeCurveScenarioId,
        typeCurveOverrides,
        inventoryPackageOverrides,
        interestModelableOnly,
      }),
    [
      view,
      packageId,
      calculationId,
      effectivePage,
      pageSize,
      search,
      sort,
      filters,
      effectiveTableFilters,
      mapScope,
      typeCurveScenarioId,
      typeCurveOverrides,
      inventoryPackageOverrides,
      interestModelableOnly,
    ]
  );
  const filterScope = useMemo(
    () => ({ search: search || undefined, filters, mapScope }),
    [search, filters, mapScope]
  );

  return {
    rows,
    totalCount: query.data?.totalCount ?? 0,
    isFetching: query.isFetching || isRunning,
    error: query.error,
    pagination,
    onPaginationChange,
    onSortChange,
    onGlobalSearchChange,
    onColumnFilterChange,
    queryKey,
    filterScope,
  };
}

/**
 * The master_data React Query key — server read params only (NO `slotOverrides`; an edit
 * must not refetch). Shared by `useMasterData` and the optimistic write in SlotView so the
 * two never drift. Normalized (trimmed search, `[]`/`null` fallbacks) so equivalent requests
 * share one cache entry.
 *
 * @param {object} params - The read params (a MasterDataRequest plus the active view).
 * @return {unknown[]} The React Query key.
 */
export function masterDataQueryKey(
  params: MasterDataRequest & { view: MasterDataView }
) {
  return [
    MASTER_DATA_QUERY_KEY,
    params.packageId,
    params.view,
    {
      calculationId: params.calculationId ?? null,
      page: params.page,
      pageSize: params.pageSize,
      search: params.search?.trim() ?? '',
      sort: params.sort ?? null,
      filters: params.filters ?? [],
      tableFilters: params.tableFilters ?? [],
      mapScope: params.mapScope ?? null,
      // INS-10669 — both of these decide which ROWS exist, so they have to key the cache.
      // `slotOverrides` stays out (an inline edit must not refetch; it changes a row's
      // values and is written optimistically), but the scenario and the TC Manager's
      // choices change the population itself: without them here, picking a scenario or
      // pressing Done leaves the grid showing the previous curve's places.
      typeCurveScenarioId: params.typeCurveScenarioId ?? null,
      // The canonical encoding, not the array: identical choices in a different order must
      // not mint a second cache entry. Same form the tile URL and the BE token use.
      typeCurveOverrides:
        encodeTypeCurveOverrides(params.typeCurveOverrides ?? []) ?? null,
      // The spacing half keys the cache for the same reason (INS-10669).
      inventoryPackageOverrides:
        encodeInventoryPackageOverrides(
          params.inventoryPackageOverrides ?? []
        ) ?? null,
      // Asset-picker scope — normalized so absent == false (one entry).
      interestModelableOnly: params.interestModelableOnly ?? false,
    },
  ];
}

/** Query-key prefix for the per-package tract→wells fetch (the carry group multi-select roster). */
export const TRACT_WELLS_QUERY_KEY = 'eval3-new:tract-wells' as const;

/**
 * Page size for the assets pages pulled to build the tract→wells roster. 500 is
 * the endpoint's MAX allowed pageSize — master_data rejects anything not in
 * {50, 100, 250, 500} with a 422 (a larger value silently zeroed the whole
 * roster). The `assets` view has no per-tract narrowing param, so `useTractWells`
 * pages through the modelable set until every SELECTED tract has a roster row (or
 * the set is exhausted) — a selected tract beyond page one would otherwise never
 * seed its carry groups / UMI election.
 */
const TRACT_WELLS_PAGE_SIZE = 500;

/** The tract→wells roster + fetch state, for the carry group well multi-select. */
export interface TractWells {
  /** tractId (the picker row id) → the tract's wells; an absent key ⇒ not (yet) resolved / not modelable. */
  wellsByTract: Map<string, CarryWell[]>;
  /**
   * tractId → the tract's net acres (`tractNetAcres`), for the UMI election $/NMA lease-bonus
   * seed. `null` when the tract carries no net-acre figure (never fabricated); an
   * absent key ⇒ the tract isn't (yet) in the resolved roster.
   */
  netAcresByTract: Map<string, number | null>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Builds the tract→wells roster for the carry group well multi-select.
 * Reads the EXISTING `assets` master_data view (calc mode,
 * `interestModelableOnly: true`): each asset row carries its slot allocations
 * inline (`row.slotAllocations`), whose VALUED entries ARE the tract's wells (an
 * unvalued slot never prices, so it can't carry), and they're indexed
 * by the same row id the picker/interest-item uses (`String(row.id)`). No new
 * backend. Covers freshly-picked AND reopened items uniformly — every modeled
 * tract is modelable, so it appears in this narrowed view.
 *
 * Fetched only when `enabled` (there is ≥ 1 carry item to serve) AND the calc is
 * readable (`useCalcLifecycle`), so an election-only / empty section fetches
 * nothing.
 *
 * The `assets` view is server-paginated with no per-tract narrowing param, so a
 * selected tract beyond the first page would never get a roster (its carry groups
 * never seed, UMI stays on "Loading election..."). To cover it, the fetch pages
 * through the modelable set until every SELECTED tract is resolved or the set is
 * exhausted — usually one page (the modeled set is a handful), more only when the
 * package has > 500 modelable tracts and a selection lands past page one.
 *
 * @param {number} packageId - The package.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null (package mode).
 * @param {boolean} enabled - Fetch only when there's a carry item to serve.
 * @param {string[]} selectedTractIds - The modeled tract ids to guarantee a
 *   roster for; pagination stops early once all are resolved.
 * @return {TractWells} The roster map + fetch state.
 */
export function useTractWells(
  packageId: number,
  calculationId: number | null,
  enabled: boolean,
  selectedTractIds: readonly string[] = []
): TractWells {
  const { canFetch } = useCalcLifecycle(calculationId);
  // Stable digest of the wanted set for the query key, so a changed selection
  // refetches (and re-paginates to cover any newly-selected far-page tract).
  const wantedKey = useMemo(
    () => Array.from(new Set(selectedTractIds)).sort().join(','),
    [selectedTractIds]
  );
  const query = useQuery<MasterDataResponse<AssetRow>, Error>({
    queryKey: [
      TRACT_WELLS_QUERY_KEY,
      packageId,
      calculationId ?? null,
      wantedKey,
    ],
    queryFn: async ({ signal }) => {
      const wanted = new Set(selectedTractIds);
      const rows: AssetRow[] = [];
      let page = 1;
      let hasMore = true;
      let last: MasterDataResponse<AssetRow> | undefined;
      // Always fetch page 1, then keep paging only while the server has more AND a
      // selected tract is still unresolved (bounded by the modelable set's size).
      while (hasMore) {
        const raw = await api
          .post(MASTER_DATA_URL, {
            json: {
              packageId,
              view: 'assets',
              page,
              pageSize: TRACT_WELLS_PAGE_SIZE,
              interestModelableOnly: true,
              ...calcModeField(calculationId),
            },
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<MasterDataResponse<Record<string, unknown>>>();
        last = adaptMasterDataResponse(
          raw,
          'assets'
        ) as MasterDataResponse<AssetRow>;
        rows.push(...last.rows);
        for (const row of last.rows) {
          wanted.delete(String((row as { id?: string }).id));
        }
        hasMore = last.hasMore && wanted.size > 0;
        page += 1;
      }
      return { ...(last as MasterDataResponse<AssetRow>), rows };
    },
    enabled: enabled && canFetch,
    placeholderData: keepPreviousData,
    staleTime: MASTER_DATA_STALE_TIME_MS,
  });

  const { wellsByTract, netAcresByTract } = useMemo(() => {
    const wellsMap = new Map<string, CarryWell[]>();
    const acresMap = new Map<string, number | null>();
    for (const row of query.data?.rows ?? []) {
      const tractId = (row as { id?: string }).id;
      if (!tractId) {
        continue;
      }
      const seen = new Set<string>();
      const wells: CarryWell[] = [];
      for (const slot of row.slotAllocations ?? []) {
        // Only offer priceable slots — a confirmed-unvalued slot (bad well status,
        // package/wellbore excluded, dead LTD) never prices, so it can't carry.
        // `valued === false` is the exclude test (undefined ⇒ keep) so a response
        // missing the field never nukes the roster.
        if (slot.valued === false) {
          continue;
        }
        const wellId = slot.slotId ?? (slot as { id?: string }).id;
        if (!wellId || seen.has(wellId)) {
          continue;
        }
        seen.add(wellId);
        wells.push({
          id: wellId,
          name: slot.wellName || slot.slotIdDisplay || wellId,
          uwi: slot.uwi || undefined,
          // OUR share of this slot, straight from the BE's per-tract field — NOT
          // `totalSlotNri`/`totalSlotWi`, which are the whole SLOT's totals repeated
          // on every tract the slot touches. Seeding from those priced a tract at
          // the entire slot: slot 3002554025 reads 4.824283% under all three of its
          // tracts, which actually hold 3.015177%, 0.904553% and 0.904553% of it.
          //
          // `tractNri`/`tractWi` come from InterestCertification::TractInterest —
          // the same rule the slots-view drill-down and the allocations rows use, so
          // this reads the same number every other per-tract surface shows rather
          // than re-deriving one here.
          //
          // Absent ⇒ null, never a fall back to the slot total: an unknown share
          // must read as unknown, and the editor already says so.
          nri: typeof slot.tractNri === 'number' ? slot.tractNri : null,
          wi: typeof slot.tractWi === 'number' ? slot.tractWi : null,
        });
      }
      wellsMap.set(String(tractId), wells);
      // `netAcres` (mapped from `tractNetAcres`) is the tract's net acres, used to seed
      // the UMI election $/NMA lease bonus. Null when the tract carries no figure — the
      // election editor shows "—" and keeps the bonus a manual total (no fabrication).
      const netAcres = (row as { netAcres?: number | null }).netAcres;
      acresMap.set(
        String(tractId),
        typeof netAcres === 'number' ? netAcres : null
      );
    }
    return { wellsByTract: wellsMap, netAcresByTract: acresMap };
  }, [query.data]);

  return {
    wellsByTract,
    netAcresByTract,
    isLoading: query.isFetching,
    isError: query.isError,
  };
}

/** The three dataset-accordion counts; each `undefined` while loading / on error. */
export interface DatasetCounts {
  assets?: number;
  units?: number;
  /**
   * Combined "Properties" count for the Properties accordion — every property in
   * the filter- + scope-aware population (wells + LTD), i.e. the BE's live
   * `matchedSlotCount`. `undefined` while loading / on error.
   */
  properties?: number;
}

/**
 * Counts for the Assets / Properties / Units accordion titles (mock §4), sourced
 * from the filter-aware `executive_summary` (INS-10093 added explicit
 * `assetCount`/`unitCount`/`matchedSlotCount`). Reads the page filter state and
 * calls `useExecutiveSummary` with the SAME key the Executive Summary panel uses,
 * so the two share one fetch — no extra request, and the counts narrow by the same
 * dataset filters + spatial scope as the summary (unlike the old master_data-backed
 * counts, which had no units count and didn't narrow by `mapScope` — the
 * master_data scope gap). Assets ← the asset/tract count; Units ← `unitCount`;
 * Properties ← `matchedSlotCount` (every property in the population: wells + LTD).
 * Each is `undefined` while loading or on error (the title omits the count).
 *
 * @param {number} packageId - The package whose counts to load.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null.
 * @return {DatasetCounts} The Assets / Properties / Units counts (undefined when unavailable).
 */
export function useDatasetCounts(
  packageId: number,
  calculationId: number | null
): DatasetCounts {
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const { data } = useExecutiveSummary({
    packageId,
    calculationId,
    typeCurveScenarioId,
    typeCurveOverrides,
    inventoryPackageOverrides,
    filters,
    mapScope,
  });
  return useMemo<DatasetCounts>(() => {
    if (!data) {
      return {};
    }
    return {
      assets: data.assets,
      units: data.unitCount,
      // Properties = every property in the filter- + scope-aware population
      // (wells + LTD), the BE's live `matchedSlotCount`.
      properties: data.matchedSlotCount,
    };
  }, [data]);
}

/** Query-key prefix for the filter-aware Executive Summary fetch. */
export const EXECUTIVE_SUMMARY_QUERY_KEY =
  'eval3-new:executive-summary' as const;

/**
 * Endpoint path for the Executive Summary metrics. No leading slash — the `api`
 * (ky) instance sets `prefixUrl`, so paths must be relative.
 */
export const EXECUTIVE_SUMMARY_URL = 'queries/packages/executive_summary';

/**
 * A typed `executive_summary` failure. Without it the hook threw ky's bare `HTTPError` and the
 * panel's notification showed "Unprocessable Content" over a Retry button that could never
 * work — the BE's real message (which names the missing snapshot and the action that
 * regenerates it) never reached the user (INS-12433).
 */
export class ExecutiveSummaryError extends ApiEnvelopeError {
  static override errorName = 'ExecutiveSummaryError';
}

/**
 * Fetches the filter-aware Executive Summary
 * (`POST /queries/packages/executive_summary`) — the stat-strip metrics, narrowed
 * by the same dataset filters + spatial scope the table reads. Keyed on `packageId`
 * + the server filters + the map scope (normalized to `[]` / `null` so "nothing set"
 * is one stable cache entry); `keepPreviousData` keeps the prior numbers visible
 * (dimmed) through a refetch. Identity is `packageId` only (the BE resolves the
 * latest completed calc; pricing tiles are `null` until one exists).
 *
 * @param {ExecutiveSummaryRequest} req - The package + calc id (from context, passed by the caller) + active filters / map scope.
 * @return {object} The React Query result for the summary metrics.
 */
export function useExecutiveSummary(
  req: ExecutiveSummaryRequest
): UseQueryResult<ExecutiveSummaryResponse, Error> {
  const { canFetch } = useCalcLifecycle(req.calculationId);
  // INS-11129 Phase 2 — the calc id rides every read; the BE serves frozen or hybrid
  // counts by comparing the body's fold against the stored one. Shared by the panel +
  // `useDatasetCounts`.
  const pendingTcWire = useAtomValue(pendingTypeCurveWireAtom);
  return useQuery<ExecutiveSummaryResponse, Error>({
    queryKey: [
      EXECUTIVE_SUMMARY_QUERY_KEY,
      req.packageId,
      {
        calculationId: req.calculationId ?? null,
        typeCurveScenarioId: req.typeCurveScenarioId ?? null,
        // INS-10669 — a TC choice changes which places the counts are taken over.
        typeCurveOverrides:
          encodeTypeCurveOverrides(req.typeCurveOverrides ?? []) ?? null,
        inventoryPackageOverrides:
          encodeInventoryPackageOverrides(
            req.inventoryPackageOverrides ?? []
          ) ?? null,
        filters: req.filters ?? [],
        mapScope: req.mapScope ?? null,
      },
    ],
    queryFn: async ({ signal }) => {
      // Omit the empty halves so the unfiltered request body is clean and matches
      // the normalized key. The `catalog` tag rides along unchanged — the BE ignores
      // it (matches by `attribute`), as on master_data. `calculationId` puts the
      // request in calc mode when set.
      const payload = {
        packageId: req.packageId,
        ...calcModeField(req.calculationId),
        ...scenarioField(req.typeCurveScenarioId),
        ...(req.filters?.length ? { filters: req.filters } : {}),
        ...(req.mapScope ? { mapScope: req.mapScope } : {}),
        // The session's PENDING TC entries (deltas + tombstones) — the BE folds
        // committed (via the calc id) + these; survivors alone cannot delete a
        // committed choice. Same channel master_data's pending log uses.
        ...(pendingTcWire.length ? { slot_overrides: pendingTcWire } : {}),
      };
      // TEMPORARY timeout (see EVAL3_REQUEST_TIMEOUT_MS) — the summary runs the
      // same heavy slot population as master_data; decrease back once the BE is fast.
      try {
        return await api
          .post(EXECUTIVE_SUMMARY_URL, {
            json: payload,
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<ExecutiveSummaryResponse>();
      } catch (err) {
        // Types the envelope so the panel can show the BE's message (which names the
        // missing snapshot) and can tell a PERMANENT failure — `calculation_data_unavailable`,
        // retryable:false — from a transient one, instead of offering a dead Retry for
        // both (INS-12433). Cancellations rethrow untouched; see the helper.
        return throwTypedEnvelopeError(err, ExecutiveSummaryError);
      }
    },
    enabled: canFetch, // calc-mode data exists only for completed calcs (P5 rule)
    placeholderData: keepPreviousData, // keep prior numbers (dimmed) on refetch
    staleTime: 1_800_000, // 30 min — keyed per filter set + session-stable, so refetch only on a new filter set
  });
}

/** Query-key prefix for the Charts & Visuals catalog fetch (Add-Chart list). */
export const CHART_CATALOG_QUERY_KEY = 'eval3-new:chart-catalog' as const;

/**
 * Endpoint path for the chart catalog. No leading slash — the `api` (ky) instance sets
 * `prefixUrl`, so paths must be relative.
 */
export const CHART_CATALOG_URL = 'queries/packages/chart_catalog';

/** Query-key prefix for a chart's series (data) fetch. */
export const CHART_SERIES_QUERY_KEY = 'eval3-new:chart-series' as const;

/**
 * Endpoint path for a chart's series data. No leading slash — the `api` (ky) instance
 * sets `prefixUrl`, so paths must be relative.
 */
export const CHART_SERIES_URL = 'queries/packages/chart_series';

/** Query-key prefix for the Custom builder's field-options vocabulary. */
export const CHART_FIELD_OPTIONS_QUERY_KEY =
  'eval3-new:chart-field-options' as const;

/**
 * Endpoint path for the Custom builder's dropdown vocabulary. No leading slash —
 * the `api` (ky) instance sets `prefixUrl`, so paths must be relative.
 */
export const CHART_FIELD_OPTIONS_URL = 'queries/packages/chart_field_options';

/**
 * A typed `chart_series` failure. The endpoint returns a structured
 * `{ error: { code, message, field? } }` envelope on 4xx/5xx (e.g. `invalid_field` names
 * the offending `spec.x.field`); the hook parses it off the ky `HTTPError` so the Custom
 * builder can render an inline field error rather than a generic "request failed". Falls
 * back to `code`/`message` undefined when the body isn't the structured envelope.
 */
export class ChartSeriesError extends ApiEnvelopeError {
  static override errorName = 'ChartSeriesError';

  field?: string;

  /**
   * `field` comes THIRD (chart_series is the only endpoint that carries one) but `retryable`
   * must still be threaded: chart_series_controller renders `retryable: false` for a missing
   * snapshot and `retryable: true` for the transient variant, and this class now satisfies
   * `instanceof ApiEnvelopeError` — so defaulting it to true would make every consumer using
   * the shared gate show a live Retry on a snapshot-less calculation, which is the exact
   * defect this work closes elsewhere.
   *
   * @param {string} message - Human-readable failure message.
   * @param {string | undefined} code - BE error code (e.g. 'invalid_field').
   * @param {string | undefined} field - The offending `spec.x.field`, when the envelope named one.
   * @param {boolean} retryable - Whether a retry could plausibly succeed; defaults to true when unknown.
   */
  constructor(
    message: string,
    code?: string,
    field?: string,
    retryable = true
  ) {
    super(message, code, retryable);
    this.field = field;
  }
}

/**
 * Fetches the Charts & Visuals catalog (`POST /queries/packages/chart_catalog`) — the
 * pre-canned Add-Chart presets, each with its `ChartSpec` + per-eval `availability`.
 * Scoped by `calculationId` alone (the contract carries no `packageId`): its presence flips
 * the calc-gated presets to `available:true`; the four economics presets stay
 * `available:false` regardless. Gated on a calc existing (like `chart_series`), so it never
 * fires without an identifier. The catalog is identical for any two calcs — `calculationId`
 * only flips calc-mode vs package-mode availability — so the query key uses that BINARY MODE,
 * not the id: switching between evals is a cache hit, not a refetch.
 *
 * @param {ChartCatalogRequest} req - The (calc-mode) calculation id.
 * @param {object} [options] - `enabled` gate — pass `false` to defer the fetch (e.g. until
 *   the Add-Chart picker opens) so it isn't fired on page load. AND-ed with the calc gate.
 * @return {object} The React Query result for the catalog.
 */
export function useChartCatalog(
  req: ChartCatalogRequest,
  options?: { enabled?: boolean }
): UseQueryResult<ChartCatalogResponse, Error> {
  return useQuery<ChartCatalogResponse, Error>({
    // Key on calc MODE (calc vs package), not the id — the catalog is identical for any two
    // calcs, so switching evals must stay a cache hit rather than trigger a refetch.
    queryKey: [
      CHART_CATALOG_QUERY_KEY,
      { calcMode: req.calculationId !== null },
    ],
    queryFn: ({ signal }) =>
      api
        .post(CHART_CATALOG_URL, {
          json: calcModeField(req.calculationId),
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<ChartCatalogResponse>(),
    // Only fetch once a calc exists — the contract scopes by `calculationId` alone, so a
    // no-calc request has no identifier. Package mode shows the empty "needs a calc" state.
    enabled: (options?.enabled ?? true) && req.calculationId !== null,
    placeholderData: keepPreviousData,
    staleTime: 1_800_000, // catalog is config, released with deploys — rarely changes
  });
}

/**
 * Fetches the Custom builder's dropdown vocabulary (`POST /queries/packages/chart_field_options`)
 * — the plottable `fields` (each with `type` / `planes` / `roles` / `format` / gating, and
 * for numeric fields `kind` / `defaultAggregate` / `aggregates`) plus the `styles` /
 * `aggregates` / `planes` enums and the `styleByX` / `aggregateKinds` maps, served from the
 * authoritative BE `FIELDS` registry so the builder never hardcodes a parallel field list.
 * Scoped by `calculationId` alone (the contract carries no `packageId`); gated on a calc
 * existing, so the dropdowns stay empty until a calc exists. `calculationId` is sent but
 * IGNORED (the registry is identical for every eval), so the query key carries no calc
 * dimension — switching between evals is a cache hit, not a refetch. The vocabulary is
 * config-like (changes with deploys), so it stays fresh for 30 min.
 *
 * @param {ChartFieldOptionsRequest} req - The (calc-mode) calculation id.
 * @param {object} [options] - `enabled` gate — pass `false` to defer the fetch. AND-ed with
 *   the calc gate.
 * @return {object} The React Query result for the field-options vocabulary.
 */
export function useChartFieldOptions(
  req: ChartFieldOptionsRequest,
  options?: { enabled?: boolean }
): UseQueryResult<ChartFieldOptionsResponse, Error> {
  return useQuery<ChartFieldOptionsResponse, Error>({
    // No calc dimension in the key — the response is the static `FIELDS` registry (the id is
    // sent but ignored), so switching evals must stay a cache hit rather than refetch.
    queryKey: [CHART_FIELD_OPTIONS_QUERY_KEY],
    queryFn: ({ signal }) =>
      api
        .post(CHART_FIELD_OPTIONS_URL, {
          json: calcModeField(req.calculationId),
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<ChartFieldOptionsResponse>(),
    // Only fetch once a calc exists — the contract scopes by `calculationId` alone, so a
    // no-calc request has no identifier. Package mode leaves the builder dropdowns empty.
    enabled: (options?.enabled ?? true) && req.calculationId !== null,
    placeholderData: keepPreviousData,
    staleTime: 1_800_000, // vocabulary is config, released with deploys — rarely changes
  });
}

/**
 * Fetches one chart's render-ready series (`POST /queries/packages/chart_series`) for a
 * `ChartSpec`. Scope is `calculationId` (calc mode — every dataSource needs a calc, so the
 * fetch is gated on `useCalcLifecycle.canFetch` and on the spec being complete). Dataset
 * filters ride inside `spec.filters` (so chart data matches the table for the same
 * filters); `mapScope` is a sibling field. Surfaces `meta` (sampled/cap) and a typed
 * `ChartSeriesError` (parsed `{ code, field }`) so a bad Custom field shows inline.
 *
 * @param {ChartSeriesRequest} req - The calc id, the `ChartSpec`, and (optional) map scope.
 * @param {object} [options] - `enabled` gate (AND-ed with the calc + spec gates).
 * @return {object} The React Query result for the series.
 */
export function useChartSeries(
  req: ChartSeriesRequest,
  options?: { enabled?: boolean }
): UseQueryResult<ChartSeriesResponse, ChartSeriesError> {
  // Every dataSource reads ValueRollup / the warehouse parquet, which exist ONLY for a
  // COMPLETED calc — so gate on `completed`, not `canFetch` (which also allows package
  // mode, where the endpoint always 422s `calculation_required`). Package mode → the body
  // shows the "requires a calc" empty state instead of firing a doomed request.
  const { state } = useCalcLifecycle(req.calculationId);
  // A complete spec: an X field and at least one series. An in-progress Custom build
  // (no series yet) must not fire a guaranteed-422 request.
  const hasSpec = Boolean(req.spec.x.field) && req.spec.series.length > 0;

  return useQuery<ChartSeriesResponse, ChartSeriesError>({
    queryKey: [
      CHART_SERIES_QUERY_KEY,
      {
        calculationId: req.calculationId ?? null,
        spec: req.spec,
        mapScope: req.mapScope ?? null,
      },
    ],
    queryFn: async ({ signal }) => {
      // `calculationId` scopes calc mode; the spec (with its `filters`) drives the
      // projection; `mapScope` narrows spatially. Omitted when unset so the body stays
      // clean and matches the normalized key.
      const payload = {
        ...calcModeField(req.calculationId),
        spec: req.spec,
        ...(req.mapScope ? { mapScope: req.mapScope } : {}),
      };
      try {
        return await api
          .post(CHART_SERIES_URL, {
            json: payload,
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<ChartSeriesResponse>();
      } catch (err) {
        // Surface the envelope's `code`/`field` (e.g. `invalid_field` → `spec.x.field`)
        // instead of a bare status. `retryable` is threaded too: chart_series renders it
        // for both snapshot codes, and dropping it would default a permanent failure to
        // retryable and put a dead Retry back on this surface.
        if (err instanceof HTTPError) {
          const envelope = await readErrorEnvelope(err);
          throw new ChartSeriesError(
            envelope?.message ?? err.message,
            envelope?.code,
            envelope?.field,
            envelope?.retryable ?? true
          );
        }
        throw err;
      }
    },
    enabled: state === 'completed' && hasSpec && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 1_800_000,
  });
}

// ─── Chart-config persistence (per-eval chart layout, S7) ───

/** `chart_config` payload schema version — a forward-compat migration hook. */
export const CHART_CONFIG_VERSION = 1;

/** Debounce for the chart-config autosave — one PATCH settles a burst of edits. */
export const CHART_AUTOSAVE_MS = 800;

/**
 * The custom chart's authored spec, minus the live page `filters` (never persisted — merged
 * fresh at fetch time). Stays camelCase; the server snake-cases it at rest and camelizes it
 * back on read, so the FE never sees snake.
 *
 * @param {ChartSpec} [spec] - The custom chart's authored spec.
 * @return {ChartSpec | undefined} The filter-free spec.
 */
function specWithoutFilters(spec?: ChartSpec): ChartSpec | undefined {
  if (!spec) {
    return undefined;
  }
  const next = { ...spec };
  delete next.filters;
  return next;
}

/**
 * Serializes the in-memory chart list into the persisted `ChartConfig`. Pure camelCase —
 * sent like any request body (`rails-boost` snake-cases it at rest; the jbuilder camelizes it
 * back on read), so no client-side casing. The single enforcement point of the storage
 * contract: a CATALOG instance persists id-only (its `spec`/`toggle` re-resolve from the catalog
 * on load, so preset fixes propagate); a CUSTOM instance persists its authored `spec` with
 * `filters` stripped (page filters stay live at fetch time, never saved). The work-area grid
 * (Rows × Columns, `chartGridAtom`) persists alongside the charts. Fixed key order →
 * `JSON.stringify` is a stable change-detection signal.
 *
 * @param {Chart[]} charts - The current chart instances.
 * @param {{rows: number, columns: number}} grid - The current work-area grid dimensions.
 * @return {ChartConfig} The persist-ready payload.
 */
export function serializeChartConfig(
  charts: Chart[],
  grid: { rows: number; columns: number }
): ChartConfig {
  return {
    version: CHART_CONFIG_VERSION,
    charts: charts.map<ChartInstanceConfig>((chart) => {
      if (chart.source === 'catalog') {
        return {
          instanceId: chart.instanceId,
          source: 'catalog',
          catalogId: chart.catalogId,
          name: chart.name,
          visible: chart.visible,
        };
      }
      return {
        instanceId: chart.instanceId,
        source: 'custom',
        name: chart.name,
        visible: chart.visible,
        spec: specWithoutFilters(chart.spec),
      };
    }),
    grid: { ...grid },
  };
}

/**
 * Rehydrates the persisted (camelCase) `ChartConfig` into the `Chart` list — the inverse of
 * `serializeChartConfig`. A CATALOG instance re-resolves its `spec`/`toggle` from the catalog
 * (`catalogById`); a stale `catalogId` (preset removed) is DROPPED (pruned on the next save). A
 * CUSTOM instance carries its persisted spec verbatim. No key mapping — the server already
 * camelized the payload.
 *
 * @param {ChartConfig} config - The persisted layout from `CalcDetail.chartConfig`.
 * @param {Map<string, ChartCatalogEntry>} catalogById - Catalog entries keyed by id, for re-resolution.
 * @return {Chart[]} The hydrated chart instances.
 */
export function hydrateChartConfig(
  config: ChartConfig,
  catalogById: Map<string, ChartCatalogEntry>
): Chart[] {
  return (config.charts ?? []).flatMap<Chart>((inst) => {
    if (inst.source === 'catalog') {
      const entry = inst.catalogId
        ? catalogById.get(inst.catalogId)
        : undefined;
      return entry
        ? [
            {
              instanceId: inst.instanceId,
              source: 'catalog',
              catalogId: inst.catalogId,
              name: inst.name,
              visible: inst.visible,
              spec: entry.spec,
              toggle: entry.toggle,
            },
          ]
        : [];
    }
    return [
      {
        instanceId: inst.instanceId,
        source: 'custom',
        name: inst.name,
        visible: inst.visible,
        spec: inst.spec,
      },
    ];
  });
}

/**
 * Sanitizes a persisted grid for `chartGridAtom` — the read-side twin of the Rows/Columns
 * inputs' 1–4 clamp (the `chartsGridRows/Columns${n}` CSS classes only exist for 1–4, and
 * persisted JSON is not trusted). Per dimension: a finite number rounds and clamps into range;
 * anything else — including a legacy config with no `grid` at all — falls back to that
 * dimension's `DEFAULT_CHART_GRID` value.
 *
 * @param {Object} [grid] - The persisted grid (`ChartConfig`'s `grid`), if any.
 * @return {{rows: number, columns: number}} The safe grid to seed.
 */
export function sanitizeChartGrid(grid?: {
  rows?: unknown;
  columns?: unknown;
}): { rows: number; columns: number } {
  const dimension = (value: unknown, fallback: number): number => {
    return typeof value === 'number' && Number.isFinite(value)
      ? Math.min(4, Math.max(1, Math.round(value)))
      : fallback;
  };
  return {
    rows: dimension(grid?.rows, DEFAULT_CHART_GRID.rows),
    columns: dimension(grid?.columns, DEFAULT_CHART_GRID.columns),
  };
}

/**
 * Persists the per-eval chart layout (`PATCH /evaluations/calculations/:id { chart_config }`),
 * mirroring `useRenameEval`. On success it invalidates `[CALC_DETAIL_QUERY_KEY, id]` so React
 * Query refetches the calc detail from the BE (the source of truth) — the idiomatic
 * invalidate-on-mutation flow, rather than hand-writing the value we just sent into the cache.
 * `useCalcDetail` is `staleTime:Infinity` and never auto-refetches, so this invalidation is what
 * (a) reconciles the cached `chartConfig` after a save — without it the stale pre-save config
 * lingers and the layout vanishes when you navigate back to the eval (SPA nav) — and (b) unsticks
 * a stale detail (e.g. a calc whose Pusher completion this cache missed, still reading
 * non-`Completed`, which gates the chart / table reads off) that would otherwise never self-heal.
 * `refetchType: 'all'` makes it refetch even an INACTIVE detail query: a flushed save (calc
 * switch / unmount) lands after the query left the screen, where the default `'active'` only
 * marks it stale — navigating back would then seed from the stale cache and a later edit could
 * PATCH the pre-flush state back over the save.
 * The ensuing refetch does NOT re-seed `chartsAtom` mid-edit: the `loadedRef` guard in
 * `useChartPersistence` re-hydrates only on an eval switch / first config arrival, so the atom
 * stays the source of truth once loaded. Last-write-wins across concurrent editors (v1).
 *
 * @return {object} The React Query mutation.
 */
export function useSaveCharts(): UseMutationResult<
  unknown,
  Error,
  { id: number; chart_config: ChartConfig }
> {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: number; chart_config: ChartConfig }>(
    {
      // Read via member access (no `chart_config` local binding — that snake identifier trips
      // the camelcase lint rule); the wire key itself is an object-literal property (allowed).
      mutationFn: (vars) =>
        api
          .patch(`evaluations/calculations/${vars.id}`, {
            json: { chart_config: vars.chart_config },
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json(),
      // Invalidate the calc detail so React Query refetches BE truth (proper invalidation over a
      // hand-written cache entry). useCalcDetail is staleTime:Infinity, so without this the cache
      // keeps the pre-save chartConfig — the layout would vanish on navigate-back (SPA nav) — and
      // a stale detail (e.g. a missed-completion calc still reading non-`Completed`, which gates
      // the chart / table reads off) would never self-heal. refetchType 'all' covers a flushed
      // save landing after the query went inactive (calc switch / unmount) — default 'active'
      // would only mark it stale, so navigating back would seed from the pre-save cache. The
      // loadedRef guard in useChartPersistence keeps the ensuing refetch from re-seeding
      // chartsAtom mid-edit.
      onSuccess: (_res, vars) => {
        void queryClient.invalidateQueries({
          queryKey: [CALC_DETAIL_QUERY_KEY, vars.id],
          refetchType: 'all',
        });
      },
    }
  );
}

/**
 * The per-eval chart-layout persistence engine — hydrate on load + debounced autosave on change,
 * in ONE hook. A single `loadedRef` ({ calcId, baseline }) ties them together without an atom.
 * Mounted once via the null `ChartsPersistenceSync` sentinel so the atom subscriptions re-render
 * only that sentinel, not the heavy page root.
 *
 * HYDRATE: on calc switch, seeds `chartsAtom` from `CalcDetail.chartConfig` (catalog instances
 * re-resolve spec/toggle from the catalog, stale ids dropped; custom instances carry their spec)
 * and seeds `chartGridAtom` from `config.grid` (sanitized; 2×2 default when absent). Clears while
 * the detail / catalog is still resolving so a prior eval's charts never linger; draft / package
 * mode stays empty. The hydrated state becomes the autosave baseline, so the immediate
 * re-observation no-ops.
 *
 * AUTOSAVE: on any `chartsAtom` / `chartGridAtom` change once loaded, serializes and — when it
 * differs from the baseline — debounce-PATCHes it. The debounced payload carries its own calc id;
 * leaving the calc flushes the pending save first so the next calc's edit can't clobber it, and
 * unmounting (leaving the eval3 page) flushes it too. Last-write-wins (v1).
 */
export function useChartPersistence(): void {
  const { calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const config = calcDetail?.chartConfig;

  const setCharts = useSetAtom(chartsAtom);
  const setGrid = useSetAtom(chartGridAtom);
  const charts = useAtomValue(chartsAtom);
  const grid = useAtomValue(chartGridAtom);

  const { mutate } = useSaveCharts();

  const persisted = useMemo<ChartInstanceConfig[]>(
    () => config?.charts ?? [],
    [config]
  );
  const hasCatalogCharts = persisted.some((inst) => inst.source === 'catalog');
  // Catalog specs re-resolve from the catalog on load; fetch it only when there are catalog
  // charts to resolve (custom-only or empty layouts skip the request).
  const { data: catalog } = useChartCatalog(
    { calculationId },
    { enabled: hasCatalogCharts }
  );
  const catalogById = useMemo(
    () => new Map((catalog?.charts ?? []).map((entry) => [entry.id, entry])),
    [catalog]
  );

  // What we've LOADED into chartsAtom: the calc id + its serialized baseline. `null` until
  // hydrated, so autosave stays quiet through the clear-then-seed transient. A same-calc config
  // echo (our own save syncing CalcDetail) is ignored — only a switch to a different calc re-seeds.
  const loadedRef = useRef<{ calcId: number; baseline: string } | null>(null);

  const debouncedSave = useMemo(
    () =>
      debounce(
        (id: number, next: ChartConfig) => mutate({ id, chart_config: next }),
        CHART_AUTOSAVE_MS
      ),
    [mutate]
  );

  // The sentinel unmounting (leaving the eval3 page) must flush the pending trailing save —
  // otherwise an edit made within CHART_AUTOSAVE_MS of navigating away is silently dropped.
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  useEffect(() => {
    // Leaving the calc we loaded (switch or → package mode) → flush its pending save first, or
    // the next calc's edit would clobber lodash's not-yet-fired trailing call (payload carries id).
    const loaded = loadedRef.current;
    if (loaded && loaded.calcId !== calculationId) {
      debouncedSave.flush();
    }

    // Draft / package mode, or the detail not loaded yet → clear + stay quiet until it arrives.
    if (calculationId === null || !config) {
      loadedRef.current = null;
      setCharts({ type: 'clear' });
      setGrid({ ...DEFAULT_CHART_GRID });
      return;
    }
    // Same calc already loaded → ignore config echoes (our own save) so edits aren't reverted.
    if (loaded?.calcId === calculationId) {
      return;
    }
    // Catalog charts need the catalog to re-resolve their spec — clear + wait for it.
    if (hasCatalogCharts && (catalog?.charts.length ?? 0) === 0) {
      setCharts({ type: 'clear' });
      return;
    }

    // Entering a new calc — seed the atoms (grid seeds from the persisted config, sanitized;
    // 2×2 default when absent) and adopt the hydrated state as the autosave baseline so the
    // immediate re-observation is a no-op.
    const hydrated = hydrateChartConfig(config, catalogById);
    const seededGrid = sanitizeChartGrid(config.grid);
    setCharts({ type: 'set', charts: hydrated });
    setGrid(seededGrid);
    loadedRef.current = {
      calcId: calculationId,
      baseline: JSON.stringify(serializeChartConfig(hydrated, seededGrid)),
    };
  }, [
    calculationId,
    config,
    persisted,
    hasCatalogCharts,
    catalog,
    catalogById,
    setCharts,
    setGrid,
    debouncedSave,
  ]);

  useEffect(() => {
    const loaded = loadedRef.current;
    if (calculationId === null || loaded === null) {
      return;
    }
    const next = serializeChartConfig(charts, grid);
    const serialized = JSON.stringify(next);
    if (serialized === loaded.baseline) {
      return;
    }
    loaded.baseline = serialized;
    debouncedSave(calculationId, next);
  }, [charts, grid, calculationId, debouncedSave]);
}

/** Query-key prefix for the map-element id resolution (visibility + color). */
export const MAP_ELEMENTS_QUERY_KEY = 'eval3-new:map-elements' as const;

/**
 * Endpoint path for the map-element id resolver. No leading slash — the `api`
 * (ky) instance sets `prefixUrl`, so paths must be relative.
 */
export const MAP_ELEMENTS_URL = 'queries/packages/map_elements';

/**
 * Whether a `map_elements` request narrows the map at all — at least one dataset
 * filter, a spatial scope, or the asset-picker's `interestModelableOnly` flag (the picker
 * opens unfiltered and its map must still fetch the modelable subset). When false
 * the visibility query is disabled (the resolver is never called with an empty
 * page state) and the layers show everything.
 *
 * @param {object} req - The request's filters / map scope / picker flag.
 * @return {boolean} True when the request carries filters, a map scope, or the picker flag.
 */
function hasMapNarrowing(
  req: Pick<
    MapElementsRequest,
    'filters' | 'mapScope' | 'interestModelableOnly'
  >
): boolean {
  return (
    (req.filters?.length ?? 0) > 0 ||
    Boolean(req.mapScope) ||
    Boolean(req.interestModelableOnly)
  );
}

/**
 * Resolves the map's visible id set (`POST /queries/packages/map_elements`) for the
 * current page filter state — the `{ uwis, names }` the scoped layers filter on.
 * ONE combined call (filters + mapScope); disabled when neither is set (no
 * narrowing → the layers show everything). Keyed on packageId + the server filters
 * + the map scope (normalized so "nothing set" is one stable entry); the server
 * filter `catalog` IS honored here (it scopes which element types come back), unlike
 * on `master_data` / `executive_summary`. `keepPreviousData` holds the last-good
 * visibility through a refetch. Each of the three scoped layers calls this with an
 * identical `req`, so React Query dedupes them to a single fetch (no atom, no
 * effect-mirror). `mapScope` is threaded in from `mapScopeAtom` by the layer in P2-T26.
 *
 * @param {MapElementsRequest} req - The package + calc id (from context, passed by the caller) + active filters / map scope.
 * @return {object} The React Query result for the matched id set.
 */
export function useMapElements(
  req: MapElementsRequest
): UseQueryResult<MapElements, Error> {
  // `packageId` scopes the resolve; `calculationId` (the URL `:evalId`) → calc mode
  // (eval-scoped filters accepted). Both arrive via `req` (the caller reads page
  // context). Keyed on both (modes cache apart).
  //
  // Strip the `catalog` tag from each filter for the VISIBILITY call (INS-10376).
  // Visibility wants the matched population's FULL related set (wells + units +
  // tracts + subtracts) — a tract filter must still light up its wells/units on
  // the map. An ABSENT catalog is `map_elements`' documented signal for that
  // ("no/any untagged predicate → return every element type"), so stripping opts
  // this call into it. Narrowing is unaffected — the BE routes predicates by
  // ATTRIBUTE, not catalog.
  //
  // Why strip on the FE, not ignore `catalog` on the BE (PR #6853 review): the SAME
  // `map_elements` endpoint also backs COLOR-BY (`colorGroupQueryOptions`), which
  // sends a catalog-TAGGED filter and RELIES on the scoping so a color rule paints
  // only its own element type — a `wells` rule resolves to `uwis` only, never unit/
  // tract names, else the color bleeds across layers. So `catalog` is a per-CALL
  // signal, not a global one; the BE can't blanket-ignore it without breaking color.
  const filters = (req.filters ?? []).map((filter) => {
    const stripped = { ...filter };
    delete stripped.catalog;
    return stripped;
  });
  const { canFetch } = useCalcLifecycle(req.calculationId);
  // INS-11129 Phase 2 — the calc id rides every read; the BE hybridizes when the fold
  // in the body diverges from the stored one, so the map id-sets track the same
  // population master_data serves.
  const pendingTcWire = useAtomValue(pendingTypeCurveWireAtom);
  return useQuery<MapElements, Error>({
    queryKey: [
      MAP_ELEMENTS_QUERY_KEY,
      req.packageId,
      {
        calculationId: req.calculationId ?? null,
        // Part of the key: the scenario decides which LTD places exist, so a pick has to
        // re-resolve rather than reuse the previous scenario's highlight (INS-10669).
        typeCurveScenarioId: req.typeCurveScenarioId ?? null,
        // And so do the TC Manager's choices, for the same reason. Encoded so a reordering
        // cannot split the cache.
        typeCurveOverrides:
          encodeTypeCurveOverrides(req.typeCurveOverrides ?? []) ?? null,
        inventoryPackageOverrides:
          encodeInventoryPackageOverrides(
            req.inventoryPackageOverrides ?? []
          ) ?? null,
        filters,
        mapScope: req.mapScope ?? null,
        // Asset-picker scope — normalized so absent == false. NOTE: a
        // flagged entry deliberately never dedupes with `colorGroupQueryOptions`
        // (color resolution is page-only; the picker mounts no color rules).
        interestModelableOnly: req.interestModelableOnly ?? false,
      },
    ],
    queryFn: async ({ signal }) => {
      // Omit the empty halves so the request body is clean + matches the normalized
      // cache key. `calculationId` puts the request in calc mode when set.
      const payload = {
        packageId: req.packageId,
        ...calcModeField(req.calculationId),
        ...(typeof req.typeCurveScenarioId === 'number'
          ? { typeCurveScenarioId: req.typeCurveScenarioId }
          : {}),
        ...(filters.length ? { filters } : {}),
        ...(req.mapScope ? { mapScope: req.mapScope } : {}),
        // The BE derives the effective TC choices from the override LOG
        // (SlotOverrideEngine.type_curve_overrides), so they ride as `slot_overrides`
        // entries rather than a parameter of their own — the same channel the table and
        // the catalogs already use. PENDING entries (deltas + tombstones): the BE folds
        // committed via the calc id, and only a tombstone can delete a committed choice.
        ...(pendingTcWire.length ? { slot_overrides: pendingTcWire } : {}),
        ...(req.interestModelableOnly ? { interestModelableOnly: true } : {}),
      };
      // TEMPORARY timeout (see EVAL3_REQUEST_TIMEOUT_MS) — map_elements runs the
      // same heavy slot population as master_data; decrease back once the BE is fast.
      try {
        return await api
          .post(MAP_ELEMENTS_URL, {
            json: payload,
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<MapElements>();
      } catch (err) {
        // INS-12433 — typed for consistency with the other envelope endpoints, NOT because
        // anything reads it today: every consumer (useMapVisibility, useFilteredMapBounds
        // and the three layer-filter hooks) destructures `data` only, so a failed
        // map_elements fetch is currently silent and the layers keep their last-good
        // visibility. No error UI is invented here. This only guarantees the next consumer
        // that DOES read it gets the BE's message and `retryable`, rather than "Unprocessable
        // Content" and a guess.
        return throwTypedEnvelopeError(err);
      }
    },
    // Disabled when there's nothing to narrow by — the layers fall back to "show
    // all" via useMapVisibility's null-on-inactive reset — and while the calc-mode
    // gate is closed (P5 rule).
    enabled: canFetch && hasMapNarrowing(req),
    placeholderData: keepPreviousData, // keep last-good visibility through a refetch
    staleTime: 60_000,
  });
}

/**
 * Layer-facing map visibility: the matched `MapElements`, or `null` when nothing
 * narrows the map. The `null`-on-inactive reset is load-bearing — when no filters /
 * scope are set the query is disabled but React Query still holds its last `data`,
 * so this MUST return `null` (not that stale set) to mean "no narrowing → show
 * everything". `null` (show all) is kept DISTINCT from `{ uwis: [], names: [] }` (a
 * filter that matched nothing → show nothing); this hook never coalesces empty to
 * null. While the calc-mode gate is CLOSED (`useCalcLifecycle.canFetch` — the viewed calc is
 * Processing) with narrowing set, the disabled query's retained data is returned
 * as-is: the frozen prior visibility, NOT null. The pure layer-filter builders
 * compose this with the pin (`mapHighlightAtom`) in P2-T25.
 *
 * @param {MapElementsRequest} req - The package + calc id (from context) + active filters / map scope.
 * @return {MapElements | null} The matched id set, or `null` to show everything.
 */
export function useMapVisibility(req: MapElementsRequest): MapElements | null {
  const { data } = useMapElements(req);
  return hasMapNarrowing(req) ? (data ?? null) : null;
}

/**
 * The camera-framing extent for the current filter/scope narrowing — the
 * `map_elements` `bbox` (union of the catalog-scoped highlighted features). Reads
 * the page filter state (serverFiltersAtom + mapScopeAtom) and calls useMapElements
 * with the SAME key the layers use, so it dedupes to their fetch (no extra request).
 * Returns `null` ONLY when nothing narrows the map (→ the caller falls back to the
 * package extent) or before the bbox resolves — a pure "here is the narrowed bbox"
 * data hook, with no knowledge of the spatial MODE. Whether to FRAME the camera to
 * that bbox is the consumer's call: `PackageMap`'s `fitToBounds` owns the camera
 * policy (and is where the Visible-Extent framing suppression + its rationale live).
 *
 * @param {number} packageId - The package to frame within.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null.
 * @return {BBox | null} The narrowed highlight's extent, or null when nothing narrows.
 */
export function useFilteredMapBounds(
  packageId: number,
  calculationId: number | null
): BBox | null {
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const interestModelableOnly = useAtomValue(interestModelableOnlyAtom);
  const req: MapElementsRequest = {
    packageId,
    calculationId,
    typeCurveScenarioId,
    typeCurveOverrides,
    inventoryPackageOverrides,
    filters,
    interestModelableOnly,
    mapScope,
  };
  const { data } = useMapElements(req);
  // No useMemo (unlike usePackageBounds): React Query keeps `data` referentially
  // stable and FitBoundsControl diffs bounds by VALUE (a joined signature), so a
  // fresh array identity per render is harmless here — don't "optimize" it away.
  return hasMapNarrowing(req) ? (data?.bbox ?? null) : null;
}

/**
 * React Query options for resolving ONE color group's ids
 * (`POST /queries/packages/map_elements`). Shared by `useColorElements` (the layer
 * paint) and `useColorRuleStatuses` (the panel's per-rule status) so both observe the
 * IDENTICAL key — a single-attribute value-only filter, no `mapScope` — and therefore
 * dedupe to one fetch and cache hard (color ids depend only on the value). Color
 * resolution is package-wide; the layer's visibility filter does the spatial scoping.
 *
 * @param {number} packageId - The package to resolve against.
 * @param {ColorGroup} group - One (rule × distinct color) group from `toColorGroups`.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null (package mode).
 * @param {boolean} enabled - The calc-mode gate (`useCalcLifecycle.canFetch`) — false disables the fetch.
 * @return {object} The React Query options for this group's map_elements call.
 */
function colorGroupQueryOptions(
  packageId: number,
  group: ColorGroup,
  calculationId: number | null,
  enabled: boolean
) {
  return {
    enabled,
    queryKey: [
      MAP_ELEMENTS_QUERY_KEY,
      packageId,
      // Same key shape as `useMapElements` (calc id + a value-only filter, no scope,
      // never picker-scoped — color rules are page-only), so a color call caches
      // hard and can share a visibility call's entry.
      {
        calculationId: calculationId ?? null,
        filters: [group.serverFilter],
        mapScope: null,
        interestModelableOnly: false,
      },
    ],
    // Built outside the `useQueries` call, so nothing types the query-function context
    // here: the `signal` is typed explicitly.
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api
        .post(MAP_ELEMENTS_URL, {
          // A group's filter is always present + non-empty, so the body sends it
          // unconditionally (no conditional spread like `useMapElements`); `mapScope`
          // is omitted — color is package-wide — matching the `mapScope: null` key.
          // `calculationId` (calc mode) is sent when set so eval-scoped color rules
          // resolve.
          json: {
            packageId,
            ...calcModeField(calculationId),
            filters: [group.serverFilter],
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<MapElements>(),
    staleTime: 1_800_000, // ids depend only on the value → cache hard (30 min)
  };
}

/**
 * Resolves the map ids each color group paints (`POST /queries/packages/map_elements`,
 * one call per group). `toColorGroups` expands the dataset's map-painting rules into
 * one value-only group per distinct color; this fans out a `useQueries` POST per group
 * and stitches the responses back into `ColorElements[]` (in group order, so
 * `buildColorExpression`'s last-listed-wins precedence matches the rule listing). Each
 * call's key is the SAME `[MAP_ELEMENTS_QUERY_KEY, packageId, { filters, mapScope }]`
 * shape the visibility hook uses — keyed only on the single-attribute, value-only server
 * filter with no `mapScope` — so the ids depend only on the values and cache hard (a
 * visibility call with the identical single filter would even dedupe to it). Color
 * resolution is package-wide (no `mapScope`); the layer's visibility filter does the
 * spatial scoping. Pass a STABLE `rules` reference (the layer memoizes the map-painting
 * subset) so the groups + the returned array identity don't churn the layer's paint memo.
 *
 * @param {number} packageId - The package whose color ids to resolve.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null.
 * @param {ColorRule[]} rules - The dataset's map-painting color rules (enabled ∧ targeting the map), in listing order.
 * @return {ColorElements[]} The resolved color → ids list, in rule-then-color order.
 */
export function useColorElements(
  packageId: number,
  calculationId: number | null,
  rules: ColorRule[]
): ColorElements[] {
  // One resolution group per (rule × distinct color); memoized on the rules
  // reference so the query list + the merge below only change when the rules do.
  const groups = useMemo(() => toColorGroups(rules), [rules]);

  const { canFetch } = useCalcLifecycle(calculationId);

  // One map_elements POST per group. `useQueries` preserves the queries' order, so
  // `results[i]` is the response for `groups[i]` — the 1:1 alignment that
  // `mergeColorElements`'s positional zip (and the last-listed-wins precedence) relies on.
  const results = useQueries({
    queries: groups.map((group) =>
      colorGroupQueryOptions(packageId, group, calculationId, canFetch)
    ),
  });

  // `useQueries` returns a fresh array identity every render, so memoize the stitched
  // result on a stable signature of each query's status + last-updated stamps (the
  // `useBulkAvailableAfes` pattern) — this keeps the returned `ColorElements[]`
  // referentially stable when nothing actually changed, so the layer's paint memo
  // (which depends on it) doesn't churn during map interaction.
  const signature = results
    .map(
      (result) =>
        `${result.status}:${result.dataUpdatedAt}:${result.errorUpdatedAt}`
    )
    .join('|');

  return useMemo<ColorElements[]>(
    () => mergeColorElements(groups, results),
    // `results` is read through `signature` (stable) rather than listed directly —
    // its identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, signature]
  );
}

/** One color rule's aggregate `map_elements` resolution, for its `ColorRuleCard`. */
export interface ColorRuleStatus {
  status: 'loading' | 'error' | 'ready';
  onRetry: () => void;
}

/**
 * Per-rule color-resolution status for the Color by Attribute cards (resolves audit
 * #5). Observes the SAME `map_elements` query keys `useColorElements` uses (so the
 * panel sees the layer's color calls with no second fetch — React Query dedupes by
 * key), tagging each group with its rule id so the per-group results aggregate UP to
 * one status per rule: `error` if any of the rule's groups errored, else `loading`
 * while any are still fetching, else `ready`. `onRetry` refetches that rule's
 * group(s). Keyed by `rule.id`; a rule with no colored values (no groups) is absent
 * from the map (its card shows no status). Returns a stable object between real
 * status changes (the `useBulkAvailableAfes` signature-memo).
 *
 * @param {number} packageId - The package to resolve against.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null.
 * @param {ColorRule[]} rules - The dataset's color rules (the cards rendered).
 * @return {Record<string, ColorRuleStatus>} Status + retry per rule id.
 */
export function useColorRuleStatuses(
  packageId: number,
  calculationId: number | null,
  rules: ColorRule[]
): Record<string, ColorRuleStatus> {
  // Each group tagged with its rule id, so the per-group results below aggregate per
  // rule. `flatMap` keeps the rule→groups boundary `toColorGroups` flattens away.
  const taggedGroups = useMemo(
    () =>
      rules.flatMap((rule) =>
        toColorGroups([rule]).map((group) => ({ ruleId: rule.id, group }))
      ),
    [rules]
  );

  const { canFetch } = useCalcLifecycle(calculationId);

  const results = useQueries({
    queries: taggedGroups.map(({ group }) =>
      colorGroupQueryOptions(packageId, group, calculationId, canFetch)
    ),
  });

  // Stable signature (see useColorElements) so the returned map keeps its identity
  // between real status changes. `fetchStatus` is included here (unlike
  // useColorElements, which is data-driven) because the loading aggregation below
  // reads `isLoading` — a fetching flip must recompute the memo.
  const signature = results
    .map(
      (result) =>
        `${result.status}:${result.fetchStatus}:${result.dataUpdatedAt}:${result.errorUpdatedAt}`
    )
    .join('|');

  return useMemo<Record<string, ColorRuleStatus>>(() => {
    // Collect each rule's group flags + refetchers, then aggregate.
    const byRule = new Map<
      string,
      { hasError: boolean; isLoading: boolean; refetchers: (() => void)[] }
    >();
    taggedGroups.forEach(({ ruleId }, index) => {
      const result = results[index];
      const entry = byRule.get(ruleId) ?? {
        hasError: false,
        isLoading: false,
        refetchers: [],
      };
      entry.hasError ||= result.isError;
      // `isLoading` (NOT `isPending`): a DISABLED query with no data is also pending,
      // and these reads are disabled while the calc-mode gate is closed
      // (`useCalcLifecycle.canFetch`) — a card must not spin for the whole run. Same
      // guard `useBulkAvailableAfes` uses.
      entry.isLoading ||= result.isLoading;
      entry.refetchers.push(result.refetch);
      byRule.set(ruleId, entry);
    });

    const statusByRule: Record<string, ColorRuleStatus> = {};
    byRule.forEach(({ hasError, isLoading, refetchers }, ruleId) => {
      const status: ColorRuleStatus['status'] = hasError
        ? 'error'
        : isLoading
          ? 'loading'
          : 'ready';
      statusByRule[ruleId] = {
        status,
        onRetry: () => refetchers.forEach((refetch) => refetch()),
      };
    });
    return statusByRule;
    // `results` is read through `signature` (stable) — its identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taggedGroups, signature]);
}

/**
 * Clears the pinned "show on map" row on a genuine filter/scope change (FR#4).
 * Watches `pinResetSignalAtom` — the derived signal whose reference changes only on
 * a dataset-filter change, a spatial MODE change, or an AOI polygon commit, and
 * which deliberately ignores Visible-Extent pans (the carve-out: clearing there would
 * loop pin → fitToBounds → moveend → clear). On a change it dispatches `locatedRowAtom`
 * `clear`. Called once from the always-mounted `Eval3NewContent` (it survives hiding
 * the map or the table), which therefore re-renders only on those pin-clearing events,
 * not on every pan. A UI-state reset, not data fetching — hence a `useEffect`; the
 * first run (mount) is skipped so an initial render doesn't churn the (already-empty)
 * pin.
 *
 * @return {void}
 */
export function usePinResetOnFilterChange(): void {
  const signal = useAtomValue(pinResetSignalAtom);
  const dispatch = useSetAtom(locatedRowAtom);
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    dispatch({ type: 'clear' });
  }, [signal, dispatch]);
}

// ─── Visibility ∩ pin: per-layer map filters ───
// The scoped layers (Slot / Tract / Unit) call these to get their Mapbox `filter` —
// the page-filter VISIBILITY (`useMapVisibility` over the dataset filters + spatial
// scope) intersected with the PIN (`mapHighlightAtom`). All three layers request the
// same visibility, so React Query dedupes to one `map_elements` fetch. The `useMemo`
// is LOAD-BEARING: these layers re-render during map interaction, and a fresh filter
// array each render makes react-map-gl re-apply the filter and visibly jank zoom.
// Both sets are stable references between changes, so the memo keeps one filter array
// until the selection actually changes. Each layer calls its hook (its own
// per-instance memo). Do NOT inline these.

/**
 * The Slot layer's (`wells_v2`) Mapbox filters — the page-filter VISIBILITY
 * (`useMapVisibility` over the active dataset filters + spatial scope) intersected
 * with the PIN (`mapHighlightAtom`) by `uwi`, via `slotHighlightFilter`. Both null →
 * `['all']` (show every well); never `undefined` (mapbox rejects that).
 *
 * Returns three filters: `base` (survey line + the two hole points) plus the
 * `stick` / `eps` lateral-line gates that narrow it to the survey > stick > EPS
 * single-lateral hierarchy (see `slotLateralFilters`). All three are memoized on
 * the two stable sets so react-map-gl doesn't re-apply the filters + jank zoom.
 *
 * @param {number} packageId - The package whose map_elements visibility to resolve.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null.
 * @return {{ base: Expression, eps: Expression, stick: Expression }} The memoized slot-layer filters.
 */
export function useSlotHighlightFilter(
  packageId: number,
  calculationId: number | null
): { base: Expression; eps: Expression; stick: Expression } {
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  // Asset-picker scope — inside the picker's fresh Provider the layers'
  // visibility resolves the modelable subset (same key as the bounds hook → dedupes).
  const interestModelableOnly = useAtomValue(interestModelableOnlyAtom);
  const visibility = useMapVisibility({
    packageId,
    calculationId,
    typeCurveScenarioId,
    typeCurveOverrides,
    inventoryPackageOverrides,
    filters,
    mapScope,
    interestModelableOnly,
  });
  const pin = useAtomValue(mapHighlightAtom);
  return useMemo(() => {
    // `base` = visibility ∩ pin membership; `slotLateralFilters` gates it into the
    // survey > stick > EPS single-lateral hierarchy (see its doc).
    const base = slotHighlightFilter(visibility, pin);
    return { base, ...slotLateralFilters(base) };
  }, [visibility, pin]);
}

/**
 * The polygon layers' (Tract `assets` / Unit `development_areas`) Mapbox `filter` —
 * shape-only, then the page-filter VISIBILITY (`useMapVisibility`) intersected with
 * the PIN (`mapHighlightAtom`) by `name`, via `polygonHighlightFilter`. Both null →
 * shape-only (all polygons draw). Shares the one deduped `map_elements` fetch with
 * the other layers. Memoized on the two stable sets (load-bearing for smooth zoom).
 *
 * @param {number} packageId - The package whose map_elements visibility to resolve.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null.
 * @return {Expression} The memoized polygon-layer filter.
 */
export function usePolygonHighlightFilter(
  packageId: number,
  calculationId: number | null
) {
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  // Asset-picker scope — see useSlotHighlightFilter.
  const interestModelableOnly = useAtomValue(interestModelableOnlyAtom);
  const visibility = useMapVisibility({
    packageId,
    calculationId,
    typeCurveScenarioId,
    typeCurveOverrides,
    inventoryPackageOverrides,
    filters,
    mapScope,
    interestModelableOnly,
  });
  const pin = useAtomValue(mapHighlightAtom);
  return useMemo(
    () => polygonHighlightFilter(visibility, pin),
    [visibility, pin]
  );
}

/**
 * The LTD layer's Mapbox `filter` — the page-filter VISIBILITY intersected with the PIN by
 * `slot_id`, via `ltdSlotHighlightFilter`. Both null → every stick draws. Shares the one deduped
 * `map_elements` fetch with the other layers (same request shape, including the scenario read
 * from the atom, so the keys match and no second request is made).
 *
 * Memoized on the two stable sets — load-bearing for smooth zoom, as with the sibling hooks: a
 * fresh filter array identity on every render makes mapbox re-apply the layer filter.
 *
 * @param {number} packageId - The package whose map_elements visibility to resolve.
 * @param {number | null} calculationId - The eval-calc id (calc mode) or null.
 * @return {Expression} The memoized LTD-layer filter.
 */
export function useLtdSlotHighlightFilter(
  packageId: number,
  calculationId: number | null
): Expression {
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const visibility = useMapVisibility({
    packageId,
    calculationId,
    typeCurveScenarioId,
    typeCurveOverrides,
    inventoryPackageOverrides,
    filters,
    mapScope,
  });
  const pin = useAtomValue(mapHighlightAtom);
  return useMemo(
    () => ltdSlotHighlightFilter(visibility, pin),
    [visibility, pin]
  );
}

/**
 * The EFFECTIVE Type Curve Scenario for the run (INS-10669):
 *
 *     with a case selected:  the case's own scenario  (null while its detail loads)
 *     with no case:          the explicit pick  ??  null
 *
 * `null` is not "no scenario" — it means the FE asserts nothing and the BE applies its own
 * default (Legacy) in the single place that owns it, `Query::Packages::InsightsLTDMerge`. That is
 * why the chain deliberately stops here rather than substituting 1: a default named in two
 * places is how the table and the calculation came to disagree in the first place.
 *
 * With a case selected the explicit pick is ignored: a case-level run takes its Type Curve from
 * the case (the server stamps the case's at start), so the case's scenario is used, and nothing
 * (null) until the case's detail has loaded. With no case, the explicit pick, else null.
 *
 * It also publishes the resolved value to `typeCurveScenarioIdAtom`, which is what the read
 * surfaces key on — the table, the filter catalogs, the summary, the map layers and the tile
 * URLs. They are far from this form, and the alternative (threading the value through every one
 * of them as a prop) both churns a lot of call sites and risks two layers disagreeing. Publishing
 * once means there is a single value in play, and every map layer keeps building the same
 * `map_elements` query key so they still dedupe to one fetch.
 *
 * While a completed run of the selected case is shown, the surfaces read the Type Curve that run
 * started with instead (`runCaseScenarioId`), not the case's current one, which may have
 * changed since. The returned value stays the case's, since it is what the next run will use.
 *
 * The write is in an effect because the case's scenario arrives from a React Query read, not from
 * an atom — the one case where derived state cannot simply be computed on demand.
 *
 * @param {WorkingConfigForm} workingConfig - The eval settings form.
 * @return {number | null} The effective scenario id, or null to let the BE default apply.
 */
export function useTypeCurveParams(
  workingConfig: WorkingConfigForm
): number | null {
  const explicitScenarioId = useStore(
    workingConfig.form.store,
    (state) => state.values.typeCurveScenarioId
  );
  const evaluationCaseId = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseId
  );
  // The case's own scenario. A cache hit beside Case management's read of the same key; with no
  // case selected the hook gates itself off and no request is made.
  const { data: caseDetail } = useEvaluationCaseDetail(evaluationCaseId);
  // A case's run takes its Type Curve from the case, whatever a run saved before stored (the
  // server stamps the case's at start), so the ledger and the TC Manager follow the case's. With
  // no case, an explicit pick wins.
  const effectiveScenarioId =
    evaluationCaseId !== null
      ? (caseDetail?.typeCurveScenarioId ?? null)
      : (explicitScenarioId ?? null);

  // The shown run's detail: a cache hit beside the page's own read of it.
  const calculationId = useOptionalEval3Page()?.calculationId ?? null;
  const { data: calcDetail } = useCalcDetail(calculationId);
  const runScenarioId = runCaseScenarioId(
    calcDetail,
    evaluationCaseId,
    'type_curve_scenario_id'
  );
  const publishedScenarioId =
    runScenarioId !== undefined ? runScenarioId : effectiveScenarioId;

  const publish = useSetAtom(typeCurveScenarioIdAtom);
  useEffect(() => {
    publish(publishedScenarioId);
  }, [publish, publishedScenarioId]);

  return effectiveScenarioId;
}

/**
 * The DCA scenario the eval shows, which a new DCA workspace opens on. Needs no run.
 *
 * With a case selected: the scenario a completed run of that case started with, else the case's
 * own (null until the case's detail has loaded). With no case: the Eval Settings pick, which an
 * opened eval seeds from its run.
 *
 * @param {WorkingConfigForm} workingConfig - The eval settings form.
 * @return {number | null} The DCA scenario id, or null for none.
 */
export function useShownDcaScenarioId(
  workingConfig: WorkingConfigForm
): number | null {
  const pickedScenarioId = useStore(
    workingConfig.form.store,
    (state) => state.values.dcaScenarioId
  );
  const evaluationCaseId = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseId
  );
  const { data: caseDetail } = useEvaluationCaseDetail(evaluationCaseId);
  const calculationId = useOptionalEval3Page()?.calculationId ?? null;
  const { data: calcDetail } = useCalcDetail(calculationId);

  if (evaluationCaseId === null) return pickedScenarioId ?? null;

  const runScenarioId = runCaseScenarioId(
    calcDetail,
    evaluationCaseId,
    'dca_scenario_id'
  );
  if (runScenarioId !== undefined) return runScenarioId;
  return caseDetail?.dcaScenarioId ?? null;
}

/** Query-key prefix for the Type Curve Scenario picker options. */
export const TYPE_CURVE_SCENARIO_OPTIONS_QUERY_KEY =
  'eval3-new:type-curve-scenario-options' as const;

/**
 * The selectable Type Curve Scenarios for the left panel's picker.
 *
 * Reuses the EXISTING reservoir listing endpoint (`POST /queries/reservoir/type_curve_scenarios`)
 * rather than adding one — the picker needs an id and a name, which that already returns.
 * Package-independent, so it is keyed on nothing and cached for the session.
 *
 * @return {object} The React Query result; `data` is the scenario options.
 */
export function useTypeCurveScenarioOptions(): UseQueryResult<
  TypeCurveScenarioOption[],
  Error
> {
  return useQuery<TypeCurveScenarioOption[], Error>({
    queryKey: [TYPE_CURVE_SCENARIO_OPTIONS_QUERY_KEY],
    queryFn: async ({ signal }) => {
      const response = await api
        .post('queries/reservoir/type_curve_scenarios', {
          json: {},
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<{ results?: TypeCurveScenarioOption[] }>();
      return response.results ?? [];
    },
    staleTime: Infinity, // the scenario list does not change while the page is open
  });
}

/** Query-key prefix for the Type Curve Scenario ledger under the picker. */
export const TYPE_CURVE_SUMMARY_QUERY_KEY =
  'eval3-new:type-curve-summary' as const;

/**
 * The ledger under the scenario picker (`POST /queries/packages/type_curve_summary`) — LTD
 * places, LTD-capable units, and permits/DUCs coverage for the selected scenario.
 *
 * Keyed on the scenario so a pick refreshes it; `null` omits the param and the BE summarizes its
 * Legacy default, matching what the table would show. Every figure comes from the same resolver /
 * slot-read / reconciler the table runs, so the panel cannot quote a different place count than
 * the grid renders.
 *
 * @param {object} params - The package + selected scenario.
 * @param {number} params.packageId - The package the figures are computed against.
 * @param {number | null} params.typeCurveScenarioId - The selected scenario, or null.
 * @return {object} The React Query result; `data` is the `TypeCurveSummaryResponse`.
 */
export function useTypeCurveSummary({
  packageId,
  typeCurveScenarioId,
}: {
  packageId: number;
  typeCurveScenarioId: number | null;
}): UseQueryResult<TypeCurveSummaryResponse, Error> {
  // INS-10669 — the TC Manager's choices change the very figures this ledger quotes, so it
  // has to re-ask on Done exactly as the grid and the catalogs do. Read here rather than
  // threaded as a prop, matching `useFilterOptions` above, so the section's caller is
  // unchanged and cannot pass a different set than the table used.
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const pendingTcWire = useAtomValue(pendingTypeCurveWireAtom);
  // Calc mode (a saved eval) read from the page route, not a prop — the section is far from
  // the router and every sibling surface already keys on it. While the working TC config
  // matches the eval's stored one, the ledger describes the eval that WAS calculated,
  // exactly as master_data and the executive summary do; when it DIVERGES, every surface
  // shifts to the same hybrid (blob + replaced TC pairs) together (INS-11129 Phase 2) —
  // the ledger must never be the odd panel out in either direction.
  const { calculationId } = useEval3Page();
  return useQuery<TypeCurveSummaryResponse, Error>({
    queryKey: [
      TYPE_CURVE_SUMMARY_QUERY_KEY,
      packageId,
      typeCurveScenarioId,
      calculationId ?? null,
      encodeTypeCurveOverrides(typeCurveOverrides) ?? null,
      encodeInventoryPackageOverrides(inventoryPackageOverrides) ?? null,
    ],
    queryFn: ({ signal }) =>
      api
        .post('queries/packages/type_curve_summary', {
          json: {
            packageId,
            ...calcModeField(calculationId),
            ...scenarioField(typeCurveScenarioId),
            // The BE folds the choices out of the override LOG
            // (`SlotOverrideEngine.type_curve_overrides`), the same channel master_data and
            // the catalogs use — so they ride as `slot_overrides` entries, not a parameter
            // of their own. PENDING entries (deltas + tombstones): the BE folds committed
            // via the calc id, and only a tombstone can delete a committed choice.
            ...(pendingTcWire.length ? { slot_overrides: pendingTcWire } : {}),
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<TypeCurveSummaryResponse>(),
    // Keep the prior figures visible through a refetch so the ledger doesn't flash.
    placeholderData: keepPreviousData,
    staleTime: 300_000, // 5 min — the same posture as the other case-panel reads
  });
}

/** Query-key prefix for the Tracts the package holds in part. */
export const PARTIAL_TRACTS_QUERY_KEY = 'eval3-new:partial-tracts' as const;

/** Endpoint path for the Tracts the package holds in part (no leading slash — `api` sets `prefixUrl`). */
export const PARTIAL_TRACTS_URL = 'queries/packages/partial_tracts';

/**
 * Co-locates the partial-tracts key and fetcher: the package's own when no evaluation is open,
 * the evaluation's when one is.
 * @param {number} packageId - The open package id (from the eval3 page context).
 * @param {number | null} calculationId - The open evaluation, or null.
 * @return The queryOptions for the Tracts the package holds in part.
 */
const partialTractsQueryOptions = (
  packageId: number,
  calculationId: number | null
) =>
  queryOptions({
    queryKey: [PARTIAL_TRACTS_QUERY_KEY, packageId, calculationId],
    queryFn: ({ signal }) =>
      api
        .post(PARTIAL_TRACTS_URL, {
          json: { packageId, ...calcModeField(calculationId) },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<PartialTractsResponse>(),
    // As long as the tables whose rows carry the same counts (`useMasterData`); a finished run,
    // which rewrites what it recorded, refreshes it with them (PACKAGE_SCOPED_COMPLETION_KEYS).
    staleTime: MASTER_DATA_STALE_TIME_MS,
  });

/** The "(N of M Subtracts)" label of each Tract the package holds in part; see `usePartialTracts`. */
export interface PartialTractLabels {
  /** By the Tract's Salesforce id; null for a Tract held whole (or until the list loads). */
  labelForTract: (tractId: string) => string | null;
  /** By the Tract's name, which is what an Asset value is; null as above. */
  labelForName: (name: string | null | undefined) => string | null;
  /** An Asset value as the tables name it: the name, then its label when it has one; blank stays blank. */
  nameForAsset: (name: string) => string;
}

/**
 * The "(N of M Subtracts)" label of each Tract the package holds in part
 * (`POST /queries/packages/partial_tracts`), for the views that name a Tract by name or id
 * alone: the Value Rollup and Visual Summary by Asset, the custom charts, Cashflows by Month
 * and Interest Modeling. The Tract tables carry the counts on their own rows. Counted as
 * those rows count them — as the evaluation recorded them when the page shows one, the
 * package as it stands otherwise — and worded by `subtractCoverageLabel` alone. Until the
 * list loads, and for a Tract held whole, every lookup is null, so a view just shows the name.
 *
 * @param {object} [options] - Options.
 * @param {boolean} [options.ofPackage] - The package as it stands even while an evaluation is
 *   open: for what describes the next run (the Calculate messages, Interest Modeling), not the
 *   one on screen.
 * @return {PartialTractLabels} Label lookups by Tract id and by Tract name.
 */
export function usePartialTracts({
  ofPackage = false,
}: { ofPackage?: boolean } = {}): PartialTractLabels {
  const { packageId, calculationId } = useEval3Page();
  const { data } = useQuery(
    partialTractsQueryOptions(packageId, ofPackage ? null : calculationId)
  );
  return useMemo(() => {
    const byId = new Map<string, string>();
    const byName = new Map<string, string>();
    data?.partialTracts.forEach((tract) => {
      const label = subtractCoverageLabel(
        tract.includedSubtractCount,
        tract.subtractCount
      );
      if (label === null) {
        return;
      }
      byId.set(tract.tractId, label);
      if (tract.name) {
        byName.set(tract.name, label);
      }
    });
    /**
     * @param {string | null | undefined} name - An Asset value: a Tract's name, or blank.
     * @return {string | null} Its label, or null.
     */
    const labelForName = (name: string | null | undefined) =>
      name ? (byName.get(name) ?? null) : null;
    return {
      labelForTract: (tractId) => byId.get(tractId) ?? null,
      labelForName,
      nameForAsset: (name) =>
        name ? withSubtractCoverage(name, labelForName(name)) : name,
    };
  }, [data]);
}

// ─── Pin-to-highlight: expanded-row child checkboxes ───
// A geometry subtable (Asset Slot-Allocations / Slot Tract-Allocations) calls this
// to turn its multi-select into the pinned row's child exclusions. The checkboxes
// are a reactive VIEW of `locatedRowAtom`, and a checked box means "this child is
// shown on the map": a NON-pinned row drives nothing, so its boxes are all
// UNCHECKED; the pinned row shows every child checked except its `excludedChildIds`.
// Unpinning (or pinning a different row) therefore resets this row's boxes to
// unchecked. Toggling dispatches `setExclusions`, which auto-pins the row (capturing
// its geometry from the parent row's decorations) — so pin (bulk) and checkbox
// (local) compose over one shared selection.

/**
 * Controlled multi-select for an expanded-row geometry subtable: maps the row's
 * checkbox state to the pinned row's `excludedChildIds`. The `selection` seed is
 * empty when this row isn't pinned (it drives nothing, so nothing is checked) and,
 * when it is the pinned row, all children checked minus its exclusions.
 * `onSelectionChange` writes the new exclusions back, auto-pinning the parent row
 * when it isn't already pinned. Scoped to this row via `selectAtom` so toggling one
 * subtable doesn't re-render the others (mirrors `LocateCell`).
 *
 * `parentRow` is the parent table row; its `id` + geometry decorations
 * (`mapElements` / `childHighlights` / `bbox`) ride the row index signature, so it's
 * typed as `Record<string, unknown>` and the decorations are read by cast.
 *
 * @param {object} parentRow - The parent table row (carries id + the geometry decorations).
 * @param {string[]} childIds - The subtable's child ids (each a `getRowId`); the all-checked set.
 * @return {object} The `selection` seed and the `onSelectionChange` handler for `<DataTable>`.
 */
export function useChildHighlightSelection(
  parentRow: Record<string, unknown>,
  childIds: string[]
): {
  selection: DataTableSelectionState;
  onSelectionChange: (state: TableState) => void;
} {
  const rowId = String(parentRow.id ?? '');
  const mapElements = parentRow.mapElements as MapElements | undefined;
  const childHighlights = parentRow.childHighlights as
    ChildHighlight[] | undefined;
  const bbox = parentRow.bbox as BBox | undefined;
  const dispatch = useSetAtom(locatedRowAtom);

  // This row's exclusions when it's the pinned row, else null (not pinned).
  // Compared by content so an equal-but-new array doesn't re-render.
  const excludedAtom = useMemo(
    () =>
      selectAtom(
        locatedRowAtom,
        (rows) =>
          rows.find((row) => row.id === rowId)?.excludedChildIds ?? null,
        (a, b) => {
          if (a === b) {
            return true;
          }
          if (!a || !b) {
            return a === b;
          }
          return a.length === b.length && a.every((id, i) => id === b[i]);
        }
      ),
    [rowId]
  );
  const excluded = useAtomValue(excludedAtom);

  // Not pinned (`excluded === null`) → this row drives nothing → no boxes checked.
  // Pinned → all children checked except its exclusions (`excluded` is `[]` when
  // pinned with none excluded, which is distinct from the not-pinned `null`).
  const selection = useMemo<DataTableSelectionState>(() => {
    if (excluded === null) {
      return { selectAll: false, toggledNodes: [] };
    }
    const excludedSet = new Set(excluded);
    return {
      selectAll: false,
      toggledNodes: childIds.filter((id) => !excludedSet.has(id)),
    };
  }, [childIds, excluded]);

  const onSelectionChange = useCallback(
    (state: TableState) => {
      const checked = new Set(state.selection.toggledNodes);
      const excludedChildIds = childIds.filter((id) => !checked.has(id));
      dispatch({
        type: 'setExclusions',
        // Build the pinned row from the parent's decorations so a toggle on an
        // unpinned row auto-pins it. `mapElements` falls back to empty (a parent row
        // always carries it; the fallback only guards the index-signature read).
        row: {
          id: rowId,
          mapElements: mapElements ?? { uwis: [], slotIds: [], names: [] },
          childHighlights: childHighlights ?? [],
          excludedChildIds,
          bbox,
        },
        excludedChildIds,
      });
    },
    [childIds, dispatch, rowId, mapElements, childHighlights, bbox]
  );

  return { selection, onSelectionChange };
}

/**
 * Pin-aware checkbox controller for an inert (map-inert) expanded-row subtable — the
 * subtract subtables, which carry checkboxes for visual parity but drive no map geometry
 * (subtract names match no map feature). The parent's pin state is the authoritative
 * source: checked while pinned, cleared on unpin. Between pin changes the user may still
 * toggle the boxes freely (they scope nothing) and a manual tick STICKS — a pin/unpin is
 * the reset event that re-seeds them. This preserves the pre-controlled behavior while
 * satisfying the DataTable's controlled-selection contract (a controlled click with no
 * `onSelectionChange` would otherwise snap straight back).
 *
 * Single source of truth: ONE local `Set`, re-seeded on a pin (or child-set) change — NOT
 * a continuous merge of two states. Scoped to the parent row via `selectAtom`.
 *
 * @param {string} parentRowId - The parent table row id.
 * @param {string[]} childIds - The subtable's child ids (the checked set while pinned).
 * @return {object} The controlled `selection` value + its `onSelectionChange` handler.
 */
export function useInertChildSelection(
  parentRowId: string,
  childIds: string[]
): {
  selection: DataTableSelectionState;
  onSelectionChange: (state: TableState) => void;
} {
  const isPinnedAtom = useMemo(
    () =>
      selectAtom(locatedRowAtom, (rows) =>
        rows.some((row) => row.id === parentRowId)
      ),
    [parentRowId]
  );
  const isPinned = useAtomValue(isPinnedAtom);
  // A content signature of the child set so a pin/child-set change re-seeds (matching the
  // old seed's `[isPinned, childIds]` deps) without keying on the array identity.
  const childSignature = JSON.stringify([...childIds].sort());

  // Local selection is the source of truth BETWEEN pin events; lazily seeded from the
  // current pin so a subtable mounted while already pinned starts checked.
  const [local, setLocal] = useState<Set<string>>(
    () => new Set(isPinned ? childIds : [])
  );
  // Reset-on-event (React's "adjust state during render" pattern; state-compared, so it's
  // StrictMode-safe): a pin flip OR a child-set change re-seeds `local` from the pin.
  const [prev, setPrev] = useState({ isPinned, childSignature });
  if (prev.isPinned !== isPinned || prev.childSignature !== childSignature) {
    setPrev({ isPinned, childSignature });
    setLocal(new Set(isPinned ? childIds : []));
  }

  const selection = useMemo<DataTableSelectionState>(
    () => ({ selectAll: false, toggledNodes: [...local] }),
    [local]
  );
  const onSelectionChange = useCallback(
    (state: TableState) => setLocal(new Set(state.selection.toggledNodes)),
    []
  );
  return { selection, onSelectionChange };
}

// ─── Working-config form (TanStack Form) ───

/**
 * TanStack Form store for the full Calculate payload (`WorkingConfig`) — the four
 * Slot-view per-cell override maps PLUS the Eval Settings inputs (effective date, DCA
 * scenario, description, notes, `createNewEvalOnCalculate`) and the Case-management
 * fields (case + overrides + price deck, P4). Owned by `Eval3NewContent` and passed by
 * prop to `SlotView` (slot setters) and the Eval Settings form (config setters), so it
 * survives a view unmounting.
 *
 * The form is a typed, dirty-tracked, resettable state container. `form.state.isDirty`
 * gates Calculate; the seeding effect hydrates the editable fields (existing eval →
 * calc detail; from-scratch draft → today + create-new); `buildCalculateRequest`
 * serializes `state.values` into the request.
 *
 * Slot edits come from the modals / inline date cells (`set*Source` / `set*Date`);
 * the Eval Settings inputs come from `setEffectiveDate` / `setDiscountToDate` /
 * `setDcaScenarioId` (and the description / notes / create-new fields bound directly in
 * the form component); the Case-management surfaces come from `setEvaluationCase`
 * (resets overrides + the deck pick + the discount-to date, P4 FR#10) /
 * `setDomainOverride` / `setPriceDeckId`.
 *
 * @param {object} [params] - The seed source: the viewed eval's `calcDetail` + the draft flag
 *   + the org default Scenario a draft pre-selects.
 * @return {object} The form instance and the setter helpers.
 */
/**
 * The interest positions of this session's most recent Calculate POST (202-accepted),
 * i.e. values that ARE persisted server-side. Read by `useUnsavedChanges`: on a
 * fork/draft run, `useCalculate` navigates to the NEW eval in the same tick, so the
 * guard's render-captured committed baseline still belongs to the OLD (or null draft)
 * eval — comparing live values against it would spuriously prompt on Calculate's own
 * navigation. Matching the last-submitted items proves the live values are persisted.
 * Scoped to the SUBMITTING eval view: cleared on eval switch (see the
 * discard-on-eval-switch effect below) so a stale value — `[]` from an unmodeled run
 * is the dangerous one — can never suppress the prompt for a different eval's folded
 * remove-all edit. Module-scoped (not an atom): write and read sites live in this
 * file, and the block callback needs a synchronous read at transition time.
 */
let lastSubmittedInterestItems: InterestItem[] | null = null;

/** Test-only: resets the module-scoped last-submitted capture between tests. */
export function resetLastSubmittedInterestItems(): void {
  lastSubmittedInterestItems = null;
}

export function useWorkingConfigForm({
  calculationId = null,
  calcDetail,
  isDraft = false,
  defaultScenarioId = null,
}: {
  /**
   * The viewed eval's id (route `:evalId`, null in package mode). Changing it
   * hard-resets the form to the target's seed — see the reset effect below.
   */
  calculationId?: number | null;
  /** The viewed eval's calc detail (the owner fetches it via `useCalcDetail`). */
  calcDetail?: CalcDetail;
  /** True for an active from-scratch draft (Model A) — seeds today + create-new. */
  isDraft?: boolean;
  /**
   * The org's default Scenario id (`isDefault` on `GET /scenarios`) — a fresh DRAFT
   * pre-selects it so a brand-new eval is calculatable immediately. Passed in by the
   * owner (which reads `useScenarios`) so this hook stays fetch-free; `null` (no
   * default / list not loaded) leaves the draft's scenario empty → pick required.
   */
  defaultScenarioId?: number | null;
} = {}) {
  // Seed the editable config by DERIVING the form's `defaultValues` from the source
  // (hydrate, not user-driven) — NOT by `form.reset()` in an effect. Why: `reset(values)`
  // mutates the form's internal `options.defaultValues` to the seeded values, but the
  // `useForm` adapter re-applies the STATIC `defaultValues` prop on the next render
  // (`FormApi.update` → re-syncs the store to the prop when it differs), which silently
  // REVERTS the seed back to blanks. Deriving `defaultValues` instead means the prop IS
  // the seed, so `update` syncs the store to it (when it changes) with no fight:
  //  - from-scratch draft → today + create-new forced (immediately calculatable);
  //  - existing eval → from the calc detail's `configurations` (snake_case on the wire —
  //    raw-hash value, not camelized) + description/notes, update-in-place by default;
  //  - config-less detail → fall back to defaults. Defensive only — the 202 cache-seed
  //    echoes the submitted `configurations` (see `useCalculate`), so the calculate
  //    flow never hits this; in-progress edits survive via their touch flags (`update`
  //    won't clobber a touched field). `isDirty` is correct because values ==
  //    defaultValues on seed.
  //
  // INS-10777 — the two override fields seed differently. `slotOverrides` (PENDING ops)
  // always starts empty: the grid receives committed overrides already resolved from the
  // BE, so re-seeding them as pending would double-paint. `committedSlotOverrides` DOES
  // hydrate, in wire shape, purely so Calculate can send the full history back — the BE
  // merges configurations on a re-run, so sending only pending ops would drop everything
  // committed before this page load.
  const seedValues = useMemo<WorkingConfig>(() => {
    if (isDraft) {
      return {
        ...defaultWorkingConfig,
        effectiveDate: moment().format('YYYY-MM-DD'),
        // A late-arriving scenarios list re-syncs a pristine draft via the derived-
        // defaultValues `update` (see the param doc for the why).
        scenarioId: defaultScenarioId,
        createNewEvalOnCalculate: true,
      };
    }
    if (calcDetail?.configurations) {
      return {
        ...defaultWorkingConfig,
        // INS-10777 — the committed log rides along so Calculate resends the full history
        // (see the block comment above). `slotOverrides` stays empty from the defaults.
        // Cast for the same reason `scheduleSlotOverrides` does below: the response type
        // spells overrides in camelCase, but a STORED entry is snake_case. Nothing reads
        // a field here — the array is concatenated onto the request untouched.
        committedSlotOverrides: (calcDetail.configurations.slot_overrides ??
          []) as unknown as Array<Record<string, unknown>>,
        effectiveDate: calcDetail.configurations.effective_date ?? '',
        // Seed from the stored case overrides. Only a REAL user override is stored
        // (the builder sends nothing when the field is blank), so a run the user never
        // set a date on seeds blank again and keeps tracking the effective date —
        // which is what stops the field pinning itself to a materialised snapshot the
        // first time the eval is calculated.
        // snake_case: rails-boost underscores the inbound key, so the stored +
        // served-back override is `discount_to_date` (reading camelCase here silently
        // dropped a user-set value once the run finished — see buildCalculateRequest).
        // `?.` + `??` cover both a run with no `evaluation_case_overrides` key (every
        // pre-INS-11484 eval) and an explicit null, so this is always a string.
        discountToDate:
          calcDetail.configurations.evaluation_case_overrides
            ?.discount_to_date ?? '',
        // The mode seeds from the stored type the same way. Absent = '' = "no
        // override", so every pre-INS-11484 run reopens on Use case default rather
        // than claiming a mode the run never carried.
        discountToDateType:
          calcDetail.configurations.evaluation_case_overrides
            ?.discount_to_date_type ?? '',
        // The org-wide Scenario is echoed as the top-level `scenario` object (NOT in
        // `configurations`), so seed the picker from `scenario.id`.
        scenarioId: calcDetail.scenario?.id ?? null,
        dcaScenarioId: calcDetail.configurations.dca_scenario_id ?? null,
        // INS-10669 — the run's Type Curve Scenario, echoed in the raw snake_case
        // configurations. A pre-integration run carries none, so it seeds null and the
        // panel previews the BE's Legacy default, matching the tables.
        typeCurveScenarioId:
          calcDetail.configurations.type_curve_scenario_id ?? null,
        // Case management (P4 FR#13): the run's case, overrides and own deck pick, echoed in
        // the raw snake_case `configurations`. A pre-P4 run carries none of the keys → the
        // no-case defaults (Calculate stays gated until a pick).
        evaluationCaseId: calcDetail.configurations.evaluation_case_id ?? null,
        // An earlier, area-mode run's per-area overrides no longer apply: seed without them,
        // and say so in Case Management.
        evaluationCaseOverrides: withoutAreaOverrides(
          calcDetail.configurations.evaluation_case_overrides ??
            EMPTY_CASE_OVERRIDES
        ),
        droppedAreaOverrides: hasAreaOverrides(
          calcDetail.configurations.evaluation_case_overrides
        ),
        // A case run's per-run deck is its override. Its configurations carry only a deck the
        // request named, but a rerun from before 49c6057020 kept an earlier pick there while
        // pricing on the case's deck, so the override is the one to read.
        priceDeckId: seedPriceDeckId(calcDetail.configurations),
        // The committed positions are the working copy's baseline; editing them dirties
        // the form like any other field. Deep-camelize the committed blob — it is stored
        // and echoed back snake_case, and seeding it raw would fill the form with
        // undefined fields (blank labels, a stuck editor). See deserializeInterestItems.
        interestItems: deserializeInterestItems(
          calcDetail.configurations.interest_items
        ),
        ...(calcDetail.description !== null && {
          description: calcDetail.description,
        }),
        ...(calcDetail.notes !== null && { notes: calcDetail.notes }),
        createNewEvalOnCalculate: false,
        // INS-10671 used to carry the committed log forward here, as
        // `scheduleSlotOverrides`, for SAVED evals only — a saved recalc FORKS a new
        // eval that inherits nothing, so without it the fork silently dropped prior
        // overrides (AC 56/57). INS-10777's `committedSlotOverrides` (seeded above) now
        // does exactly that, for EVERY eval rather than just saved ones, and
        // `buildCalculateRequest` sends both arrays — so seeding it here too would send
        // the whole log TWICE per run, doubling the stored array on each re-run and
        // re-resolving every filter scope for nothing. `scheduleSlotOverrides` therefore
        // stays empty here and carries only the dialog's own new edits, which is all its
        // name ever claimed.
      };
    }
    return defaultWorkingConfig;
  }, [calcDetail, isDraft, defaultScenarioId]);

  const form = useForm({ defaultValues: seedValues });

  // Discard-on-eval-switch (FR#16): a confirmed dirty switch must ACTUALLY drop the
  // edits — TanStack's `update()` refuses to re-sync a TOUCHED form, so without this
  // the previous eval's dirty values would ride onto the target. `reset()` (no args)
  // clears every touch flag and snaps values to the CURRENT `defaultValues` (the
  // target's derived seed — `update` refreshes `options` every render even when it
  // skips the value sync). Keyed on the eval IDENTITY, not the detail: a same-eval
  // refetch never resets (dirty preservation), and a clean switch / the
  // Calculate-navigate flow (already rebaselined at the 202) reset to the same
  // values — a no-op. Skipped on mount (the ref starts at the current id).
  const prevCalcIdRef = useRef(calculationId);
  useEffect(() => {
    if (prevCalcIdRef.current === calculationId) {
      return;
    }
    prevCalcIdRef.current = calculationId;
    // The last-submitted interest positions belong to the eval view that submitted
    // them; a stale value carried onto another eval could falsely suppress the
    // unsaved-changes prompt (worst case: `[]` from an unmodeled run masks a folded
    // remove-all edit). Calculate's own fork navigation is safe — its guard check
    // runs BEFORE the route change, so this later clear can't affect it.
    lastSubmittedInterestItems = null;
    form.reset(); // no args → snap to the CURRENT defaultValues (the target's seed)
  }, [calculationId, form]);

  // INS-10669 — seed the Type Curve overrides atom from the committed log, ONCE per eval
  // identity. Without this the atom starts empty even when the eval carries committed
  // type_curve entries, so a Done in the TC Manager that RESETS a row to default publishes
  // [] over [] — no query key changes, and master_data / exec summary / the scenario
  // ledger never refetch, even though the working log was correctly rewritten. Seeding
  // makes Done's delta measure against reality: apply repaints, reset repaints, reopen
  // repaints nothing (correctly).
  //
  // Guarded by eval IDENTITY (the same rule as the form reset above), NOT the detail
  // object: the detail query refetches (focus, invalidation) with a fresh object each
  // time, and re-seeding then would silently revert an in-session Done back to the
  // committed fold on screen. The guard waits for the detail to actually arrive (or the
  // draft signal), so the one seed per eval reads the real log, not a loading gap.
  const seedTypeCurveOverridesAtom = useSetAtom(typeCurveOverridesAtom);
  const seedInventoryPackageOverridesAtom = useSetAtom(
    inventoryPackageOverridesAtom
  );
  const seedPendingTypeCurveWire = useSetAtom(pendingTypeCurveWireAtom);
  const resetScheduleRejections = useSetAtom(scheduleOverrideRejectionsAtom);
  const seededOverridesForRef = useRef<unknown>(UNSEEDED);
  // useLayoutEffect, not useEffect: usePopulationCalculationId compares the atom against
  // the calc detail's stored log, and the detail becomes visible to it on the same render
  // this seed is queued for. A post-paint seed painted one frame with atom=[] vs
  // stored=committed — a divergence-banner flash on load that contradicted that hook's
  // own "cannot flash" contract (PR #7365 review). Layout timing runs the seed before
  // the browser paints that frame.
  useLayoutEffect(() => {
    if (!isDraft && !calcDetail) {
      return;
    }
    const identity = isDraft ? DRAFT_SEED_KEY : calculationId;
    if (seededOverridesForRef.current === identity) {
      return;
    }
    seededOverridesForRef.current = identity;
    seedTypeCurveOverridesAtom(
      isDraft
        ? []
        : typeCurveOverridesFromLog(calcDetail?.configurations?.slot_overrides)
    );
    // The spacing half seeds from the same committed log, for the same reason (INS-10669).
    seedInventoryPackageOverridesAtom(
      isDraft
        ? []
        : inventoryPackageOverridesFromLog(
            calcDetail?.configurations?.slot_overrides
          )
    );
    // A fresh eval identity starts with NO pending manager edits — the wire list resets
    // with the folds, from the same trigger, so the atoms cannot describe different states.
    seedPendingTypeCurveWire([]);
    // Per-cell schedule-override rejections belong to the eval that was calculated. Reset them
    // on an eval-identity change (this same once-per-eval guard), so a 422 on one eval can't
    // carry its red "edit rejected" markers onto another eval that never wrote those cells —
    // the reverse of "the grid can never assert an override the run ignored" (INS-12497).
    resetScheduleRejections([]);
  }, [
    calcDetail,
    isDraft,
    calculationId,
    seedTypeCurveOverridesAtom,
    seedInventoryPackageOverridesAtom,
    seedPendingTypeCurveWire,
    resetScheduleRejections,
  ]);

  const setEffectiveDate = useCallback(
    (value: string) => form.setFieldValue('effectiveDate', value),
    [form]
  );

  const setDiscountToDate = useCallback(
    (value: string) => form.setFieldValue('discountToDate', value),
    [form]
  );

  // Switching mode CLEARS the date unless the new mode is 'fixed' — the only one that
  // sends a date. Mirrors the case editor (`case-details.tsx` nulls `discountToDate`
  // on floating) and keeps the form's two discount fields from disagreeing: a stale
  // date left behind under 'floating' would still satisfy the panel's
  // `discountToDateBeforeEffective` gate and block Calculate over a date no longer
  // on the wire.
  const setDiscountToDateType = useCallback(
    (value: '' | 'fixed' | 'floating') => {
      form.setFieldValue('discountToDateType', value);
      if (value !== 'fixed') {
        form.setFieldValue('discountToDate', '');
      }
    },
    [form]
  );

  const setScenarioId = useCallback(
    (value: number | null) => form.setFieldValue('scenarioId', value),
    [form]
  );

  const setDcaScenarioId = useCallback(
    (value: number | null) => form.setFieldValue('dcaScenarioId', value),
    [form]
  );

  // Selecting a case RESETS the per-run overrides, the explicit deck pick and the
  // discount-to date (P4 FR#10 — no per-case override memory; the derived deck
  // display then falls back to the new case's own deck, so no copy effect is needed).
  // The discount-to date resets for the same reason as the deck: it is a per-RUN
  // override that beats the case's own anchor, so carrying one over from case A
  // would silently suppress case B's configured date on the very next run.
  const setEvaluationCase = useCallback(
    (caseId: number) => {
      form.setFieldValue('evaluationCaseId', caseId);
      form.setFieldValue('evaluationCaseOverrides', EMPTY_CASE_OVERRIDES);
      form.setFieldValue('droppedAreaOverrides', false);
      form.setFieldValue('priceDeckId', null);
      // INS-10669 — the new case brings its own Type Curve Scenario, so the previous case's
      // explicit pick must not carry over: clearing it lets the effective value fall back to
      // the new case's scenario. Same reasoning as `priceDeckId` on the line above.
      form.setFieldValue('typeCurveScenarioId', null);
      form.setFieldValue('discountToDate', '');
      form.setFieldValue('discountToDateType', '');
    },
    [form]
  );

  /**
   * This run's scenario for one domain, case-wide. Picking the case's own scenario resets the
   * row.
   *
   * @param {CaseDomain} domain - The shape-carrying domain.
   * @param {number} scenarioId - The scenario.
   * @return {void}
   */
  const setDomainOverride = useCallback(
    (domain: CaseDomain, scenarioId: number) => {
      const prev = form.state.values.evaluationCaseOverrides;
      form.setFieldValue('evaluationCaseOverrides', {
        ...prev,
        [domain]: scenarioId,
      });
    },
    [form]
  );

  // The read surfaces (table / catalogs / summary / map) key on this atom; `setTypeCurveScenario`
  // below is its ONLY writer, so the previewed scenario and the run's cannot diverge.
  const setTypeCurveScenarioId = useSetAtom(typeCurveScenarioIdAtom);
  // Near-same contract as the scenario setter above: `setTypeCurveOverrides` is the only
  // writer of USER intent; the load/eval-switch seed (see the effect above) is the only
  // other write, and it writes the same committed-log fold — so the committed log and the
  // previewed population cannot diverge.
  const publishTypeCurveOverrides = useSetAtom(typeCurveOverridesAtom);
  const publishInventoryPackageOverrides = useSetAtom(
    inventoryPackageOverridesAtom
  );
  const publishPendingTypeCurveWire = useSetAtom(pendingTypeCurveWireAtom);

  const setPriceDeckId = useCallback(
    (value: number | null) => form.setFieldValue('priceDeckId', value),
    [form]
  );

  /**
   * INS-10669 — commits a Type Curve Scenario pick.
   *
   * Writes BOTH the form field (it rides `configurations.type_curve_scenario_id` on Calculate)
   * and `typeCurveScenarioIdAtom` (the read surfaces — table, catalogs, summary, map — key on
   * it). One writer for both, so the run and the preview can never describe different scenarios;
   * the atom is never written anywhere else.
   *
   * Also clears the pending slot overrides: they key on composite place ids
   * (`{unit}_{formation}_{slotNumber}`) belonging to the OLD scenario's places, so after a swap
   * they would either match nothing or, worse, match a different place at the same position. The
   * section confirms with the user before calling this whenever any are pending.
   *
   * @param {number | null} value - The picked scenario, or null to fall back to the BE default.
   * @return {void}
   */
  const setTypeCurveScenario = useCallback(
    (value: number | null) => {
      form.setFieldValue('typeCurveScenarioId', value);
      // TOMBSTONE every COMMITTED manager choice rather than merely clearing state: the
      // committed log survives a swap (history is append-only), so a bare clear left the
      // old scenario's picks everywhere the fold is re-derived — a no-op modal Done
      // republished fold(committed + []) and resurrected them, Calculate re-sent the
      // committed entries, and the BE re-folded them from the calc id on every read
      // (PR #7365 review, both reviewers). With the tombstones pending,
      // fold(committed + pending) = {} on every one of those paths, and the persist-time
      // compaction erases the discarded pairs from the next run's stored config. The
      // fold atoms are published as [] BECAUSE that equals the fold — the atoms==fold
      // invariant holds through the swap. Individual/bulk entries are untouched.
      const committed = form.state.values.committedSlotOverrides;
      const tombstones = [
        ...typeCurveOverridesFromLog(committed).map((override) =>
          toTypeCurveOverrideWire({
            ...override,
            typeCurveHeaderDetailsId: null,
          })
        ),
        ...inventoryPackageOverridesFromLog(committed).map((override) =>
          toInventoryPackageOverrideWire({
            ...override,
            inventoryPackageName: null,
            wellSpacingFt: null,
          })
        ),
      ];
      form.setFieldValue('slotOverrides', tombstones);
      setTypeCurveScenarioId(value);
      publishTypeCurveOverrides([]);
      publishInventoryPackageOverrides([]);
      publishPendingTypeCurveWire(tombstones);
    },
    [
      form,
      setTypeCurveScenarioId,
      publishTypeCurveOverrides,
      publishInventoryPackageOverrides,
      publishPendingTypeCurveWire,
    ]
  );

  /**
   * INS-10669 — appends the TC Manager's DELTAS when the user clicks Done.
   *
   * APPEND-ONLY, like every other override kind: changed picks arrive as normal entries,
   * resets as null-header tombstones, and they join the END of the pending log. The
   * committed history is NEVER touched — the earlier design rewrote it to make reverted
   * rows disappear, which broke the one-semantics rule of the log; the tombstone contract
   * replaces that (the fold deletes the key, Calculate's persist-time compaction drops
   * the pair from the stored config).
   *
   * The individual and bulk entries are untouched and keep their order, so the timeline
   * that decides last-write-wins for field overrides is unaffected.
   *
   * Publishes the reconciled fold of the FULL log (committed + pending) to the atom, so
   * the read surfaces re-key from the same truth Calculate would submit. Nothing outside
   * the modal moves until this runs.
   *
   * @param {TypeCurveOverride[]} deltas - The modal's per-key deltas (entries + tombstones).
   * @return {void}
   */
  const setTypeCurveOverrides = useCallback(
    (
      deltas: TypeCurveOverride[],
      packageDeltas: InventoryPackageOverride[] = []
    ) => {
      const pending = [
        ...form.state.values.slotOverrides,
        ...deltas.map(toTypeCurveOverrideWire),
        ...packageDeltas.map(toInventoryPackageOverrideWire),
      ];
      form.setFieldValue('slotOverrides', pending);
      // Publish to the read surfaces too. `slotOverrides` is deliberately absent from every
      // read query key (an inline edit must not refetch), which is correct for the two
      // value-setting override kinds but leaves a `type_curve` choice — which changes which
      // rows EXIST — invisible to the table, the catalogs, the summary and the map.
      publishTypeCurveOverrides(
        typeCurveOverridesFromLog([
          ...form.state.values.committedSlotOverrides,
          ...pending,
        ])
      );
      // The spacing half publishes the same way, from the same log (INS-10669).
      publishInventoryPackageOverrides(
        inventoryPackageOverridesFromLog([
          ...form.state.values.committedSlotOverrides,
          ...pending,
        ])
      );
      // And the RAW pending manager entries — both kinds, tombstones included — for the
      // read surfaces' request bodies (INS-11129 Phase 2): the BE folds committed + these,
      // so a revert actually deletes the committed choice. The folds above (survivors
      // only) key the caches; this wire list is what travels.
      publishPendingTypeCurveWire(
        pending.filter(
          (
            entry
          ): entry is TypeCurveOverrideWire | InventoryPackageOverrideWire => {
            const type = (entry as { type?: unknown }).type;
            return type === 'type_curve' || type === 'inventory_package';
          }
        )
      );
    },
    [
      form,
      publishTypeCurveOverrides,
      publishInventoryPackageOverrides,
      publishPendingTypeCurveWire,
    ]
  );

  /**
   * INS-10777 — append one override operation to the ordered `slotOverrides` list (the
   * unified channel for all three edit flows: inline / select-all / checkbox). Holds only
   * PENDING (uncommitted) operations; a run rebaselines them to `[]`.
   *
   * @param {SlotOverrideOperation} operation - The operation to append (slot / ids / filter).
   * @return {void}
   */
  const appendSlotOverride = useCallback(
    (operation: SlotOverrideOperation) => {
      form.setFieldValue('slotOverrides', [
        ...form.state.values.slotOverrides,
        operation,
      ]);
    },
    [form]
  );

  /**
   * INS-10671 — replace the "Property development schedule" dialog's contribution to the
   * working config (its per-slot date ops, already wire-shaped). A whole-field REPLACE,
   * so reopening the dialog and editing again never DUPLICATES the prior contribution.
   * The dialog owns this field; the slot-table `appendSlotOverride` never touches it.
   *
   * @param {Array<Record<string, unknown>>} ops - The dialog's wire ops for this session.
   * @return {void}
   */
  const setScheduleSlotOverrides = useCallback(
    (ops: Array<Record<string, unknown>>) => {
      form.setFieldValue('scheduleSlotOverrides', ops);
    },
    [form]
  );

  /**
   * Interest Modeling (INS-10673) — append interest positions to the
   * working list, skipping any tract already modeled (`tractId` is unique per item).
   * A whole-field replace so `isDirty` flips and the seeded baseline is diffable.
   *
   * @param {InterestItem[]} items - The positions to add (deduped against the current list).
   * @return {void}
   */
  const addInterestItems = useCallback(
    (items: InterestItem[]) => {
      const existing = new Set(
        form.state.values.interestItems.map((item) => item.tractId)
      );
      const fresh = items.filter((item) => !existing.has(item.tractId));
      if (fresh.length === 0) {
        return;
      }
      form.setFieldValue('interestItems', [
        ...form.state.values.interestItems,
        ...fresh,
      ]);
    },
    [form]
  );

  /**
   * INS-10673 — remove the interest position for a tract from the working list.
   *
   * @param {string} tractId - The tract whose position to drop.
   * @return {void}
   */
  const removeInterestItem = useCallback(
    (tractId: string) => {
      form.setFieldValue(
        'interestItems',
        form.state.values.interestItems.filter(
          (item) => item.tractId !== tractId
        )
      );
    },
    [form]
  );

  // ─── OGL carry group setters ───
  // All mutate `interestItems[<tract>].groups` immutably via a FUNCTIONAL updater
  // (`(prev) => next`), so two seeds/edits in the same tick can't clobber each
  // other's write. Each dirties the form like any other working-config field.

  /**
   * Immutably remap the `groups` of the carry item for `tractId` (a no-op for a
   * non-carry / absent item), via a functional field updater.
   *
   * @param {string} tractId - The carry item to touch.
   * @param {(groups: CarryGroup[]) => CarryGroup[]} update - Maps the item's current groups to the next.
   * @return {void}
   */
  const mapCarryGroups = useCallback(
    (tractId: string, update: (groups: CarryGroup[]) => CarryGroup[]) => {
      form.setFieldValue('interestItems', (items) =>
        items.map((item) => {
          if (item.tractId === tractId && item.kind === 'carry') {
            return { ...item, groups: update(item.groups ?? []) };
          }
          return item;
        })
      );
    },
    [form]
  );

  /**
   * seed a carry item's initial, EMPTY "Group 1" — the user builds the grouping
   * (INS-10673). The tract's interest cohorts are not materialised as groups: they
   * order, colour and gate the property picker instead, so the user sees where the
   * interest breaks without being handed a shape they did not choose.
   *
   * Every property therefore starts UNASSIGNED, and an unassigned property is valued
   * as a full working interest until it joins a group.
   *
   * Idempotent: a no-op when the item already has ≥ 1 group (so it never
   * overwrites an edited or reopened carry item, and firing twice is harmless).
   *
   * @param {string} tractId - The freshly-added carry item.
   * @return {void}
   */
  const seedCarryGroups = useCallback(
    (tractId: string) => {
      mapCarryGroups(tractId, (groups) => {
        if (groups.length > 0) {
          return groups;
        }
        return [makeCarryGroup('Group 1')];
      });
    },
    [mapCarryGroups]
  );

  /**
   * append a new, EMPTY group (its wells start unassigned). Named
   * "Group N+1" off the current count.
   *
   * @param {string} tractId - The carry item to add a group to.
   * @return {void}
   */
  const addCarryGroup = useCallback(
    (tractId: string) => {
      mapCarryGroups(tractId, (groups) => [
        ...groups,
        makeCarryGroup(`Group ${groups.length + 1}`),
      ]);
    },
    [mapCarryGroups]
  );

  /**
   * rename a group inline.
   *
   * @param {string} tractId - The owning carry item.
   * @param {string} groupId - The group to rename.
   * @param {string} name - The new label.
   * @return {void}
   */
  const renameCarryGroup = useCallback(
    (tractId: string, groupId: string, name: string) => {
      mapCarryGroups(tractId, (groups) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return { ...group, name };
          }
          return group;
        })
      );
    },
    [mapCarryGroups]
  );

  /**
   * remove a group. BLOCKED when only one remains (≥ 1 group invariant); a
   * removed group's wells simply fall out of every group and become unassigned
   * (valued as a full working interest).
   *
   * @param {string} tractId - The owning carry item.
   * @param {string} groupId - The group to remove.
   * @return {void}
   */
  const removeCarryGroup = useCallback(
    (tractId: string, groupId: string) => {
      mapCarryGroups(tractId, (groups) => {
        if (groups.length <= 1) {
          return groups;
        }
        return groups.filter((group) => group.id !== groupId);
      });
    },
    [mapCarryGroups]
  );

  /**
   * set a group's well membership wholesale (from the multi-select). A well
   * belongs to at most one group, so wells granted here are pulled out of every
   * SIBLING group.
   *
   * @param {string} tractId - The owning carry item.
   * @param {string} groupId - The group whose membership to replace.
   * @param {string[]} wellIds - The group's new well set.
   * @return {void}
   */
  const setCarryGroupWells = useCallback(
    (tractId: string, groupId: string, wellIds: string[]) => {
      const chosen = new Set(wellIds);
      mapCarryGroups(tractId, (groups) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return { ...group, wellIds };
          }
          return {
            ...group,
            wellIds: group.wellIds.filter((id) => !chosen.has(id)),
          };
        })
      );
    },
    [mapCarryGroups]
  );

  /**
   * patch a group's carry terms (BPO/APO WI/NRI, trigger, threshold).
   * Sparse: only the given fields change. Direction is DERIVED from the WI split,
   * never set here.
   *
   * @param {string} tractId - The owning carry item.
   * @param {string} groupId - The group to patch.
   * @param {Partial<Pick<CarryGroup, 'bpoWi' | 'bpoNri' | 'apoWi' | 'apoNri' | 'trigger' | 'threshold'>>} patch - The term fields to change.
   * @return {void}
   */
  const patchCarryGroupTerms = useCallback(
    (
      tractId: string,
      groupId: string,
      patch: Partial<
        Pick<
          CarryGroup,
          | 'bpoWi'
          | 'bpoNri'
          | 'apoWi'
          | 'apoNri'
          | 'trigger'
          | 'threshold'
          | 'payoutBasis'
        >
      >
    ) => {
      mapCarryGroups(tractId, (groups) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return { ...group, ...patch };
          }
          return group;
        })
      );
    },
    [mapCarryGroups]
  );

  // ─── UMI election setters ───
  // Same immutable functional-updater pattern as the carry setters: each remaps the
  // election item for `tractId` (a no-op for a non-election / absent item) and dirties
  // the form. An election's PROPERTIES and participating interest sit on its groups
  // (INS-10673) — the lease/royalty/trigger terms and the acreage-driven bonus stay on
  // the item, because they are negotiated once for the tract, not once per group.

  /**
   * Immutably remap the election item for `tractId` (a no-op for a non-election / absent
   * item), via a functional field updater.
   *
   * @param {string} tractId - The election item to touch.
   * @param {(item: InterestItem) => InterestItem} update - Maps the current item to the next.
   * @return {void}
   */
  const mapElectionItem = useCallback(
    (tractId: string, update: (item: InterestItem) => InterestItem) => {
      form.setFieldValue('interestItems', (items) =>
        items.map((item) => {
          if (item.tractId === tractId && item.kind === 'election') {
            return update(item);
          }
          return item;
        })
      );
    },
    [form]
  );

  /**
   * Seed a freshly-added election with the default terms, ALL the tract's
   * valued wells (the frame is scoped by `wellIds`), and the acreage-seeded lease bonus.
   * Idempotent: a no-op once the item is seeded (`wellIds` set), so it never overwrites
   * a reopened/edited election and a double-fire is harmless.
   *
   * @param {string} tractId - The freshly-added election item.
   * @param {CarryWell[]} wells - Every valued well on the tract, carrying our interest in each.
   * @param {number | null} [netAcres] - The tract's net acres, to seed the $/NMA bonus (null ⇒ manual total).
   * @return {void}
   */
  const seedElection = useCallback(
    (tractId: string, wells: CarryWell[], netAcres?: number | null) => {
      mapElectionItem(tractId, (item) => {
        if (item.wellIds !== undefined) {
          return item;
        }
        return { ...item, ...makeElectionSeed(wells, netAcres) };
      });
    },
    [mapElectionItem]
  );

  /**
   * Patch an election's terms (participate WI/NRI, carried/lease royalty,
   * trigger, threshold, lease bonus). Sparse: only the given fields change.
   *
   * @param {string} tractId - The election item to patch.
   * @param {Partial<Pick<InterestItem, 'ownerWi' | 'ownerNri' | 'carriedRoyaltyNri' | 'trigger' | 'threshold' | 'payoutBasis' | 'leaseRoyaltyNri' | 'leaseBonus'>>} patch - The term fields to change.
   * @return {void}
   */
  const patchElectionTerms = useCallback(
    (
      tractId: string,
      patch: Partial<
        Pick<
          InterestItem,
          | 'ownerWi'
          | 'ownerNri'
          | 'carriedRoyaltyNri'
          | 'trigger'
          | 'threshold'
          | 'payoutBasis'
          | 'leaseRoyaltyNri'
          | 'leaseBonus'
        >
      >
    ) => {
      mapElectionItem(tractId, (item) => ({ ...item, ...patch }));
    },
    [mapElectionItem]
  );

  /**
   * Immutably remap an election item's groups.
   *
   * @param {string} tractId - The election item to touch.
   * @param {(groups: ElectionGroup[]) => ElectionGroup[]} update - Maps the current groups to the next.
   * @return {void}
   */
  const mapElectionGroups = useCallback(
    (tractId: string, update: (groups: ElectionGroup[]) => ElectionGroup[]) => {
      mapElectionItem(tractId, (item) => {
        const groups = update(electionGroupsOf(item));
        const next = {
          ...item,
          electionGroups: groups,
          // `wellIds` stays the union of the groups — it is what scopes the frame the
          // preview prices, so a property leaving every group must leave the scope too.
          wellIds: groups.flatMap((group) => group.wellIds),
        };
        // Finish migrating an item saved before grouping: its participating interest
        // and elected outcome have just been read into the implicit group, so the
        // item-level copies must GO. Leaving them would state the same rate twice —
        // which the calculator rejects outright (an election sends one shape or the
        // other, never both) and would otherwise be two sources of truth.
        delete next.ownerWi;
        delete next.ownerNri;
        delete next.electedElection;
        return next;
      });
    },
    [mapElectionItem]
  );

  /**
   * append a new, EMPTY election group. Named "Group N+1" off the current count.
   *
   * @param {string} tractId - The election item to add a group to.
   * @return {void}
   */
  const addElectionGroup = useCallback(
    (tractId: string) => {
      mapElectionGroups(tractId, (groups) => [
        ...groups,
        makeElectionGroup(`Group ${groups.length + 1}`),
      ]);
    },
    [mapElectionGroups]
  );

  /**
   * rename an election group inline.
   *
   * @param {string} tractId - The owning election item.
   * @param {string} groupId - The group to rename.
   * @param {string} name - The new label.
   * @return {void}
   */
  const renameElectionGroup = useCallback(
    (tractId: string, groupId: string, name: string) => {
      mapElectionGroups(tractId, (groups) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return { ...group, name };
          }
          return group;
        })
      );
    },
    [mapElectionGroups]
  );

  /**
   * remove an election group. BLOCKED when only one remains (≥ 1 group invariant);
   * its properties fall out of the item's scope entirely — an unassigned property is
   * not part of the election.
   *
   * @param {string} tractId - The owning election item.
   * @param {string} groupId - The group to remove.
   * @return {void}
   */
  const removeElectionGroup = useCallback(
    (tractId: string, groupId: string) => {
      mapElectionGroups(tractId, (groups) => {
        if (groups.length <= 1) {
          return groups;
        }
        return groups.filter((group) => group.id !== groupId);
      });
    },
    [mapElectionGroups]
  );

  /**
   * set an election group's property membership wholesale (from the multi-select). A
   * property belongs to at most one group, so properties granted here are pulled out
   * of every SIBLING group.
   *
   * @param {string} tractId - The owning election item.
   * @param {string} groupId - The group whose membership to replace.
   * @param {string[]} wellIds - The group's new property set.
   * @return {void}
   */
  const setElectionGroupWells = useCallback(
    (tractId: string, groupId: string, wellIds: string[]) => {
      const chosen = new Set(wellIds);
      mapElectionGroups(tractId, (groups) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return { ...group, wellIds };
          }
          return {
            ...group,
            wellIds: group.wellIds.filter((id: string) => !chosen.has(id)),
          };
        })
      );
    },
    [mapElectionGroups]
  );

  /**
   * patch an election group's participating terms. Sparse: only the given fields change.
   *
   * @param {string} tractId - The owning election item.
   * @param {string} groupId - The group to patch.
   * @param {Partial<Pick<ElectionGroup, 'bpoWi' | 'bpoNri' | 'ownerWi' | 'ownerNri'>>} patch - The term fields to change.
   * @return {void}
   */
  const patchElectionGroupTerms = useCallback(
    (
      tractId: string,
      groupId: string,
      patch: Partial<
        Pick<ElectionGroup, 'bpoWi' | 'bpoNri' | 'ownerWi' | 'ownerNri'>
      >
    ) => {
      mapElectionGroups(tractId, (groups) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return { ...group, ...patch };
          }
          return group;
        })
      );
    },
    [mapElectionGroups]
  );

  /**
   * Mark (or clear, with `null`) the election that feeds the evaluation for ONE
   * property group — called by the surface's "Use in Evaluation" toggle. Each group
   * elects its own outcome (a tract can carry one group and lease another), so the
   * choice lives on the group; clearing removes the key so it round-trips absent.
   *
   * @param {string} tractId - The election item.
   * @param {string} groupId - The group electing.
   * @param {ElectionVariant | null} election - The elected variant, or null to clear.
   * @return {void}
   */
  const setElectionElected = useCallback(
    (tractId: string, groupId: string, election: ElectionVariant | null) => {
      mapElectionGroups(tractId, (groups) =>
        groups.map((group) => {
          if (group.id !== groupId) {
            return group;
          }
          if (election === null) {
            // Drop the key entirely (not `electedElection: undefined`): the committed
            // echo omits an unelected key, and the divergence gates compare with
            // lodash `isEqual`, which distinguishes an own-`undefined` key from an
            // absent one — an explicit `undefined` would read permanently diverged
            // after the completion refetch drops the key.
            const cleared = { ...group };
            delete cleared.electedElection;
            return cleared;
          }
          return { ...group, electedElection: election };
        })
      );
    },
    [mapElectionGroups]
  );

  return useMemo(
    () => ({
      form,
      setEffectiveDate,
      setDiscountToDate,
      setDiscountToDateType,
      setScenarioId,
      setDcaScenarioId,
      setEvaluationCase,
      setDomainOverride,
      setPriceDeckId,
      setTypeCurveScenario,
      setTypeCurveOverrides,
      appendSlotOverride,
      setScheduleSlotOverrides,
      addInterestItems,
      removeInterestItem,
      seedCarryGroups,
      addCarryGroup,
      renameCarryGroup,
      removeCarryGroup,
      setCarryGroupWells,
      patchCarryGroupTerms,
      seedElection,
      patchElectionTerms,
      addElectionGroup,
      renameElectionGroup,
      removeElectionGroup,
      setElectionGroupWells,
      patchElectionGroupTerms,
      setElectionElected,
    }),
    [
      form,
      setEffectiveDate,
      setDiscountToDate,
      setDiscountToDateType,
      setScenarioId,
      setDcaScenarioId,
      setEvaluationCase,
      setDomainOverride,
      setPriceDeckId,
      setTypeCurveScenario,
      setTypeCurveOverrides,
      appendSlotOverride,
      setScheduleSlotOverrides,
      addInterestItems,
      removeInterestItem,
      seedCarryGroups,
      addCarryGroup,
      renameCarryGroup,
      removeCarryGroup,
      setCarryGroupWells,
      patchCarryGroupTerms,
      seedElection,
      patchElectionTerms,
      addElectionGroup,
      renameElectionGroup,
      removeElectionGroup,
      setElectionGroupWells,
      patchElectionGroupTerms,
      setElectionElected,
    ]
  );
}

/** The working-config form handle, owned by `Eval3NewContent` and passed to consumers. */
export type WorkingConfigForm = ReturnType<typeof useWorkingConfigForm>;

// ─── Slot per-cell edit: modal candidate-data fetches (React Query) ───

/** Query-key prefix for the per-slot available-forecasts fetch. */
export const AVAILABLE_FORECASTS_QUERY_KEY = 'eval3-new:available-forecasts';
/** Query-key prefix for the per-slot available-AFEs fetch. */
export const AVAILABLE_AFES_QUERY_KEY = 'eval3-new:available-afes';

/**
 * Fetches the Edit Volume Source modal's candidate forecasts for a slot. With the page's
 * package and scenario, the backend lists the scenario's curves for the row's own Type Curve
 * Area (or else its unit and formation) and marks the row's resolved volume source "Current".
 *
 * @param {string} slotId - The slot whose forecasts to load.
 * @param {RowVolumeSourcePair} pair - The row's narrowing fields, from `pairFromRow`.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true; also gated on slotId).
 * @param {boolean} [options.includeArchived] - Also list archived type curves (the picker's
 *   Archived toggle).
 * @return {object} The React Query result for the available forecasts.
 */
export function useAvailableForecasts(
  slotId: string | null,
  pair: RowVolumeSourcePair | null,
  options?: { enabled?: boolean; packageId?: number; includeArchived?: boolean }
): UseQueryResult<AvailableForecastsResponse, Error> {
  const packageId = usePackageId(options?.packageId);
  const includeArchived = options?.includeArchived ?? false;
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const unitId = pair?.unitId ?? null;
  const formation = pair?.formation ?? null;
  const typeCurveAreaName = pair?.typeCurveAreaName ?? null;
  const resolvedVolumeSourceId = pair?.resolvedVolumeSourceId ?? null;
  return useQuery<AvailableForecastsResponse, Error>({
    // Scenario + pair are part of the key: switching scenario must re-ask rather than
    // serve the previous scenario's candidate list from cache. The area is keyed too —
    // a manager override can move a row to a different area without the pair changing,
    // and the cached list would otherwise be the old area's.
    queryKey: [
      AVAILABLE_FORECASTS_QUERY_KEY,
      slotId,
      packageId,
      typeCurveScenarioId,
      unitId,
      formation,
      typeCurveAreaName,
      resolvedVolumeSourceId,
      includeArchived,
    ],
    queryFn: ({ signal }) =>
      api
        // Only runs when `enabled`, which requires the slot.
        .get(`slots/${encodeURIComponent(slotId ?? '')}/available_forecasts`, {
          searchParams: {
            packageId,
            ...(typeCurveScenarioId !== null && { typeCurveScenarioId }),
            ...(unitId !== null && { unitId }),
            ...(formation !== null && { formation }),
            ...(typeCurveAreaName !== null && { typeCurveAreaName }),
            ...(resolvedVolumeSourceId !== null && {
              currentVolumeSourceId: resolvedVolumeSourceId,
            }),
            // Asked for only while the picker's Archived toggle is on.
            ...(includeArchived && { includeArchived }),
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<AvailableForecastsResponse>(),
    enabled:
      (options?.enabled ?? true) &&
      Boolean(slotId) &&
      pair !== null &&
      packageId !== undefined,
    staleTime: 300_000,
  });
}

/**
 * The package a forecast list is scoped to: an explicit one wins, otherwise the page's.
 * With neither (a surface rendered outside the page provider, such as the tearsheet without
 * its own) there is no package to scope to, and the caller does not fetch.
 *
 * @param {number} [explicit] - The caller's own package.
 * @return {number | undefined} The package, or undefined when there is none.
 */
function usePackageId(explicit?: number): number | undefined {
  const page = useOptionalEval3Page();
  return explicit ?? page?.packageId;
}

/** Query-key prefix for the slot-or-area forecasts lookup (INS-11129). */
export const AVAILABLE_FORECASTS_LOOKUP_QUERY_KEY =
  'eval3-new:available-forecasts-lookup';

/**
 * How the Bulk Edit volume-source picker narrows the forecast list: by a slot, or by a
 * Type Curve Area. Modelled as a union so the endpoint's "exactly one of" contract is
 * enforced at the type level — passing both is a 422.
 */
export type ForecastLookup =
  | { slotId: string; typeCurveAreaName?: never }
  | { typeCurveAreaName: string; slotId?: never };

/**
 * Fetches the forecasts to choose from, narrowed EITHER by slot or by Type Curve Area
 * (INS-11129). One hook because one endpoint answers both in one shape — the picker's
 * third dropdown renders the result without caring which way the user narrowed down.
 *
 * Distinct from `useAvailableForecasts` above, which serves the inline per-slot volume
 * editor off the REST route and also sends the row's pair and resolved volume source.
 *
 * @param {ForecastLookup | null} lookup - The chosen slot or area; null disables the query.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true; also gated on lookup).
 * @param {boolean} [options.includeArchived] - Also list archived type curves (the picker's
 *   Archived toggle).
 * @return {object} The React Query result for the matching forecasts.
 */
export function useAvailableForecastsLookup(
  lookup: ForecastLookup | null,
  options?: { enabled?: boolean; packageId?: number; includeArchived?: boolean }
): UseQueryResult<AvailableForecastsResponse, Error> {
  // Same scenario sourcing as the inline picker — Bulk Edit must not offer a
  // curve the inline editor would refuse to, whichever way the user narrowed down.
  const packageId = usePackageId(options?.packageId);
  const includeArchived = options?.includeArchived ?? false;
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  return useQuery<AvailableForecastsResponse, Error>({
    // Both discriminants in the key so switching filter type cannot serve a stale list.
    queryKey: [
      AVAILABLE_FORECASTS_LOOKUP_QUERY_KEY,
      lookup?.slotId ?? null,
      lookup?.typeCurveAreaName ?? null,
      packageId,
      typeCurveScenarioId,
      includeArchived,
    ],
    queryFn: ({ signal }) =>
      api
        .post('queries/available_forecasts', {
          json: {
            ...lookup,
            packageId,
            ...(typeCurveScenarioId !== null && { typeCurveScenarioId }),
            // Asked for only while the picker's Archived toggle is on.
            ...(includeArchived && { includeArchived }),
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<AvailableForecastsResponse>(),
    enabled:
      (options?.enabled ?? true) && Boolean(lookup) && packageId !== undefined,
    staleTime: 300_000,
  });
}

/** What the volume picker's second dropdown needs from either search: its data, minus the
 * keystroke callback the caller wires up itself. */
export type FilterValueSearchResult = Omit<FilterValueSearch, 'onSearchChange'>;

/**
 * Result cap for dropdown 2. Deliberately a cap and NOT a page: past this many matches
 * the answer is a better search, not more scrolling — so there is no "load more" and the
 * user refines instead. Matches `Query::Wells::Autocomplete::LIMIT`, so both branches of
 * the dropdown show the same number of options.
 */
export const FILTER_VALUE_LIMIT = 25;

/** Below this the wells endpoint returns nothing, so don't spend a request finding out. */
export const WELL_SEARCH_MIN_CHARS = 2;

/**
 * Type Curve Areas for the volume-source picker's second dropdown (INS-11129), searched
 * SERVER-side.
 *
 * Not package-scoped: a forecast may be sourced from any area. Previously fetched
 * unpaginated (~1,700 areas, 578 KB) and filtered in the browser; now `text` goes to the
 * endpoint, which has always supported it. The first page loads with no text, so the list
 * is browsable on focus — unlike the wells search, which shows nothing until the user
 * types.
 *
 * @param {string} text - The search text (empty lists the first `FILTER_VALUE_LIMIT`).
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true; gate on the dropdown).
 * @return {FilterValueSearchResult} Options and status.
 */
export function useTypeCurveAreaSearch(
  text: string,
  options?: { enabled?: boolean }
): FilterValueSearchResult {
  const query = useQuery<{ results: Array<{ name: string }> }, Error>({
    queryKey: ['eval3-new:tc-area-search', text],
    queryFn: ({ signal }) =>
      api
        .post('queries/type_curve_areas', {
          json: { text, page: 1, pageSize: FILTER_VALUE_LIMIT },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<{ results: Array<{ name: string }> }>(),
    enabled: options?.enabled ?? true,
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  });

  return useFilterValueResult(query, (data) =>
    data.results
      .map((area) => area.name)
      .filter(Boolean)
      .map((name) => ({ value: name, label: name }))
  );
}

/**
 * Well search for the volume-source picker's second dropdown (INS-11129) — EVERY well in
 * the system, not just the package's slots.
 *
 * The picker exists to reach any forecast, and `Query::AvailableForecasts` resolves a UWI
 * by spatial intersection against the well's Type Curve Area rather than through the
 * package, so an out-of-package well returns its curves natively.
 *
 * Hits `POST /queries/wells/autocomplete` exactly as the shared `WellsAutocomplete`
 * component does — `text` only, 2-char minimum, 400 ms debounce, server-side filtering.
 * That endpoint lists only wells with an ACTIVE UNIT ASSIGNMENT (its ES query requires
 * `current_unit_name`), so an unassigned well will not appear here. Reaching that well's
 * curves means going through the Type Curve Area branch instead. Widening the endpoint
 * was considered and rejected: it is shared with the WBA Add Wellbore modal, and the
 * area branch already covers the gap.
 *
 * @param {string} text - The search text; under `WELL_SEARCH_MIN_CHARS` no request is made.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true; gate on the dropdown).
 * @return {FilterValueSearchResult} Options and status.
 */
export function useWellSearch(
  text: string,
  options?: { enabled?: boolean }
): FilterValueSearchResult {
  const trimmed = text.trim();

  const query = useQuery<
    { results: Array<{ uwi: string; name?: string }> },
    Error
  >({
    queryKey: ['eval3-new:well-search', trimmed],
    queryFn: ({ signal }) =>
      api
        .post('queries/wells/autocomplete', {
          // `text` only — the endpoint permits nothing else, and its own LIMIT is the
          // cap. Same call the shared `WellsAutocomplete` component makes.
          json: { text: trimmed },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<{ results: Array<{ uwi: string; name?: string }> }>(),
    enabled:
      (options?.enabled ?? true) && trimmed.length >= WELL_SEARCH_MIN_CHARS,
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  });

  return useFilterValueResult(query, (data) =>
    data.results
      .filter((well) => Boolean(well.uwi))
      .map((well) => ({
        value: well.uwi,
        // The UWI is what the override keys on, so it stays visible even when a
        // friendlier lease name exists.
        label: well.name ? `${well.uwi} · ${well.name}` : well.uwi,
      }))
  );
}

/**
 * Shared projection for the two dropdown-2 searches: maps the response to options and
 * derives the picker's four-way status.
 *
 * @param {object} query - The React Query result.
 * @param {Function} toOptions - Maps the response to dropdown options.
 * @return {FilterValueSearchResult} The picker-facing result.
 */
function useFilterValueResult<TData>(
  query: UseQueryResult<TData, Error>,
  toOptions: (data: TData) => FilterValueOption[]
): FilterValueSearchResult {
  const { data, error, isLoading } = query;

  return useMemo<FilterValueSearchResult>(() => {
    const items = data ? toOptions(data) : [];
    let status: FilterValueSearchResult['status'];
    if (error) {
      status = 'error';
    } else if (isLoading) {
      status = 'loading';
    } else if (items.length === 0) {
      status = 'empty';
    } else {
      status = 'ready';
    }
    return { status, items, errorMessage: error?.message };
    // `toOptions` is a literal at each call site — excluded deliberately, as including it
    // would rebuild the list every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);
}

/** The row an inline picker edits: its slot, plus the pair its own forecast list keys on. */
export interface VolumeSourceOwnSlot {
  slotId: string;
  pair: RowVolumeSourcePair;
  /**
   * The list holding the row's source, which decides where the picker opens: a decline on
   * the row's own slot, a type curve on the row's own Type Curve Area.
   */
  opensOn: VolumeForecastType;
}

/** Where the picker is narrowed to: dropdown 1 and dropdown 2. */
interface PickerNarrowing {
  filterBy: VolumeFilterBy | null;
  filterValue: string | null;
}

/**
 * The narrowing an inline picker opens on, and returns to: the row's own slot for a decline,
 * its own Type Curve Area for a type curve. Bulk Edit has no row and opens un-narrowed.
 *
 * @param {VolumeSourceOwnSlot} [ownSlot] - The row an inline picker edits.
 * @return {PickerNarrowing} The opening narrowing.
 */
const ownNarrowing = (ownSlot?: VolumeSourceOwnSlot): PickerNarrowing => {
  if (!ownSlot) return { filterBy: null, filterValue: null };
  return ownSlot.opensOn === 'pdp_decline'
    ? { filterBy: 'slot_id', filterValue: ownSlot.slotId }
    : {
        filterBy: 'type_curve_area',
        filterValue: ownSlot.pair.typeCurveAreaName,
      };
};

/**
 * The volume-source picker's narrowing state and the lists loaded for it — shared by Bulk
 * Edit and the inline Edit Volume Source modal (INS-13809), so both narrow the same way.
 *
 * With `ownSlot` the picker opens on the row's own view (see `ownNarrowing`). While it stays
 * there the forecasts come from the row's own `GET /slots/:id/available_forecasts`, which
 * sends the row's pair and resolved source — so the list, and which option is "Current",
 * match what the row offered before. Any other choice goes through the slot-or-area lookup.
 *
 * @param {object} options - Hook options.
 * @param {boolean} options.enabled - Whether the searches and lookups may fetch.
 * @param {VolumeSourceOwnSlot} [options.ownSlot] - The row an inline picker edits.
 * @param {number} [options.packageId] - Overrides the page's package (tearsheet).
 * @return {object} `picker`, the picker's data prop; `reset`, which restores the initial
 *   narrowing; and `onOwnView`, whether the picker is on the row's own view.
 */
export function useVolumeSourcePickerData({
  enabled,
  ownSlot,
  packageId,
}: {
  enabled: boolean;
  ownSlot?: VolumeSourceOwnSlot;
  packageId?: number;
}): {
  picker: VolumeSourcePickerData;
  reset: () => void;
  onOwnView: boolean;
} {
  const ownSlotId = ownSlot?.slotId ?? null;
  const own = ownNarrowing(ownSlot);
  const [filterBy, setFilterBy] = useState<VolumeFilterBy | null>(own.filterBy);
  const [filterValue, setFilterValue] = useState<string | null>(
    own.filterValue
  );
  // The Archived toggle keys the fetch: archived curves are asked for only while it is on.
  // Off again whenever the narrowing changes or resets, as a fresh list starts without them.
  const [showArchived, setShowArchived] = useState(false);

  // Dropdown 2 is searched SERVER-side on both branches, so the text lives here (it keys
  // the fetch) and is debounced out of the picker's keystroke handler. Both searches are
  // system-wide on purpose: the picker exists to reach ANY forecast, so neither the
  // package nor an active page filter may hide the source the user wants to copy.
  const [search, setSearch] = useState('');
  const debouncedSetSearch = useMemo(() => debounce(setSearch, 400), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  // Only the chosen branch fetches. Both cap at FILTER_VALUE_LIMIT with no "load more":
  // areas list on focus, wells stay idle until WELL_SEARCH_MIN_CHARS.
  const isAreaBranch = filterBy === 'type_curve_area';
  const areaSearch = useTypeCurveAreaSearch(search, {
    enabled: enabled && isAreaBranch,
  });
  const wellSearch = useWellSearch(search, {
    enabled: enabled && filterBy === 'slot_id',
  });
  const activeSearch = isAreaBranch ? areaSearch : wellSearch;

  // Switching how we narrow invalidates what was narrowed to — a UWI is not an area name —
  // so drop the stale value AND the stale query rather than let either key a nonsense
  // lookup against the other endpoint.
  const handleFilterByChange = useCallback(
    (next: VolumeFilterBy | null) => {
      setFilterBy(next);
      setFilterValue(null);
      setShowArchived(false);
      setSearch('');
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch]
  );

  const reset = useCallback(() => {
    setFilterBy(own.filterBy);
    setFilterValue(own.filterValue);
    setShowArchived(false);
    setSearch('');
    debouncedSetSearch.cancel();
  }, [own.filterBy, own.filterValue, debouncedSetSearch]);

  // A row with no area of its own opens on an empty area box; nothing is its own list then.
  const onOwnView =
    own.filterValue !== null &&
    filterBy === own.filterBy &&
    filterValue === own.filterValue;

  const ownForecasts = useAvailableForecasts(ownSlotId, ownSlot?.pair ?? null, {
    enabled: enabled && onOwnView,
    packageId,
    includeArchived: showArchived,
  });

  // `null` until both narrowing choices are made, which disables the query.
  const lookup = useMemo<ForecastLookup | null>(() => {
    if (onOwnView || !filterValue) return null;
    return filterBy === 'type_curve_area'
      ? { typeCurveAreaName: filterValue }
      : { slotId: filterValue };
  }, [onOwnView, filterBy, filterValue]);
  const lookupForecasts = useAvailableForecastsLookup(lookup, {
    enabled,
    packageId,
    includeArchived: showArchived,
  });

  const forecastsQuery = onOwnView ? ownForecasts : lookupForecasts;
  const forecastsData = forecastsQuery.data;
  const forecasts = useMemo(
    () =>
      forecastsData
        ? {
            typeCurves: forecastsData.typeCurves,
            pdpDeclines: forecastsData.pdpDeclines,
          }
        : null,
    [forecastsData]
  );

  const picker = useMemo<VolumeSourcePickerData>(
    () => ({
      filterBy,
      onFilterByChange: handleFilterByChange,
      filterValue,
      onFilterValueChange: setFilterValue,
      filterValues: {
        items: activeSearch.items,
        status: activeSearch.status,
        errorMessage: activeSearch.errorMessage,
        onSearchChange: debouncedSetSearch,
      },
      // Areas list on focus; wells cannot return anything below the endpoint's
      // two-character floor.
      minSearchChars: isAreaBranch ? 0 : WELL_SEARCH_MIN_CHARS,
      forecasts,
      forecastsLoading: forecastsQuery.isPending,
      forecastsError: forecastsQuery.error?.message,
      showArchived,
      onShowArchivedChange: setShowArchived,
    }),
    [
      filterBy,
      handleFilterByChange,
      filterValue,
      activeSearch,
      debouncedSetSearch,
      isAreaBranch,
      forecasts,
      forecastsQuery.isPending,
      forecastsQuery.error,
      showArchived,
    ]
  );

  return { picker, reset, onOwnView };
}

/**
 * Fetches the available AFEs for a slot (the Edit AFE Source modal's candidate
 * list). Replaces the legacy Jotai `availableAfesAtom` fetch for this feature;
 * enable it while the modal is open.
 *
 * @param {string} slotId - The slot whose AFEs to load.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true; also gated on slotId).
 * @return {object} The React Query result for the available AFEs.
 */
export function useAvailableAfes(
  slotId: string,
  options?: { enabled?: boolean }
): UseQueryResult<AvailableAfesResponse, Error> {
  return useQuery<AvailableAfesResponse, Error>({
    queryKey: [AVAILABLE_AFES_QUERY_KEY, slotId],
    queryFn: ({ signal }) =>
      api
        .get(`slots/${encodeURIComponent(slotId)}/available_afes`, {
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<AvailableAfesResponse>(),
    enabled: (options?.enabled ?? true) && Boolean(slotId),
    staleTime: 300_000,
  });
}

/**
 * Loads the AFE options valid for ALL of the given slots — the Edit Bulk Slots
 * panel's candidate list — by fetching each slot's available AFEs and
 * intersecting by AFE id. The React Query twin of the legacy Jotai
 * `useBulkEditAfeOptions`; fan-out is one `useQueries` over the deduped slot ids,
 * reusing the per-slot `[AVAILABLE_AFES_QUERY_KEY, slotId]` cache key so the
 * single-cell AFE modal and bulk edit share one cache. Enable it while the panel
 * is open.
 *
 * @param {string[]} slotIds - The slots whose available AFEs to intersect.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true); set to the panel's open state.
 * @return {BulkEditAfeOptions} The aggregate status + intersected AFE list.
 */
export function useBulkAvailableAfes(
  slotIds: string[],
  options?: { enabled?: boolean }
): BulkEditAfeOptions {
  const enabled = options?.enabled ?? true;

  // Stable, deduped, sorted id set so the query list (and the memo below) only
  // change when the actual selection changes.
  const ids = useMemo(
    () => [...new Set(slotIds.filter(Boolean))].sort(),
    [slotIds]
  );

  const results = useQueries({
    queries: ids.map((slotId) => ({
      queryKey: [AVAILABLE_AFES_QUERY_KEY, slotId],
      queryFn: ({ signal }) =>
        api
          .get(`slots/${encodeURIComponent(slotId)}/available_afes`, {
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<AvailableAfesResponse>(),
      enabled: enabled && Boolean(slotId),
      staleTime: 300_000,
    })),
  });

  // `useQueries` returns a fresh array identity every render, so memoize on a
  // stable signature of each query's status + last-updated stamps instead. This
  // keeps the returned `BulkEditAfeOptions` referentially stable across renders
  // when nothing actually changed (avoids thrashing the panel's downstream memos).
  const signature = results
    .map(
      (result) =>
        `${result.status}:${result.dataUpdatedAt}:${result.errorUpdatedAt}`
    )
    .join('|');

  return useMemo<BulkEditAfeOptions>(() => {
    if (ids.length === 0) {
      return { status: 'empty', items: [] };
    }
    // `isLoading` (not `isPending`) so a disabled query — the panel is
    // closed — doesn't read as perpetually loading.
    if (results.some((result) => result.isLoading)) {
      return { status: 'loading', items: [] };
    }
    const errored = results.find((result) => result.error);
    if (errored?.error) {
      return {
        status: 'error',
        items: [],
        errorMessage:
          errored.error instanceof Error
            ? errored.error.message
            : 'Failed to load available AFEs',
      };
    }
    const lists: AvailableAfe[][] = results.map(
      (result) => result.data?.availableAfes ?? []
    );
    const [first, ...rest] = lists;
    const items =
      first?.filter((afe) =>
        rest.every((list) => list.some((other) => other.id === afe.id))
      ) ?? [];
    return { status: items.length ? 'ready' : 'empty', items };
    // `results` is intentionally read through `signature` (stable) rather than
    // listed directly — its identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, signature]);
}

/** Query-key prefix for the AFE-catalog search (INS-10777 P3, select-all AFE picker). */
export const AFE_CATALOG_QUERY_KEY = 'eval3-new:afe-catalog';

/** Page size for the AFE-catalog infinite-scroll picker. */
const AFE_CATALOG_PAGE_SIZE = 25;

/** The `POST /queries/afe_catalog` response envelope (only the fields the picker needs). */
interface AfeCatalogSearchResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: AfeCatalogResult[];
}

/** The AFE-catalog picker's aggregate state (fed to the shared `SRPComboBox`). */
export interface AfeCatalogSearchResult {
  status: BulkEditAfeOptions['status'];
  items: AvailableAfe[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  fetchNextPage: () => void;
  errorMessage?: string;
}

/**
 * Maps an `afe_catalog` search row to the `AvailableAfe` shape the AFE picker + override
 * plumbing already use. `AfeWell` has no distinct well id, so `slotId` stands in
 * (display-only). Coerces the catalog's numeric AFE `id` to a string.
 *
 * @param {AfeCatalogResult} r - One catalog result row.
 * @return {AvailableAfe} The picker/override item shape.
 */
export function afeCatalogResultToAvailableAfe(
  r: AfeCatalogResult
): AvailableAfe {
  return {
    id: String(r.id),
    afeType: r.afeType,
    well: { id: r.well.slotId, name: r.well.name, href: r.well.href },
    drillingCost: r.drillingCost ?? undefined,
    completionCost: r.completionCost ?? undefined,
    facilitiesCost: r.facilitiesCost ?? undefined,
    contingenciesCost: r.contingenciesCost ?? undefined,
    otherCost: r.otherCost ?? undefined,
    totalCost: r.totalCost ?? undefined,
  };
}

/**
 * The select-all AFE picker's data source (INS-10777 P3): a server-searched, paginated
 * `POST /queries/afe_catalog` via `useInfiniteQuery`. The caller (slot-view) owns the
 * debounced `text` and gates `enabled` on the panel being open in select-all mode. Returns
 * the flattened + mapped `AvailableAfe[]` plus the infinite-scroll controls the shared
 * `SRPComboBox` consumes.
 *
 * Note: this query sets no `placeholderData`, so a new `text` is a fresh query
 * (`data` clears). `isFetching` (vs `isFetchingNextPage`) lets the picker show a spinner
 * during a search without disabling the input.
 *
 * @param {string} text - The debounced search text (empty = browse all).
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true); set to the picker's open state.
 * @return {AfeCatalogSearchResult} The picker's status + items + infinite-scroll controls.
 */
export function useAfeCatalogSearch(
  text: string,
  options?: { enabled?: boolean }
): AfeCatalogSearchResult {
  const enabled = options?.enabled ?? true;

  const query = useInfiniteQuery({
    queryKey: [AFE_CATALOG_QUERY_KEY, text],
    queryFn: ({ pageParam, signal }) =>
      api
        .post('queries/afe_catalog', {
          json: { text, page: pageParam, pageSize: AFE_CATALOG_PAGE_SIZE },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<AfeCatalogSearchResponse>(),
    initialPageParam: 1,
    getNextPageParam: (last) => {
      const loaded = last.page * last.pageSize;
      return loaded < last.totalCount ? last.page + 1 : undefined;
    },
    enabled,
    staleTime: 300_000,
  });

  const {
    data,
    error,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    fetchNextPage,
  } = query;

  return useMemo<AfeCatalogSearchResult>(() => {
    const items = (data?.pages ?? []).flatMap((page) =>
      page.results.map(afeCatalogResultToAvailableAfe)
    );
    let status: BulkEditAfeOptions['status'];
    if (error) {
      status = 'error';
    } else if (isLoading) {
      status = 'loading';
    } else if (items.length === 0) {
      status = 'empty';
    } else {
      status = 'ready';
    }
    return {
      status,
      items,
      hasNextPage: Boolean(hasNextPage),
      isFetchingNextPage,
      isFetching,
      // React Query keeps `fetchNextPage`'s identity stable across renders, so returning it
      // directly (rather than wrapping it in a fresh closure each memo) keeps the consumer's
      // `onLoadMore` stable — the picker's capturing scroll listener then attaches once
      // instead of re-subscribing on every page load.
      fetchNextPage,
      errorMessage: error?.message,
    };
  }, [
    data,
    error,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    fetchNextPage,
  ]);
}

// ─── P3 · Workspace & Eval Navigation ───

/** Query-key prefix for the package's evaluation-workspaces list (enables scoped invalidation). */
export const EVAL_WORKSPACES_QUERY_KEY = 'eval3-new:workspaces' as const;

/**
 * Fetches all evaluation workspaces for the package
 * (`GET /packages/:packageId/evaluation_workspaces`) — the tree's folder list. No
 * `view_mode`: P3 fetches the full list once and filters My/All client-side (the
 * `createdBy`-only Mine filter needs no calcs). The list arrives ordered by
 * `last_activity_at DESC`, so `workspaces[0]` is the most-recently-active. Keyed on
 * `packageId`; `staleTime` 60s (create/rename invalidate it explicitly).
 *
 * @param {number} packageId - The package whose workspaces to load.
 * @return {object} The React Query result for the workspace list.
 */
export function useEvaluationWorkspaces(
  packageId: number
): UseQueryResult<EvaluationWorkspacesResponse, Error> {
  return useQuery<EvaluationWorkspacesResponse, Error>({
    queryKey: [EVAL_WORKSPACES_QUERY_KEY, packageId],
    queryFn: ({ signal }) =>
      api
        .get(`packages/${packageId}/evaluation_workspaces`, {
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<EvaluationWorkspacesResponse>(),
    staleTime: 60_000,
  });
}

/** The current user as the `createdBy` Mine filter + the rename author surface need it. */
export interface CurrentUser {
  id: number;
  name: string;
}

/**
 * The current user, read from the root session resource. The id is `session.id` —
 * the `profile` object carries only `{ name }`, so there is NO `profile.id`; the Mine
 * scope filter matches on `createdBy.id === id` (more robust than the legacy name
 * match). Returns `undefined` until the session resource resolves.
 *
 * @return {CurrentUser | undefined} The current user's id + name, or undefined when there's no session.
 */
export function useCurrentUser(): CurrentUser | undefined {
  const session = useAtomValue(sessionResourceAtom);
  return useMemo<CurrentUser | undefined>(() => {
    if (!session) {
      return undefined;
    }
    return { id: session.id, name: session.profile?.name ?? '' };
  }, [session]);
}

/** Query-key prefix for the single-calc detail fetch (deep-link owner + run lifecycle). */
export const CALC_DETAIL_QUERY_KEY = 'eval3-new:calc-detail' as const;

/**
 * Reads a calc's detail (`GET /evaluations/calculations/:id`) — the eval's owning
 * workspace + name (P3's deep-link owner resolution) PLUS the P5 run-lifecycle +
 * form-seed fields, all already emitted by the serializer (no BE read-side change):
 *   - `status` — the CAPITALIZED display value (`status_display` →
 *     `'Processing'` / `'Completed'` / `'Error'`); compare against those literals.
 *   - `notifications` — present IFF `Processing` (the Pusher channel + events);
 *     the run-lifecycle single source of truth `useEvalRunSync` subscribes off.
 *   - `error` — the BE failure message when `status:error`.
 *   - `results` — the `eval_result` / `cashflows_by_month` hrefs (emitted whenever
 *     `calculation_id` is set, INCLUDING while `Processing` — so NOT a "completed"
 *     signal; gate `useEvalResult` on `status === 'Completed'`, not on this).
 *   - `description` / `notes` — the eval's name + notes (both already on the shared
 *     `_calculation` partial); seed the Eval Settings form.
 *   - `configurations.{ effective_date, dca_scenario_id }` — seed the form's effective
 *     date + DCA scenario. SNAKE_CASE on the wire: `configurations` is emitted as a raw
 *     hash value, so its keys are NOT camelized (unlike the top-level jbuilder keys).
 *
 * Used by P3 to expand the owner folder on a deep link (calcs are lazy, so the owner
 * can't be found by scanning loaded lists) and by P5's `useEvalRunSync` /
 * `PackageOverviewSection` / `useWorkingConfigForm`. Enabled only when `:evalId` is
 * present. A missing calc 404s (the BE `load_calculation` → `.find` →
 * `RecordNotFound`) → `isError`, which the tree treats as "no valid eval" (the
 * dead-link redirect).
 *
 * NOTE on casing: the jbuilder *writes* snake_case (`json.workspace_id`), but the app's
 * global `Jbuilder.key_format(camelize: :lower)` camelCases every response on the wire —
 * so the FE receives `workspaceId` (like every other endpoint), NOT `workspace_id`.
 *
 * @param {number | null} calculationId - The route `:evalId`, or null in package mode.
 * @return {object} The React Query result; `data` is the extended `CalcDetail`.
 */
export function useCalcDetail(
  calculationId: number | null
): UseQueryResult<CalcDetail, Error> {
  return useQuery<CalcDetail, Error>({
    queryKey: [CALC_DETAIL_QUERY_KEY, calculationId],
    queryFn: ({ signal }) =>
      api
        .get(`evaluations/calculations/${calculationId}`, {
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<CalcDetail>(),
    enabled: calculationId !== null,
    // Never auto-refetch on react-query's own terms: every calc change flows through an
    // explicit invalidation (`useEvalRunSync`'s completion handler and its recovery re-read,
    // `useRenameEval`) or the `useCalculate` 202 seed. `refetchOnWindowFocus` is off app-wide
    // and `staleTime: Infinity` keeps it that way, so an idle page issues no requests.
    //
    // P5 originally paired that with "a missed Pusher completion event recovers only on
    // reload". ONCALL-937 revisited that deferral — a client that misses the event is not an
    // edge case (it is one dropped socket or one publish-before-subscribe race, and the run
    // completes in seconds) and the page it stranded had no way back. `useEvalRunSync` now
    // owns a recovery re-read that invalidates this key while — and only while — the viewed
    // run is Processing. The trade-off this comment described no longer applies; the
    // no-auto-refetch property it protected still does.
    staleTime: Infinity,
  });
}

/**
 * Where the viewed calc is in its lifecycle:
 * - `packageMode` — no eval open; package-scoped reads are unrestricted.
 * - `resolving`   — the calc detail itself is still loading (we don't yet know).
 * - `processing`  — the BE is running the calculation.
 * - `completed`   — settled with results; calc-mode reads may fetch.
 * - `error`       — settled WITHOUT results (a failed run, or a dead-link detail
 *   read): neither fetchable nor a wait — the state that makes the two derived
 *   signals below non-complementary.
 */
export type CalcRunState =
  'packageMode' | 'resolving' | 'processing' | 'completed' | 'error';

/**
 * The viewed calc's lifecycle state plus BOTH signals the data surfaces derive from
 * it — one hook, so the correlation is structural (the truth table lives in one
 * return statement):
 *
 *  - `state` — see `CalcRunState`.
 *  - `canFetch` — the P5 gate: data depending on `:evalId` exists only for COMPLETED
 *    calcs (true in `packageMode` | `completed`). While false, the calc-scoped reads
 *    (`master_data` / `filter_options` / `executive_summary` / `map_elements` / the
 *    slot tiles) stay DISABLED — `keepPreviousData` freezes the prior view, and the
 *    completion invalidation (`useEvalRunSync`) plus this flipping true refetch them
 *    with real results.
 *  - `isRunning` — the shared calc half of the page's loading umbrella (true in
 *    `resolving` | `processing`). Each surface ORs it with its OWN source's fetch
 *    flag (`master_data` for the tables, `executive_summary` for the summary,
 *    `eval_result` for the Package Overview), so a BE calculation in flight presents
 *    exactly like a fetch in flight.
 *
 * Reads the shared `useCalcDetail` cache entry (no extra request).
 *
 * @param {number | null | undefined} calculationId - The eval-calc id, or null/undefined (package mode).
 * @return {object} `{ state, canFetch, isRunning }` for the viewed calc.
 */
export function useCalcLifecycle(calculationId: number | null | undefined): {
  state: CalcRunState;
  canFetch: boolean;
  isRunning: boolean;
} {
  // `??` coalesces only null/undefined — a falsy-but-real id can never read as
  // package mode.
  const id = calculationId ?? null;
  const { data, isError } = useCalcDetail(id);

  let state: CalcRunState = 'resolving';
  if (id === null) {
    state = 'packageMode';
  } else if (data?.status === 'Completed') {
    state = 'completed';
  } else if (data?.status === 'Error' || isError) {
    // A failed detail read (e.g. a dead-link 404) is settled-without-results, NOT a
    // wait — otherwise a dead eval would read `resolving` (loading) forever.
    state = 'error';
  } else if (data?.status === 'Processing') {
    state = 'processing';
  }

  return {
    state,
    canFetch: state === 'packageMode' || state === 'completed',
    isRunning: state === 'resolving' || state === 'processing',
  };
}

/** Query-key prefix for a workspace's calculations (evals) list. */
export const WORKSPACE_CALCS_QUERY_KEY = 'eval3-new:workspace-calcs' as const;

/** One workspace's calc-load status, for the tree's inline loading / error+Retry state. */
export interface WorkspaceCalcsStatus {
  status: 'loading' | 'error' | 'ready';
  onRetry: () => void;
}

/** What `useWorkspaceCalculations` returns — the loaded calcs, per-workspace status, and an aggregate loading flag. */
export interface WorkspaceCalculationsResult {
  /**
   * Workspace id → its calcs. A key is ABSENT while that workspace is still loading
   * and present as `[]` when it loaded empty — the orchestration relies on this
   * distinction (wait for the key vs. "no evals → stay in package mode").
   */
  byWorkspace: Record<number, WorkspaceCalculation[]>;
  /** Per-workspace status + retry (the tree renders inline loading / error states from this). */
  statusByWorkspace: Record<number, WorkspaceCalcsStatus>;
  /** True while any of the passed workspaces is still loading its first page of calcs. */
  isLoading: boolean;
}

/**
 * Lazily loads the calculations (evals) for the given workspaces — one
 * `GET /evaluation_workspaces/:id/calculations` fetch per id, via a `useQueries`
 * fan-out (the same shape as `useColorElements` / `useBulkAvailableAfes`). Pass only
 * the EXPANDED workspace ids — the lazy invariant is *needs calcs ⟺ expanded*, so
 * collapsed folders never fetch; each query is keyed per workspace, so
 * collapse→re-expand is a cache hit.
 *
 * Returns a `byWorkspace` map (a key absent while loading, `[]` when loaded-empty),
 * a per-workspace status (for the tree's inline loading / error+Retry), and
 * `isLoading`. The stitched result is memoized on a status+timestamp signature (the
 * `useBulkAvailableAfes` pattern) so its identity stays stable between real changes —
 * keeping the tree + downstream memos from churning.
 *
 * @param {number[]} workspaceIds - The expanded workspaces whose calcs to load.
 * @return {WorkspaceCalculationsResult} byWorkspace + per-workspace status + isLoading.
 */
export function useWorkspaceCalculations(
  workspaceIds: number[]
): WorkspaceCalculationsResult {
  // Stable, deduped, sorted id set so the query list + the memo below only change
  // when the expanded set actually changes.
  const ids = useMemo(
    () => [...new Set(workspaceIds)].sort((a, b) => a - b),
    [workspaceIds]
  );

  const results = useQueries({
    queries: ids.map((workspaceId) => ({
      queryKey: [WORKSPACE_CALCS_QUERY_KEY, workspaceId],
      queryFn: ({ signal }) =>
        api
          .get(`evaluation_workspaces/${workspaceId}/calculations`, {
            // Load the whole list (the tree needs every leaf); the endpoint paginates
            // but a workspace's eval count is small.
            searchParams: { limit: '10000', offset: '0' },
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<WorkspaceCalculationsResponse>(),
      staleTime: 60_000,
    })),
  });

  // `useQueries` returns a fresh array identity every render, so memoize the stitched
  // result on a stable signature of each query's status + last-updated stamps.
  const signature = results
    .map(
      (result) =>
        `${result.status}:${result.dataUpdatedAt}:${result.errorUpdatedAt}`
    )
    .join('|');

  return useMemo<WorkspaceCalculationsResult>(() => {
    const byWorkspace: Record<number, WorkspaceCalculation[]> = {};
    const statusByWorkspace: Record<number, WorkspaceCalcsStatus> = {};
    ids.forEach((workspaceId, index) => {
      const result = results[index];
      // Only record the calcs once loaded — leave the key absent while loading so
      // callers can tell "still loading" from "loaded, no evals" (`[]`).
      if (result.data) {
        byWorkspace[workspaceId] = result.data.calculations;
      }
      statusByWorkspace[workspaceId] = {
        status: result.isError ? 'error' : result.data ? 'ready' : 'loading',
        onRetry: () => {
          void result.refetch();
        },
      };
    });
    return {
      byWorkspace,
      statusByWorkspace,
      // These queries are always enabled (every passed id is an expanded folder), so
      // `isPending` === `isLoading` here — no disabled-query "perpetually
      // loading" trap (unlike `useBulkAvailableAfes`, which gates on `enabled`).
      isLoading: results.some((result) => result.isPending),
    };
    // `results` is read through `signature` (stable) rather than listed directly —
    // its identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, signature]);
}

/**
 * Maps a loaded calc to its tree-leaf / management-table row (name = the BE
 * `description`; `createdBy` flattened from `runByUser.name`).
 *
 * @param {WorkspaceCalculation} calc - One loaded workspace calculation (eval).
 * @return {WorkspaceEvalRow} The tree-leaf / management-table row shape.
 */
function toWorkspaceEvalRow(calc: WorkspaceCalculation): WorkspaceEvalRow {
  return {
    id: calc.id,
    name: calc.description ?? '',
    createdBy: calc.runByUser?.name ?? '',
    createdAt: calc.createdAt,
  };
}

/** A workspace folder plus its loaded eval leaves (empty until the folder is expanded). */
export type WorkspaceWithEvals = EvaluationWorkspace & {
  evals: WorkspaceEvalRow[];
};

/** Everything the EvalWorkspace tree needs — scope-filtered folders, package-wide totals, expansion + selection. */
export interface EvalsTree {
  /** Scope-filtered workspaces, each with its loaded eval leaves (the tree rows). */
  workspaces: WorkspaceWithEvals[];
  /** TOTAL workspace count (unfiltered) — the "N Workspaces" subtitle. */
  workspaceCount: number;
  /** Σ calculationCount over ALL workspaces (unfiltered) — the "M Evaluations" subtitle. */
  evalCount: number;
  /** The workspace LIST load state (drives the tree-region spinner / error). */
  isLoading: boolean;
  error: Error | null;
  /** Refetch the workspace list — the tree-region error Retry. */
  refetch: () => void;
  /** Expansion state (owned here so the orchestration effects can drive it); the TreeView reads + toggles it. */
  expandedWorkspaceIds: Set<number>;
  toggleExpand: (workspaceId: number) => void;
  /** Per-expanded-workspace calc-load status, for the tree's inline "Loading evals…" / error+Retry. */
  calcStatusByWorkspace: Record<number, WorkspaceCalcsStatus>;
  /** Select an eval leaf → route to it under its workspace (`workspaces/:ws/eval/:id`). */
  selectEval: (workspaceId: number, calculationId: number) => void;
  /** Select a workspace folder → route to its management/browse view. */
  selectWorkspace: (workspaceId: number) => void;
  /** Select the pinned Saved Evaluations folder → route to its package-wide browse view. */
  selectSavedEvals: () => void;
  /** Start a from-scratch draft under `workspaceId` and show it (Model A, `eval/new`). */
  startDraftEval: (workspaceId: number) => void;
  /** Select the provisional draft node → route to that workspace's `eval/new`. */
  selectDraft: (workspaceId: number) => void;
}

/**
 * Returns a navigate-to-eval handler. Selecting an eval routes to it UNDER its
 * workspace (`workspaces/:workspaceId/eval/:evalId`) — which re-keys every
 * `calculationId`-scoped surface and, being a route change, flows through the
 * `useUnsavedChanges` guard (a dirty form prompts before leaving). Re-selecting the
 * already-active eval is a no-op. The caller passes the eval's owning workspace (the
 * tree leaf's folder, the WorkspacePage's scope); if it's ever wrong, the server
 * (`Packages::Eval3Controller`) canonicalizes it to the eval's real workspace. Shared by
 * the tree leaf click and the WorkspacePage row click, so eval selection has ONE
 * definition.
 *
 * @return {Function} `selectEval(workspaceId, calculationId)` — navigate to that eval.
 */
export function useSelectEval(): (
  workspaceId: number,
  calculationId: number
) => void {
  const { packageId, calculationId } = useEval3Page();
  const history = useHistory();
  return useCallback(
    (targetWorkspaceId: number, calcId: number) => {
      if (calcId === calculationId) {
        return;
      }
      history.push(eval3EvalPath(packageId, targetWorkspaceId, calcId));
    },
    [calculationId, packageId, history]
  );
}

/**
 * The EvalWorkspace tree controller. Composes the workspace list, the lazy per-folder
 * calc loader, the current user, and the route identity into the tree's view model:
 *   - the SCOPE-FILTERED folder list (My = `createdBy.id === me.id`, All = unfiltered),
 *     each carrying its loaded eval leaves;
 *   - the package-wide subtitle TOTALS, computed from the UNFILTERED list so the scope
 *     dropdown never changes them;
 *   - expansion state, owned here so the orchestration effects can expand on
 *     deep-link / fresh-load, and which drives the lazy calc fetch (needs calcs ⟺
 *     expanded);
 *   - the selection handlers — an eval leaf navigates the route (the URL is the source
 *     of truth for the active eval), a folder sets the management-view atom;
 *   - the route-orchestration effects (E0–E3): reset the management selection on a
 *     route change, expand the deep-link owner, and auto-select the latest eval on a
 *     fresh load. They only expand / navigate — the resulting state changes drive the
 *     fetches.
 *
 * @return {EvalsTree} The tree view model + expansion + selection handlers.
 */
export function useEvalsTree(): EvalsTree {
  const { packageId, calculationId, workspaceId } = useEval3Page();
  const history = useHistory();
  const me = useCurrentUser();
  const scope = useAtomValue(scopeViewAtom);
  // The viewed eval's detail — used only to decide the deep-link expansion below (a saved eval's
  // leaf lives in the Saved Evaluations folder, not its workspace). Shares the query key with the
  // other `useCalcDetail` consumers, so it's a cache hit, not an extra fetch.
  const { data: calcDetail } = useCalcDetail(calculationId);

  const {
    data,
    isPending,
    error,
    refetch: refetchWorkspaces,
  } = useEvaluationWorkspaces(packageId);
  const allWorkspaces = data?.workspaces;

  const refetch = useCallback(() => {
    void refetchWorkspaces();
  }, [refetchWorkspaces]);

  // Subtitle totals — always from the UNFILTERED list (the scope dropdown must not
  // change them).
  const workspaceCount = allWorkspaces?.length ?? 0;
  // Σ of each workspace's UNSAVED eval count. `calculationCount` includes saved calcs, but saved
  // evals move to the Saved Evaluations folder (P8 decision 4), so they're not counted under a
  // workspace — subtract `savedCount` to keep the subtitle consistent with the shown leaves.
  const evalCount = useMemo(
    () =>
      (allWorkspaces ?? []).reduce(
        (sum, ws) => sum + (ws.calculationCount - ws.savedCount),
        0
      ),
    [allWorkspaces]
  );

  // Mine = strictly workspaces I created (`createdBy.id`); All = unfiltered. Reads only
  // the workspace row, so it needs no calcs loaded (keeps calcs lazy). Until `me`
  // resolves, Mine matches nothing rather than leaking the All list.
  const scopedWorkspaces = useMemo(() => {
    const list = allWorkspaces ?? [];
    return scope === 'mine'
      ? list.filter((ws) => ws.createdBy?.id === me?.id)
      : list;
  }, [allWorkspaces, scope, me]);

  // Expansion is owned here (a Set in state). The orchestration effects (later task)
  // call `expand`; the TreeView calls `toggleExpand`. Each produces a new Set only on a
  // real change, so the derived `expandedIds` array stays referentially stable.
  const [expandedWorkspaceIds, setExpandedWorkspaceIds] = useState<Set<number>>(
    () => new Set()
  );

  const expand = useCallback((id: number) => {
    setExpandedWorkspaceIds((prev) => {
      if (prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const toggleExpand = useCallback((id: number) => {
    setExpandedWorkspaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Only expanded workspaces load calcs (the lazy invariant). Memoized so the array
  // identity is stable between expansion changes.
  const expandedIds = useMemo(
    () => [...expandedWorkspaceIds],
    [expandedWorkspaceIds]
  );
  const { byWorkspace, statusByWorkspace } =
    useWorkspaceCalculations(expandedIds);

  // The tree rows — scope-filtered folders, each with its loaded eval leaves (empty
  // until the folder is expanded and its calcs arrive).
  const workspaces = useMemo<WorkspaceWithEvals[]>(
    () =>
      scopedWorkspaces.map((ws) => ({
        ...ws,
        // Saved evals move to the Saved Evaluations folder — exclude them from their
        // workspace's leaves (P8 decision 4). The browse table filters the same way.
        evals: (byWorkspace[ws.id] ?? [])
          .filter((calc) => !calc.saved)
          .map(toWorkspaceEvalRow),
      })),
    [scopedWorkspaces, byWorkspace]
  );

  // Navigate-to-eval (shared with WorkspacePage's row click) — routes to the eval under
  // its workspace. Defined once in `useSelectEval`.
  const selectEval = useSelectEval();

  // Open a workspace's management/browse view by ROUTING to it (`workspaces/:id`). The
  // route change re-keys the page identity (workspaceId set, calculationId null → the
  // content sections read the package rollup) and flows through the `useUnsavedChanges`
  // guard — the SAME single confirm-on-dirty the eval click uses. No atom, no clear effect.
  const selectWorkspace = useCallback(
    (targetWorkspaceId: number) => {
      history.push(eval3WorkspacePath(packageId, targetWorkspaceId));
    },
    [history, packageId]
  );

  // Open the pinned Saved Evaluations browse view by routing to it — same URL-driven pattern as
  // selectWorkspace (the route sets `isSavedEvalsView`; the confirm-on-dirty guard still applies).
  const selectSavedEvals = useCallback(() => {
    history.push(eval3SavedEvalsPath(packageId));
  }, [history, packageId]);

  // Start a from-scratch draft (Model A) under `targetWorkspaceId`: expand the owning
  // folder so the provisional node appears, then route to the `eval/new` sentinel (the
  // draft IS the route now — no atom). The push flows through the confirm-on-dirty guard.
  const startDraftEval = useCallback(
    (targetWorkspaceId: number) => {
      expand(targetWorkspaceId);
      history.push(eval3EvalPath(packageId, targetWorkspaceId, 'new'));
    },
    [expand, history, packageId]
  );

  // Show a workspace's draft work area — routes to its `eval/new`. Only reachable from the
  // provisional draft node (rendered when that route is already active), so effectively a
  // no-op re-push; kept for a clear tree handler.
  const selectDraft = useCallback(
    (targetWorkspaceId: number) => {
      history.push(eval3EvalPath(packageId, targetWorkspaceId, 'new'));
    },
    [history, packageId]
  );

  // ─── Tree expansion orchestration ───
  // This effect only EXPANDS a folder (never navigates — URL canonicalization + auto-
  // select are done SERVER-SIDE by `Packages::Eval3Controller`). Expansion drives the
  // lazy calc fetch.

  // A workspace-management / eval / draft route → expand that folder so its eval leaves
  // (and any provisional draft node) show in the tree. `expand` is idempotent, so re-runs on
  // expansion changes don't loop.
  //
  // EXCEPTION — a SAVED eval: it still routes under `/workspaces/:id/eval/:id`, but its leaf lives
  // in the Saved Evaluations folder (P8 decision 4), NOT its workspace. Auto-expanding the workspace
  // folder there would reveal unrelated evals + trigger a needless lazy calc fetch, so skip it. We
  // wait for the calc detail before deciding (undefined while loading) so a saved-eval deep link
  // never briefly mis-expands; a browse/draft route (no active calc) still expands immediately, and
  // a normal eval expands once its detail confirms it isn't saved (a cache hit in practice, and an
  // in-tree eval click already has its folder open — so the deferral isn't perceptible).
  useEffect(() => {
    if (workspaceId === null) {
      return;
    }
    if (
      calculationId !== null &&
      (calcDetail === undefined || calcDetail.saved)
    ) {
      return;
    }
    expand(workspaceId);
  }, [workspaceId, calculationId, calcDetail, expand]);

  return {
    workspaces,
    workspaceCount,
    evalCount,
    isLoading: isPending,
    error,
    refetch,
    expandedWorkspaceIds,
    toggleExpand,
    calcStatusByWorkspace: statusByWorkspace,
    selectEval,
    selectWorkspace,
    selectSavedEvals,
    startDraftEval,
    selectDraft,
  };
}

/**
 * Renames a workspace (`PATCH /evaluation_workspaces/:id`, `{ name?, notes? }`),
 * invalidating the workspace list on success so the tree, the subtitle totals, and the
 * center title all refresh from the single source. Fired from an event handler (the
 * `CenterHeader` InlineEdit `onSave`), never an effect.
 *
 * @return {object} The mutation result; call `.mutate({ id, name })`.
 */
export function useRenameWorkspace(): UseMutationResult<
  EvaluationWorkspace,
  Error,
  { id: number } & UpdateWorkspaceRequest
> {
  const queryClient = useQueryClient();
  return useMutation<
    EvaluationWorkspace,
    Error,
    { id: number } & UpdateWorkspaceRequest
  >({
    mutationFn: ({ id, ...body }) =>
      api
        .patch(`evaluation_workspaces/${id}`, {
          json: body,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<EvaluationWorkspace>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_WORKSPACES_QUERY_KEY],
      });
    },
  });
}

/** Query-key prefix for the package-wide saved-evaluations list (Saved Evaluations folder + browse). */
export const SAVED_EVALS_QUERY_KEY = 'eval3-new:saved-evals' as const;

/**
 * Renames an eval — `PATCH /evaluations/calculations/:id` with `{ description }` (the
 * eval's display "name" IS its `description`). On success invalidates calc-detail (the
 * center title), the workspace calc lists (the tree leaves + management table), AND the
 * saved-evals list (`SAVED_EVALS_QUERY_KEY` — the Saved Evaluations folder + browse table),
 * so the new name shows everywhere an eval can appear, saved OR unsaved. BE INS-10094 lifted
 * the saved-only guard, so any eval can be renamed; the BE 422s on a validation failure.
 *
 * @return {object} The mutation result; call `.mutate({ id, description })`.
 */
export function useRenameEval(): UseMutationResult<
  unknown,
  Error,
  { id: number; description: string }
> {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: number; description: string }>({
    mutationFn: ({ id, description }) =>
      api
        .patch(`evaluations/calculations/${id}`, {
          json: { description },
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CALC_DETAIL_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [WORKSPACE_CALCS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({ queryKey: [SAVED_EVALS_QUERY_KEY] });
    },
  });
}

/**
 * Marks the current calc as a saved evaluation — `POST /evaluations/calculations/:id/save` with an
 * EMPTY body. The BE `save_evaluation!` was hardened to PRESERVE the existing description/notes when
 * they're omitted (mirroring `update_details!`), so a bare save never nulls the name set via rename.
 * Synchronous 200, NO Pusher. On success invalidates FOUR caches so the whole surface reconciles:
 *   - calc detail (`CALC_DETAIL_QUERY_KEY`) → the Save button flips to "Saved";
 *   - the workspace calc lists (`WORKSPACE_CALCS_QUERY_KEY`) → the eval leaves its workspace
 *     folder + browse table (a saved eval shows only under Saved Evaluations);
 *   - the workspace LIST (`EVAL_WORKSPACES_QUERY_KEY`) → the per-workspace `savedCount`/
 *     `calculationCount` change, which drive the tree subtitle + caret (unlike a rename, a save
 *     changes counts, so this key is invalidated here but not in `useRenameEval`);
 *   - the package saved-evals list (`SAVED_EVALS_QUERY_KEY`) → it appears under Saved Evaluations.
 * The BE 422s the guards ("Evaluation is already saved" / "No calculation results to save"); the
 * hook rethrows the BE message so the owner can toast it. Fired from the Save button's onClick.
 *
 * @return {object} The mutation result; call `.mutate(calculationId)`.
 */
export function useSaveEvaluation(): UseMutationResult<unknown, Error, number> {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, number>({
    mutationFn: async (calculationId) => {
      try {
        return await api
          .post(`evaluations/calculations/${calculationId}/save`, {
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json();
      } catch (error) {
        // The save action 422s with `{ error }` — surface that guard message instead of
        // ky's generic HTTPError text. A non-HTTP error (timeout / network) rethrows as-is.
        if (!(error instanceof HTTPError)) {
          throw error;
        }
        const body = (await error.response.json().catch(() => null)) as {
          error?: string;
        } | null;
        if (body?.error) {
          throw new Error(body.error);
        }
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CALC_DETAIL_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [WORKSPACE_CALCS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [EVAL_WORKSPACES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({ queryKey: [SAVED_EVALS_QUERY_KEY] });
    },
  });
}

/**
 * Loads the package's saved evaluations — `GET /evaluations/calculations?view_mode=saved&
 * package_id=:id`. DB-backed (`build_api_index_scope(view_mode: 'saved')`), so a just-saved eval
 * appears immediately on refetch (no Elasticsearch indexing lag — that's why this uses the index
 * endpoint, not `queries/search`). Feeds the pinned "Saved Evaluations" tree folder + the
 * SavedEvalsPage browse table. Rows are `WorkspaceCalculation`-shaped (the shared `_calculation`
 * serializer). Loads the whole list (a package's saved-eval count is small).
 *
 * @param {number} packageId - The package whose saved evals to list.
 * @return {object} The React Query result; `data.calculations` is the saved-eval list.
 */
export function useSavedEvaluations(
  packageId: number
): UseQueryResult<SavedEvaluationsResponse, Error> {
  return useQuery<SavedEvaluationsResponse, Error>({
    queryKey: [SAVED_EVALS_QUERY_KEY, packageId],
    queryFn: ({ signal }) =>
      api
        .get('evaluations/calculations', {
          searchParams: {
            view_mode: 'saved',
            package_id: String(packageId),
            per_page: '10000',
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<SavedEvaluationsResponse>(),
    staleTime: 60_000,
  });
}

/** The center title bar's props, resolved from the current selection (drives a presentational CenterHeader). */
export interface CenterTitle {
  /** Workspace name | eval description | '' (package mode / not yet loaded). */
  title: string;
  /** True in workspace-management mode AND eval mode (the title is an editable InlineEdit). */
  editable: boolean;
  /** The InlineEdit's accessibility label ("Workspace name" | "Evaluation name"). */
  label?: string;
  /** Present only when editable → commits a workspace OR eval rename. */
  onSave?: (name: string) => void;
  /** Rename in flight (drives the InlineEdit saving affordance). */
  saving?: boolean;
  /** A failed-rename message (e.g. a 422 duplicate name) → CenterHeader shows a toast + reverts. */
  saveError?: string;
}

/**
 * Resolves the center title bar's props from the current selection so `CenterHeader`
 * can stay presentational. Owned by `Eval3NewContent`. Reads the selected workspace
 * (atom) and the route `calculationId`:
 *   - a workspace is selected → its name (from the workspace list), EDITABLE, with
 *     `onSave` = the workspace-rename mutation and `saving` = its in-flight flag;
 *   - else an eval is active → its `description` (from `useCalcDetail`), also EDITABLE
 *     (BE INS-10094 allows renaming any eval), `onSave` = the eval-rename mutation;
 *   - else (package mode) → an empty title.
 * The workspace-list + calc-detail reads share React Query keys with the tree, so they
 * dedupe to no extra fetch. A failed rename of either kind surfaces via `saveError`.
 *
 * @return {CenterTitle} The title bar props for the current selection.
 */
export function useCenterTitle(): CenterTitle {
  const { packageId, calculationId, workspaceId, isDraft } = useEval3Page();
  const { data } = useEvaluationWorkspaces(packageId);
  const calcDetail = useCalcDetail(calculationId);
  const { mutate: renameWorkspaceMutate, isPending: workspaceSaving } =
    useRenameWorkspace();
  const { mutate: renameEvalMutate, isPending: evalSaving } = useRenameEval();
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  // Clear a stale rename error when the active title target changes — a different
  // workspace opened, or a different eval activated — so a toast never carries over.
  useEffect(() => {
    setSaveError(undefined);
  }, [workspaceId, calculationId]);

  const renameWorkspace = useCallback(
    (name: string) => {
      if (workspaceId === null) {
        return;
      }
      setSaveError(undefined);
      renameWorkspaceMutate(
        { id: workspaceId, name },
        {
          // On failure (e.g. a 422 "name already taken"), surface a message; the
          // CenterHeader reverts the field to the previous name + shows the toast.
          onError: () =>
            setSaveError(
              'Couldn’t rename the workspace. The name may already be in use.'
            ),
        }
      );
    },
    [workspaceId, renameWorkspaceMutate]
  );

  const renameEval = useCallback(
    (description: string) => {
      if (calculationId === null) {
        return;
      }
      setSaveError(undefined);
      renameEvalMutate(
        { id: calculationId, description },
        {
          onError: () =>
            setSaveError('Couldn’t rename the evaluation. Please try again.'),
        }
      );
    },
    [calculationId, renameEvalMutate]
  );

  // Workspace-management (browse) mode → editable workspace name. Excludes a draft
  // (`eval/new` also has a workspace + null calc): composing a new eval isn't "viewing
  // workspace X", so it falls through to the package-mode empty bar (as the draft did
  // pre-refactor).
  if (workspaceId !== null && calculationId === null && !isDraft) {
    const workspace = data?.workspaces.find((ws) => ws.id === workspaceId);
    return {
      title: workspace?.name ?? '',
      editable: true,
      label: 'Workspace name',
      onSave: renameWorkspace,
      saving: workspaceSaving,
      saveError,
    };
  }

  // Eval mode → the active eval's name (its `description`), editable (empty until
  // calc-detail loads).
  if (calculationId !== null) {
    return {
      // Same display name the tree leaf + table show (description, else <id>-<author>).
      title: evalDisplayName(
        calculationId,
        calcDetail.data?.description,
        calcDetail.data?.runBy?.name
      ),
      editable: true,
      label: 'Evaluation name',
      onSave: renameEval,
      saving: evalSaving,
      saveError,
    };
  }

  // Package mode → empty bar.
  return { title: '', editable: false };
}

/**
 * Creates a workspace (`POST /packages/:packageId/evaluation_workspaces`,
 * `{ name?, notes? }` — a blank name gets a BE-assigned default). Returns the full
 * new workspace (incl. its `id`, so the caller can select it). Fired from the Add
 * modal's Create button.
 *
 * On success it OPTIMISTICALLY prepends the created workspace to the cached list
 * (`setQueryData`) BEFORE the reconciling refetch: the create response IS the
 * `EvaluationWorkspace` list-item shape (same type, `calculationCount`/`savedCount`
 * already 0 on a fresh folder), so no field is fabricated. This lets a client
 * navigation to the just-created `/workspaces/:newId` render its name + (empty) tree
 * immediately, rather than blanking until the invalidation refetch lands. The
 * `invalidateQueries` stays as the authoritative reconcile (it corrects `meta.totalCount`
 * and any server-assigned defaults). Guarded so it no-ops until the list is cached and
 * skips a duplicate if the id is somehow already present (idempotent).
 *
 * @param {number} packageId - The package to create the workspace under.
 * @return {object} The mutation result; call `.mutate({ name })`.
 */
export function useCreateWorkspace(
  packageId: number
): UseMutationResult<
  Omit<EvaluationWorkspace, 'calculationCount' | 'savedCount'>,
  Error,
  CreateWorkspaceRequest
> {
  const queryClient = useQueryClient();
  return useMutation<
    Omit<EvaluationWorkspace, 'calculationCount' | 'savedCount'>,
    Error,
    CreateWorkspaceRequest
  >({
    mutationFn: (body) =>
      api
        .post(`packages/${packageId}/evaluation_workspaces`, {
          json: body,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<Omit<EvaluationWorkspace, 'calculationCount' | 'savedCount'>>(),
    onSuccess: (created) => {
      // Optimistic prepend so a navigation to the new workspace renders immediately off
      // the cache. The create response (`create.json.jbuilder`, `include_counts: false`)
      // OMITS `calculationCount` / `savedCount` — reflected in the mutation's `Omit<…>`
      // type — so fill the known zeros for a brand-new (calc-less) workspace, which widens
      // it back to the full `EvaluationWorkspace` list-item shape for the cache; otherwise
      // the tree's `evalCount`
      // reduce over the list would add `undefined` and render `NaN` until the reconcile
      // refetch lands. A fresh workspace's last-activity is "now", so it sorts to the front
      // (matching the list's last-activity-DESC order). Skipped until the list is cached
      // (nothing to seed) and if the id is already present (no duplicate).
      queryClient.setQueryData<EvaluationWorkspacesResponse>(
        [EVAL_WORKSPACES_QUERY_KEY, packageId],
        (prev) => {
          if (!prev || prev.workspaces.some((ws) => ws.id === created.id)) {
            return prev;
          }
          const optimistic = { ...created, calculationCount: 0, savedCount: 0 };
          return {
            ...prev,
            workspaces: [optimistic, ...prev.workspaces],
            meta: { ...prev.meta, totalCount: prev.meta.totalCount + 1 },
          };
        }
      );
      // Reconcile against the server (corrects totals / any server-assigned defaults).
      void queryClient.invalidateQueries({
        queryKey: [EVAL_WORKSPACES_QUERY_KEY, packageId],
      });
    },
  });
}

// ─── P5 · Calculate, Eval Settings & Package Overview ───

/** Query-key prefix for a completed calc's aggregated result (Package Overview). */
export const EVAL_RESULT_QUERY_KEY = 'eval3-new:eval-result' as const;

/** Inputs for `useEvalResult`. */
interface UseEvalResultParams {
  /** The viewed eval-calc id (route `:evalId`), or `null` in package mode. */
  calcId: number | null;
  /**
   * The viewed eval's `useCalcDetail` status (CAPITALIZED — `'Completed'` etc.). The
   * gate: `eval_result` fires ONLY on `'Completed'`, so it doesn't 404-churn while the
   * calc is `Processing`. The completion refetch flips this to `'Completed'`, re-opening
   * the gate. Passed by the caller (which already reads `useCalcDetail`), not re-fetched.
   */
  status?: CalcStatus;
  /** The one time dimension `eval_result` takes (from `displaySettingsAtom.aggregation`). */
  aggregationKey: AggregationKey;
  /** Dataset filters — same `serverFiltersAtom` shape the table/summary send. */
  filters?: ServerFilter[];
  /** Spatial scope — same `mapScopeAtom` polygon the table/summary send. */
  mapScope?: MapScope;
}

/**
 * Fetches a completed calc's aggregated result
 * (`POST /evaluations/calculations/:id/eval_result`) — the Package Overview pricing
 * rollup, narrowed by the same `{ filters, mapScope }` page state the table + summary
 * read, at the chosen `aggregationKey` (the one time dimension this endpoint takes;
 * measurement basis + date range are client-side over the response). Keyed on `calcId`
 * + `{ aggregationKey, filters, mapScope }` (normalized to `[]` / `null` so "nothing
 * set" is one stable entry); `keepPreviousData` keeps the prior rollup visible through
 * a refetch — so an update-in-place recalc shows the old result until completion.
 *
 * **Gated on `calcId !== null && status === 'Completed'`** — NOT on the presence of
 * `results.evalResults`, which the serializer emits whenever `calculation_id` is set
 * (i.e. while `Processing` too — the Pusher channel needs it), so ORing it in would
 * defeat the guard and 404-churn against an unwritten result blob.
 *
 * @param {UseEvalResultParams} params - The calc id + status gate + the display/filter inputs.
 * @return {object} The React Query result for the aggregated free-cash-flow response.
 */
export function useEvalResult(
  params: UseEvalResultParams
): UseQueryResult<AggregatedFreeCashFlowResultResponse, Error> {
  const { calcId, status, aggregationKey, filters, mapScope } = params;
  return useQuery<AggregatedFreeCashFlowResultResponse, Error>({
    queryKey: [
      EVAL_RESULT_QUERY_KEY,
      calcId,
      {
        aggregationKey,
        filters: filters ?? [],
        mapScope: mapScope ?? null,
      },
    ],
    queryFn: ({ signal }) => {
      // Omit the empty halves so the unfiltered body is clean + matches the normalized
      // key. `aggregationKey` is camelCase (the feature-wide convention; the BE config
      // reader is camel/snake tolerant). The calc id is the URL `:id`, not a body field.
      const payload = {
        aggregationKey,
        ...(filters?.length ? { filters } : {}),
        ...(mapScope ? { mapScope } : {}),
      };
      // TEMPORARY timeout (see EVAL3_REQUEST_TIMEOUT_MS) — the result assembler runs the
      // same heavy slot population as master_data; decrease back once the BE is fast.
      return api
        .post(`evaluations/calculations/${calcId}/eval_result`, {
          json: payload,
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<AggregatedFreeCashFlowResultResponse>();
    },
    enabled: calcId !== null && status === 'Completed',
    placeholderData: keepPreviousData, // keep the prior rollup (in-place recalc)
    staleTime: 1_800_000, // 30 min — keyed per (calc, aggregation, filter set); session-stable
  });
}

/** Query-key prefix for the cursor-paginated cashflows-by-month warehouse query. */
export const CASHFLOWS_BY_MONTH_QUERY_KEY =
  'eval3-new:cashflows-by-month' as const;

/** Rows per cashflows-by-month page (the warehouse default; keyset-cursor paged). */
const CASHFLOWS_PAGE_SIZE = 500;

/**
 * Appends the warehouse GRAIN columns (`date`, `uniqueSlotId`) to a cashflows sort
 * so the keyset cursor is built over a UNIQUE ordering. The BE constructs its
 * keyset condition from the sent sort columns verbatim, with no tiebreaker of its
 * own — so a sort on a non-unique column alone (e.g. `netCashflow`) lets rows tied
 * across a page boundary be skipped or duplicated. Appending the grain makes every
 * ordering total, closing that from the FE (a BE-side default tiebreaker is still
 * the right long-term fix — see the deferred ledger).
 *
 * BE-compat constraint (verified against `Warehouse::QueryBuilder`): the keyset is
 * ONE tuple comparison — `(col1, col2, …) >|< (v1, v2, …)` — whose comparator comes
 * from the FIRST column's direction (`cursor_comparator`). A tuple compare is
 * lexicographic in a single direction, so it's only correct when EVERY sort column
 * shares that direction. The appended grain therefore INHERITS the leading
 * direction (a tiebreaker's direction is meaningless to the user; tuple
 * compatibility isn't). Grain columns already present keep their position.
 *
 * @param {CashflowSort[] | undefined} sort - The user's sort (empty/undefined = the default order).
 * @return {CashflowSort[]} The sort with the grain appended — never empty, so the
 *   request always carries an explicit, unique, single-direction ordering.
 */
export function withGrainTiebreakers(
  sort: CashflowSort[] | undefined
): CashflowSort[] {
  const base = sort ?? [];
  const dir = base[0]?.dir ?? 'asc';
  const present = new Set(base.map((entry) => entry.column));
  return [
    ...base,
    // `date` first so the no-user-sort default mirrors the BE's own
    // `date asc, slot_ref asc` visual order (with a unique second key).
    ...(present.has('date') ? [] : [{ column: 'date', dir }]),
    ...(present.has('uniqueSlotId') ? [] : [{ column: 'uniqueSlotId', dir }]),
  ];
}

/** Inputs for `useCashflowsByMonth`. */
interface UseCashflowsByMonthParams {
  /** The viewed eval-calc id (route `:evalId`), or `null` in package mode. */
  calcId: number | null;
  /**
   * The viewed eval's `useCalcDetail` status — the fetch gate (`'Completed'` only).
   * The BE 404-gates merely on `calculation_id` presence, which is set WHILE
   * `Processing` too, so the parquet may not exist yet — same posture as
   * `useEvalResult`: never fetch mid-run.
   */
  status?: CalcStatus;
  /** Warehouse sort (camelCase columns; the BE underscores). Empty → BE default `date, slot_ref`. */
  sort?: CashflowSort[];
  /** Server-side text search (slot_ref / uwi / well_name …). */
  search?: string;
  /** Dataset filters — the page-level `serverFiltersAtom` shape (NOT warehouse column filters). */
  filters?: ServerFilter[];
  /**
   * Per-column warehouse filters (INS-11272) from the inline filter row —
   * `{ column, op, value }`. Sent in the SAME `filters` array as the page-level
   * dataset filters: the BE keeps the `column`-shaped entries for the warehouse
   * query and resolves the `attribute`-shaped page filters separately, so the two
   * AND-compose.
   */
  columnFilters?: WarehouseColumnFilter[];
  /** Spatial scope — same `mapScopeAtom` polygon the other reads send. */
  mapScope?: MapScope;
}

/**
 * Fetches a completed calc's cashflows-by-month
 * (`POST /evaluations/calculations/:id/cashflows_by_month`) — a slot × month
 * warehouse (parquet) query, CURSOR-paginated (no totalCount), hence
 * `useInfiniteQuery`: each page carries `meta.nextCursor` + `hasMore`, and the table
 * consumes the accumulated `data.pages` via DataTable's infinite-scroll mode.
 *
 * The page-level `{ filters, mapScope }` ride the body and narrow the rows to the
 * matching slots (the BE resolves them to a `unique_slot_id IN` warehouse filter) —
 * the same narrowing the table/summary/map read. The inline per-column filters
 * (`columnFilters`, INS-11272) ride the SAME `filters` array as `{ column, op, value }`
 * warehouse predicates: the BE keeps those for the warehouse query and resolves the
 * `attribute`-shaped page filters separately, so the two AND-compose.
 *
 * Keyed on `calcId` + `{ sort, search, filters, mapScope }` (a change resets the
 * accumulated pages — correct: the cursor encodes the sort/filter set). NOTE: unlike
 * the sibling reads there is NO `placeholderData: keepPreviousData`, so a key change
 * clears the rows and reloads rather than dimming the prior view through the
 * transition. Gated on `status === 'Completed'` (the BE's own 404 gate passes while
 * `Processing`, so the status gate is load-bearing).
 * Invalidated by `useEvalRunSync` on completion.
 *
 * @param {UseCashflowsByMonthParams} params - The calc id + status gate + query inputs.
 * @return {object} The infinite-query result; flatten `data.pages[].rows` for the table.
 */
export function useCashflowsByMonth(
  params: UseCashflowsByMonthParams
): UseInfiniteQueryResult<InfiniteData<CashflowsByMonthPage>, Error> {
  const { calcId, status, sort, search, filters, columnFilters, mapScope } =
    params;
  // The page-level dataset filters AND the inline per-column warehouse filters ride
  // ONE `filters` array (INS-11272): the BE keeps the `column`-shaped entries for the
  // warehouse query and resolves the `attribute`-shaped page filters to a slot
  // restriction separately, so the two AND-compose. Kept separate in the query key
  // (they're distinct user intents) so either one changing resets the cursor pages.
  const combinedFilters: Array<ServerFilter | WarehouseColumnFilter> = [
    ...(filters ?? []),
    ...(columnFilters ?? []),
  ];
  return useInfiniteQuery({
    queryKey: [
      CASHFLOWS_BY_MONTH_QUERY_KEY,
      calcId,
      {
        sort: sort ?? [],
        search: search ?? '',
        filters: filters ?? [],
        columnFilters: columnFilters ?? [],
        mapScope: mapScope ?? null,
      },
    ],
    queryFn: ({ pageParam, signal }) => {
      // Omit the empty halves so the first-page body is clean + matches the
      // normalized key; `cursor` rides only on follow-up pages. The sort ALWAYS
      // goes on the wire, grain-tiebroken (`withGrainTiebreakers`) so the keyset
      // cursor pages over a unique ordering — deterministic per key, so keying on
      // the raw user `sort` above stays correct.
      const payload = {
        limit: CASHFLOWS_PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
        sort: withGrainTiebreakers(sort),
        ...(search ? { search } : {}),
        ...(combinedFilters.length ? { filters: combinedFilters } : {}),
        ...(mapScope ? { mapScope } : {}),
      };
      return api
        .post(`evaluations/calculations/${calcId}/cashflows_by_month`, {
          json: payload,
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<CashflowsByMonthPage>();
    },
    // The first page has no cursor: an empty string, which the body above leaves out.
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.meta.hasMore
        ? (lastPage.meta.nextCursor ?? undefined)
        : undefined;
    },
    enabled: calcId !== null && status === 'Completed',
    staleTime: 1_800_000, // 30 min — keyed per (calc, query inputs); session-stable
  });
}

// ─── P6 · Output Aggregation (ValueRollup) ───

/** Query key for the static ValueRollup options catalog (global — one entry). */
export const VALUE_ROLLUP_OPTIONS_QUERY_KEY =
  'eval3-new:value-rollup-options' as const;

/** GET path for the ValueRollup options catalog. Relative — `api` (ky) sets prefixUrl. */
export const VALUE_ROLLUP_OPTIONS_URL = 'queries/packages/value_rollup_options';

/** Query-key prefix for the ValueRollup table pivot (offset-paginated). */
export const VALUE_ROLLUP_QUERY_KEY = 'eval3-new:value-rollup' as const;

/** POST path for the ValueRollup table pivot. Relative — `api` (ky) sets prefixUrl. */
export const VALUE_ROLLUP_URL = 'queries/packages/value_rollup';

/**
 * Fetches the static ValueRollup options catalog
 * (`GET /queries/packages/value_rollup_options`) — the dimension + metric picker
 * lists and the group-by / metric defaults. GLOBAL and static (the BE builds it from
 * no input), so it's ONE cache entry (no packageId / calcId in the key) with
 * `staleTime: Infinity` — fetched once on the section's first mount, a cache hit on
 * every later open. Deliberately NOT invalidated by `useEvalRunSync` (a run doesn't
 * change the vocabulary). The wire→domain rename (`defaults.metrics` → `metric`)
 * happens in `adaptRollupOptions`.
 *
 * @param {object} [options] - `enabled` gate — pass `false` to defer the fetch (e.g. a
 *   consumer that only needs the defaults on demand). Defaults to `true` (fetch on mount).
 * @return {object} The React Query result; `data` is the adapted `RollupOptions`.
 */
export function useRollupOptions(options?: {
  enabled?: boolean;
}): UseQueryResult<RollupOptions, Error> {
  return useQuery<RollupOptions, Error>({
    queryKey: [VALUE_ROLLUP_OPTIONS_QUERY_KEY],
    queryFn: async ({ signal }) => {
      const raw = await api
        .get(VALUE_ROLLUP_OPTIONS_URL, {
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<RollupOptionsResponse>();
      return adaptRollupOptions(raw);
    },
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
  });
}

/** Inputs for `useValueRollup`. */
interface UseValueRollupParams {
  /** The viewed eval-calc id (route `:evalId`), or `null` in package mode. */
  calcId: number | null;
  /** The package every query is scoped to (sent in the body, like `master_data`). */
  packageId: number;
  /**
   * The viewed eval's `useCalcDetail` status. P6 is calc-mode only, so the fetch is
   * gated on `'Completed'` (with `calcId !== null`) — package mode / a mid-run calc
   * never fetches. The completion refetch flips this and re-opens the gate.
   */
  status?: CalcStatus;
  /**
   * The EFFECTIVE group-by dimensions (`rollupDimensionsAtom ?? options.defaults`).
   * Empty ⇒ the query stays disabled (the "select a dimension" empty state, not a
   * fetch). FIRST-EVER open the section chains this after `useRollupOptions` resolves
   * (the default group-by lives in the config); later opens read the persisted atom.
   */
  dimensions: string[];
  /** 1-indexed page (section-owned; resets to 1 on any control change). */
  page: number;
  /** Rows per page (one of `MASTER_DATA_PAGE_SIZES`). */
  pageSize: number;
  /** Single-column server sort, or null (the BE's default order). */
  sort?: RollupSort | null;
  /** Debounced server search (from `rollupSearchQueryAtom`). */
  search?: string;
  /** Applied cashflow-period window, or null (unscaled values). */
  cashflowPeriod?: CashflowPeriod | null;
  /** Dataset filters — the same `serverFiltersAtom` shape every eval3 read sends. */
  filters?: ServerFilter[];
  /** Spatial scope — the same `mapScopeAtom` polygon every eval3 read sends. */
  mapScope?: MapScope;
  /**
   * The single active bin, or null. Numeric binning adds a leading `numericBin` column
   * and regroups the rows; temporal binning adds the separate `temporalBins` summary.
   * The two are mutually exclusive (the atom holds one value), so this is one param.
   */
  bin?: RollupBin | null;
}

/**
 * Fetches one OFFSET page of the ValueRollup table pivot
 * (`POST /queries/packages/value_rollup`) — the self-describing `columns`, the
 * aggregated `rows` (+ per-row `pctOfPackage`), the grand `totals`, and the applied
 * cashflow summary. Offset pagination (`page` / `pageSize` / `totalCount`) mirroring
 * `useMasterData`: a paginated `useQuery` (NOT `useInfiniteQuery`) with
 * `keepPreviousData`, so a page / sort / search / group / period / filter change dims
 * the prior page through the transition. The section owns `page` / `pageSize` and
 * passes them straight through (single page owner, the `useMasterDataView` pattern).
 *
 * Keyed on `packageId` + the full request identity (`calculationId`, `dimensions`,
 * `page`, `pageSize`, `cashflowPeriod`, `filters`, `mapScope`, `sort`, `search`;
 * empty halves normalized so "nothing set" is one stable entry). **Gated on
 * `calcId !== null && status === 'Completed' && dimensions.length > 0`** — P6 is
 * calc-mode only, and no dimensions is the empty state, not a fetch. Invalidated by
 * `useEvalRunSync` on run completion (the packageId-scoped key). The wire→domain map
 * is `adaptValueRollupPage`.
 *
 * @param {UseValueRollupParams} params - The calc identity + offset paging + the group/sort/search/period/filter inputs.
 * @return {object} The React Query result; `data` is the adapted `RollupPage`.
 */
export function useValueRollup(
  params: UseValueRollupParams
): UseQueryResult<RollupPage, Error> {
  const {
    calcId,
    packageId,
    status,
    dimensions,
    page,
    pageSize,
    sort,
    search,
    cashflowPeriod,
    filters,
    mapScope,
    bin,
  } = params;
  // Derive the two scalar request params from the single active bin (mutually exclusive by the
  // atom's shape) — shared with the content export via `rollupBinParams`.
  const { numericBin, temporalBin } = rollupBinParams(bin);
  return useQuery<RollupPage, Error>({
    queryKey: [
      VALUE_ROLLUP_QUERY_KEY,
      packageId,
      {
        calculationId: calcId,
        dimensions,
        page,
        pageSize,
        cashflowPeriod: cashflowPeriod ?? null,
        filters: filters ?? [],
        mapScope: mapScope ?? null,
        sort: sort ?? null,
        // Trimmed so a whitespace-only search doesn't mint a duplicate entry
        // (matches the trimmed body the builder sends).
        search: search?.trim() ?? '',
        // The bin object fully determines numericBin/temporalBin, so key on it directly.
        bin: bin ?? null,
      },
    ],
    queryFn: async ({ signal }) => {
      // `enabled` guarantees a non-null calc id here (P6 is calc-mode only); the
      // builder drops the empty halves + trims the search so the body matches the key.
      try {
        const raw = await api
          .post(VALUE_ROLLUP_URL, {
            json: buildValueRollupRequest({
              calculationId: calcId as number,
              packageId,
              dimensions,
              page,
              pageSize,
              sort,
              search,
              cashflowPeriod,
              filters,
              mapScope,
              numericBin,
              temporalBin,
            }),
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<ValueRollupResponse>();
        return adaptValueRollupPage(raw);
      } catch (err) {
        // INS-12433 — the card renders a Retry on every failure; typing the envelope lets
        // it drop that Retry when the BE says the failure is permanent.
        return throwTypedEnvelopeError(err);
      }
    },
    // A numeric bin is itself a grouping, so a bin-only rollup (no dimensions) is a valid
    // fetch — `rollupHasGrouping` mirrors the BE guard (a temporal bin alone still needs a
    // dimension) and is shared with the card's blank-state gate + the export config gate.
    enabled:
      calcId !== null &&
      status === 'Completed' &&
      rollupHasGrouping(dimensions, bin),
    placeholderData: keepPreviousData, // smooths page/sort/search/group transitions
    staleTime: 1_800_000, // 30 min — keyed per (calc, request identity); session-stable
  });
}

/** Query-key prefix for the Visual Summary chart series. */
export const VISUAL_SUMMARY_QUERY_KEY = 'eval3-new:visual-summary' as const;

/** POST path for the Visual Summary chart. Relative — `api` (ky) sets prefixUrl. */
export const VISUAL_SUMMARY_URL = 'queries/packages/visual_summary';

/**
 * Top-N cap the chart requests (and the BE ranks + truncates to). The chart is a
 * ranked bar summary, not a full listing — 25 keeps it legible.
 */
export const VISUAL_SUMMARY_LIMIT = 25;

/** Inputs for `useVisualSummary`. */
interface UseVisualSummaryParams {
  /** The viewed eval-calc id (route `:evalId`), or `null` in package mode. */
  calcId: number | null;
  /** The package every query is scoped to (sent in the body, like `master_data`). */
  packageId: number;
  /**
   * The viewed eval's `useCalcDetail` status. P6 is calc-mode only, so the fetch is
   * gated on `'Completed'` (with `calcId !== null`); the completion refetch re-opens it.
   */
  status?: CalcStatus;
  /** The chart's OWN group-by dimensions (independent of the table's). Empty ⇒ disabled. */
  dimensions: string[];
  /**
   * The chart metric key (config metric, `slotOnly` excluded by the caller — a date
   * isn't chartable and the BE returns null for one). Empty ⇒ the query stays disabled.
   */
  metric: string;
  /** The chart's OWN applied cashflow-period window, or null (unscaled values). */
  cashflowPeriod?: CashflowPeriod | null;
  /** Dataset filters — the same `serverFiltersAtom` shape every eval3 read sends. */
  filters?: ServerFilter[];
  /** Spatial scope — the same `mapScopeAtom` polygon every eval3 read sends. */
  mapScope?: MapScope;
}

/**
 * Fetches the ranked top-N Visual Summary series
 * (`POST /queries/packages/visual_summary`) — one `value` per group, already
 * FE-domain-shaped (dim values + `value`), so the hook returns `raw.rows` with no
 * adapter. Mirrors `useValueRollup` (same base builder + calc-mode gating +
 * `keepPreviousData`), but it is top-N (`limit`), NOT offset-paginated, and carries
 * no sort / search / `%` — the chart is a ranked bar summary. The response's applied
 * period carries NO `cashflowPeriodSummary` (table-only), so there's no summary line.
 *
 * Keyed on `packageId` + the full request identity (`calculationId`, `dimensions`,
 * `metric`, `limit`, `cashflowPeriod`, `filters`, `mapScope`; empty halves normalized).
 * **Gated on `calcId !== null && status === 'Completed' && dimensions.length > 0 &&
 * metric !== ''`.** Invalidated by `useEvalRunSync` on run completion (the
 * packageId-scoped key), alongside `value_rollup`.
 *
 * @param {UseVisualSummaryParams} params - The calc identity + chart dims / metric + optional window/filter/scope.
 * @return {object} The React Query result; `data` is the ranked `VisualSummaryRow[]`.
 */
export function useVisualSummary(
  params: UseVisualSummaryParams
): UseQueryResult<VisualSummaryRow[], Error> {
  const {
    calcId,
    packageId,
    status,
    dimensions,
    metric,
    cashflowPeriod,
    filters,
    mapScope,
  } = params;
  return useQuery<VisualSummaryRow[], Error>({
    queryKey: [
      VISUAL_SUMMARY_QUERY_KEY,
      packageId,
      {
        calculationId: calcId,
        dimensions,
        metric,
        limit: VISUAL_SUMMARY_LIMIT,
        cashflowPeriod: cashflowPeriod ?? null,
        filters: filters ?? [],
        mapScope: mapScope ?? null,
      },
    ],
    queryFn: async ({ signal }) => {
      // `enabled` guarantees a non-null calc id + a metric here; the builder drops the
      // empty halves so the body matches the key.
      try {
        const raw = await api
          .post(VISUAL_SUMMARY_URL, {
            json: buildVisualSummaryRequest({
              calculationId: calcId as number,
              packageId,
              dimensions,
              metric,
              limit: VISUAL_SUMMARY_LIMIT,
              cashflowPeriod,
              filters,
              mapScope,
            }),
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<VisualSummaryResponse>();
        return raw.rows;
      } catch (err) {
        // INS-12433 — same reason as the rollup card it sits beside: without this both
        // cards offer a Retry that cannot work on a snapshot-less calculation.
        return throwTypedEnvelopeError(err);
      }
    },
    enabled:
      calcId !== null &&
      status === 'Completed' &&
      dimensions.length > 0 &&
      metric !== '',
    placeholderData: keepPreviousData, // smooths group/metric/period transitions
    staleTime: 1_800_000, // 30 min — keyed per (calc, request identity); session-stable
  });
}

/**
 * The shared page / calc / dataset context every Output Aggregation card reads: the
 * package + viewed calc identity, the calc's status gate, the run-in-flight flag, the
 * dataset filters / spatial scope, and the "(N of M Subtracts)" an Asset that names a
 * Tract held in part carries (`usePartialTracts`). Both the table and chart cards call it,
 * so this composition lives in one place. Reads only (no writes) — a filter/scope change
 * re-renders every card that calls it, which is the intended shared-input behavior.
 *
 * @return {object} `{ packageId, calculationId, status, isRunning, filters, mapScope, labelForName, nameForAsset }`.
 */
export function useRollupCardContext() {
  const { packageId, calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const { isRunning } = useCalcLifecycle(calculationId);
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const { labelForName, nameForAsset } = usePartialTracts();
  // No scenario here on purpose: value rollup requires a completed calc, whose frozen blob
  // already has the places baked in, so the pick cannot change what it reads (INS-10669).
  return {
    packageId,
    calculationId,
    status: calcDetail?.status,
    isRunning,
    filters,
    mapScope,
    labelForName,
    nameForAsset,
  };
}

/** Query-key prefix for the DCA-scenario list (the Eval Settings dropdown options). */
export const DCA_SCENARIOS_QUERY_KEY = 'eval3-new:dca-scenarios' as const;

/**
 * Endpoint path for the DCA-scenario list. No leading slash — `api` (ky) sets
 * `prefixUrl`, so paths are relative.
 */
export const DCA_SCENARIOS_URL = 'dca_scenarios';

/**
 * Fetches the DCA-scenario options (`GET /dca_scenarios`) for the Eval Settings DCA
 * dropdown — a bare `DcaScenarioItem[]` (`{ id, name, href }`, ordered by name).
 * Existing endpoint, reused (the legacy `eval-3-workspace` panel fetches the same).
 * Package-independent and session-stable, so `staleTime: Infinity` (fetched once). The
 * "Legacy" (`null`) default is prepended in the dropdown component (T11), not here.
 *
 * @return {object} The React Query result; `data` is the DCA-scenario list.
 */
export function useDcaScenarios(): UseQueryResult<DcaScenarioItem[], Error> {
  return useQuery<DcaScenarioItem[], Error>({
    queryKey: [DCA_SCENARIOS_QUERY_KEY],
    queryFn: ({ signal }) =>
      api
        .get(DCA_SCENARIOS_URL, { signal, timeout: EVAL3_REQUEST_TIMEOUT_MS })
        .json<DcaScenarioItem[]>(),
    staleTime: Infinity,
  });
}

/** Query-key prefix for the org-wide Scenario list (the Eval Settings "Select Scenario" options). */
export const SCENARIOS_QUERY_KEY = 'eval3-new:scenarios' as const;

/**
 * Endpoint path for the org-wide Scenario list. No leading slash — `api` (ky) sets
 * `prefixUrl`, so paths are relative.
 */
export const SCENARIOS_URL = 'scenarios';

/**
 * Fetches the org-wide Scenario options (`GET /scenarios`) for the Eval Settings
 * "Select Scenario" combobox — `ScenariosResponse[]` (`{ id, name, interestType, href,
 * isDefault, … }`). These are the composable case/price-deck bundles a calc runs with
 * (sent top-level as `scenarioId`), the same list the legacy panel's "Select Scenario"
 * uses. Package-independent + session-stable, so `staleTime: Infinity` (fetched once).
 * Mirrors `useDcaScenarios` — a DIFFERENT list (DCA decline-curve scenarios); both feed
 * separate dropdowns.
 *
 * @return {object} The React Query result; `data` is the Scenario list.
 */
export function useScenarios(): UseQueryResult<ScenariosResponse[], Error> {
  return useQuery<ScenariosResponse[], Error>({
    queryKey: [SCENARIOS_QUERY_KEY],
    queryFn: ({ signal }) =>
      api
        .get(SCENARIOS_URL, { signal, timeout: EVAL3_REQUEST_TIMEOUT_MS })
        .json<ScenariosResponse[]>(),
    staleTime: Infinity,
  });
}

// ─── P5 · Calculate lifecycle ───

/**
 * Triggers a calculation — `POST /evaluations/calculate` (async; the BE returns 202).
 * TRIGGER ONLY: it does NOT subscribe to Pusher (`useEvalRunSync` is the sole
 * subscriber). The caller builds the body with `buildCalculateRequest(form.state.values,
 * ids)` and passes it to `mutate`, so the form-library coupling stays at the callsite.
 *
 * On the 202 it SEEDS the calc-detail cache (`setQueryData`) with the response's
 * `notifications` PLUS an echo of the just-submitted request (`configurations`,
 * `description`, `notes`, the scenario id), so the detail-driven subscriber attaches
 * immediately AND the form's seed always sees a config-carrying detail — no blank
 * window (the completion refetch replaces the echo with BE truth, the same persisted
 * config). For a brand-new / forked eval (the response `id` differs from the viewed
 * eval) it REBASELINES the form (`form.reset` to the current values — the edits are now
 * persisted in the new eval, so they're no longer "unsaved" and must not trip the
 * `useUnsavedChanges` block on Calculate's own navigation), invalidates the workspace
 * calc-list (so the new leaf appears in the tree), NAVIGATES to the new `:id`, then
 * clears the from-scratch draft. An update-in-place recalc reuses the same id and stays
 * put — NOT rebaselined, so a BE error keeps the form dirty for retry (FR#9). A POST
 * failure surfaces via the mutation's `error` (the caller renders an `ErrorToast`); the
 * form stays dirty (NOT rebaselined) so the user can fix and retry.
 *
 * @param {object} form - The lifted working-config form handle (the callsite holds it).
 * @return {object} The mutation; call `.mutate(request)` with a `CalculateRequest`.
 */
export function useCalculate(
  form: WorkingConfigForm['form']
): UseMutationResult<CalculateResponse, Error, CalculateRequest> {
  const { calculationId } = useEval3Page();
  const queryClient = useQueryClient();
  const selectEval = useSelectEval();
  const clearPendingTypeCurveWire = useSetAtom(pendingTypeCurveWireAtom);
  const setScheduleRejections = useSetAtom(scheduleOverrideRejectionsAtom);

  return useMutation<CalculateResponse, Error, CalculateRequest>({
    // A fresh attempt clears the previous one's per-cell schedule-override rejections
    // (INS-12497); `onError` re-populates them if THIS attempt is refused, so a stale
    // rejection can never outlive the run it belonged to.
    onMutate: () => setScheduleRejections([]),
    // A locked/out-of-order 422 carries per-cell rejections (parsed in `mutationFn` into
    // a `ScheduleCalculateRejectedError`). Publish them so the Slots table marks the exact
    // rejected cell instead of leaving its optimistic ✓ over a write the run refused. Any
    // other failure clears them (the generic error toast covers those).
    onError: (error) =>
      setScheduleRejections(
        error instanceof ScheduleCalculateRejectedError ? error.rejections : []
      ),
    mutationFn: async (request) => {
      try {
        return await api
          .post('evaluations/calculate', {
            json: request,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<CalculateResponse>();
      } catch (error) {
        // Surface the BE-sent message instead of ky's generic HTTPError text.
        // Both calculate error shapes carry `error`; the catch-all 422 (downstream
        // calculator / case-config failures, e.g. "No slot in this package falls
        // inside any area on this evaluation case (North Block)") also carries the
        // richer `errorMessage`. A timeout / network error
        // (not an HTTPError → no body) or a message-less body rethrows unchanged.
        if (!(error instanceof HTTPError)) {
          throw error;
        }
        const body = (await error.response.json().catch(() => null)) as {
          error?: string;
          errorMessage?: string;
          errors?: { slot_overrides?: unknown[] };
        } | null;
        // INS-10671 — a locked/out-of-order schedule date-override 422 carries a
        // per-entry `{ errors: { slot_overrides: [...] } }` body (no `error`/`errorMessage`).
        // Surface it as typed rejections so the schedule edits' rejection is not lost now
        // that overrides ride the unified Calculate instead of the dialog's own POST.
        const rejections = parseRejections(body);
        if (rejections.length > 0) {
          throw new ScheduleCalculateRejectedError(rejections);
        }
        const message = body?.errorMessage || body?.error;
        if (message) {
          throw new Error(message);
        }
        throw error;
      }
    },
    onSuccess: ({ id, notifications, run }, request) => {
      // Record the just-persisted interest positions so the nav guard
      // (`useUnsavedChanges`) can tell Calculate's own same-tick navigation — whose
      // live values ARE persisted, to a possibly NEW eval whose detail the guard's
      // render-captured baseline can't see yet — apart from genuinely unsubmitted
      // interest edits.
      lastSubmittedInterestItems = request.configurations.interest_items ?? [];
      // Seed the calc-detail cache from the 202 so the single detail-driven subscriber
      // (`useEvalRunSync`) attaches at once, ECHOING the just-submitted request so the
      // detail is never config-less (see the JSDoc above). `createdAt` isn't known
      // until the completion refetch — the `as CalcDetail` documents that gap; the
      // scenario echo carries only the id (the one field the form seed reads).
      queryClient.setQueryData<CalcDetail>(
        [CALC_DETAIL_QUERY_KEY, id],
        (prev) =>
          ({
            ...prev,
            status: 'Processing',
            // Carry the 202's own `run.dispatched` (false by construction — the run
            // dispatches from the job, not from the request that returned this). It must be
            // written EXPLICITLY, not left to `...prev`: on a re-run `prev` is the last
            // COMPLETED detail, whose `dispatched: true` would otherwise survive into this
            // run and mislabel its pre-dispatch phase; on a first run there is no `prev` at
            // all and the field would be `undefined`. Either way the pre-dispatch phase
            // (`Resolving scenarios…`) would never render — the 202 is the only word we get
            // until the job's post-dispatch event, since nothing refetches the detail in
            // between. Optional-chained so a response predating the field seeds `undefined`
            // ("unknown" — the panel falls back to the generic label) rather than throwing.
            dispatched: run?.dispatched,
            notifications,
            workspaceId: request.workspaceId,
            configurations: request.configurations,
            description: request.description ?? null,
            notes: request.notes ?? null,
            scenario:
              request.scenarioId !== undefined
                ? { id: request.scenarioId, name: '', href: '' }
                : null,
          }) as CalcDetail
      );
      // On ANY successful 202 the submitted override operations are persisted in the
      // eval's configurations (the create path stores them; the in-place path merges
      // them via update_columns) AND the echo above folds them into
      // `committedSlotOverrides` — so pending must clear on BOTH branches, or an
      // update-in-place run leaves the just-committed ops lingering as pending and the
      // NEXT run sends them twice: committed + pending, duplicating field entries in the
      // stored log on every subsequent in-place run (TC entries were saved by persist
      // compaction; field entries just accumulated). The old "no rebaseline on in-place,
      // so a BE error keeps the form dirty for retry" reasoning only covers the ERROR
      // path — onSuccess never fires there, so nothing about retry changes.
      form.reset({
        ...form.state.values,
        slotOverrides: [],
        scheduleSlotOverrides: [],
      });
      // The pending TC wire mirrors the pending log — committed now carries the entries,
      // so the read bodies must stop re-sending them (a lingering tombstone would keep
      // deleting a choice the run just persisted away, and a lingering delta would keep
      // hybridizing a fold that now matches). The fold atom needs no touch: it already
      // equals fold(new committed + nothing).
      clearPendingTypeCurveWire([]);
      // A new / forked eval gets a fresh id ≠ the viewed one → it's a new tree leaf:
      // refresh the calc-list, then navigate to it. Navigating from an `eval/new` draft
      // route to the real `eval/:id` IS the draft→persisted transition (the draft is
      // route-derived — no atom to clear).
      if (id !== calculationId) {
        // The reset above ALSO rebaselines (isDirty → false) BEFORE the push — the
        // `useUnsavedChanges` block reads dirtiness at transition time, so Calculate's
        // own navigation passes without the confirm. (The echo-rederived seed the form
        // re-syncs to after navigating may differ from this baseline on lossy fields —
        // display-only slot fields, and `createNewEvalOnCalculate` flips to false —
        // intended: the new eval defaults to update-in-place, and completion re-hydrates
        // from BE truth anyway.)
        void queryClient.invalidateQueries({
          queryKey: [WORKSPACE_CALCS_QUERY_KEY],
        });
        // Navigate to the new eval UNDER the workspace it was created in (the request's
        // target); the nested route needs the parent segment.
        selectEval(request.workspaceId, id);
      }
    },
  });
}

// Missed-Pusher recovery for the viewed run (ONCALL-937). Completion reaches this page over
// Pusher alone, and Pusher does not replay: a client not subscribed at the instant the webhook
// publishes loses the event permanently, and the run then reads Processing forever. These two
// knobs mirror the legacy page's already-tuned INS-8619 poll.
//
// The delay exists so Pusher always gets first refusal — the overwhelming majority of runs
// settle well inside it and cost nothing. Recovery arms ONLY while the viewed run is
// Processing, so a settled or idle page schedules nothing at all.
const RUN_RECOVERY_DELAY_MS = 3 * 60 * 1000; // 3 minutes
const RUN_RECOVERY_INTERVAL_MS = 30 * 1000; // 30 seconds
// When to stop re-reading a server that is not answering. NOT a verdict on the run — nothing
// here ever decides a run failed. It exists because a persistently failing read leaves the
// LAST GOOD detail in the cache (retries are off app-wide), so `status` stays 'Processing',
// the effect never disarms, and the interval would run for as long as the tab is open. Set
// past the server's own 30-minute watchdog so a reachable server always gets to speak first.
//
// That ordering is load-bearing, not cosmetic: it is the only reason the bound is not a silent
// hang. `evaluation_calculation_spec.rb` pins this constant against `PROCESSING_TIMEOUT` — but
// only its DEFAULT, because `processing_timeout` honours EVAL_PROCESSING_TIMEOUT_MINUTES, which
// no spec can see. Raise that env var to 35+ and every tick in the window becomes a no-op and
// the silent spinner is the general case. Raise this constant with it.
const RUN_RECOVERY_MAX_MS = 35 * 60 * 1000; // 35 minutes

// Everything a finished run makes stale, as DATA rather than as two code paths (ONCALL-937).
// There are two ways to learn a run settled — the Pusher event and the recovery re-read — and
// they must refresh exactly the same set; a read added to one and forgotten in the other is a
// silently stale screen. Splitting the table by what the key is SCOPED to also makes the one
// real asymmetry explicit: the calc-scoped keys below carry the run id and are safe to
// invalidate from any run, the package-scoped ones do not and are not.
//
// Exported so the tests can assert both paths against this table instead of restating it.
export const CALC_SCOPED_COMPLETION_KEYS = [
  EVAL_RESULT_QUERY_KEY,
  CASHFLOWS_BY_MONTH_QUERY_KEY,
] as const;

export const PACKAGE_SCOPED_COMPLETION_KEYS = [
  MASTER_DATA_QUERY_KEY,
  FILTER_OPTIONS_QUERY_KEY,
  EXECUTIVE_SUMMARY_QUERY_KEY,
  MAP_ELEMENTS_QUERY_KEY,
  VALUE_ROLLUP_QUERY_KEY,
  VISUAL_SUMMARY_QUERY_KEY,
  // A run re-run in place records its asset list again, so its "(N of M Subtracts)" can change.
  PARTIAL_TRACTS_QUERY_KEY,
] as const;

/** What `useEvalRunSync` returns — the derived run flag for the viewed eval. */
export interface EvalRunSync {
  /** True while the viewed eval is `Processing` (derived from calc-detail, not a client flag). */
  isCalculating: boolean;
}

/**
 * The SOLE Pusher subscriber + the server-state-driven run sync for the VIEWED eval.
 * There is NO client run-tracking — the single source of truth is the eval's
 * `useCalcDetail`: its `notifications` block is present iff the BE calc is `Processing`.
 *
 * `useJobCompletion` subscribes WHILE `notifications` is present — covering a fresh POST
 * (via the cache the 202 seeds), a switch to an already-running eval, and a refresh-resume,
 * uniformly — and drives THREE triggers into one `reconcile`: the subscription being
 * registered (which closes the publish-before-subscribe race, ONCALL-937), each
 * `calculation_result` event, and a poll backstop armed only while the run is Processing.
 *
 * The reconcile invalidates `[CALC_DETAIL, calcId]` and, if the REFRESHED RECORD is terminal,
 * the completion table (`CALC_SCOPED_COMPLETION_KEYS` + `PACKAGE_SCOPED_COMPLETION_KEYS`).
 * The event's own payload is never consulted for that decision: it is a hint that something
 * may have changed, never the change itself, so nothing a stale or out-of-order publish
 * claims can reach the screen. The detail refetch drops `notifications` (→ the subscription
 * is released) and flips `status` to `Completed`/`Error`, which re-opens the `eval_result`
 * gate and the `useCalcLifecycle.canFetch` gate — the now-stale reads refetch with the run's
 * results. Cleanup unsubscribes on a calcId/channel change — exactly one subscribe per
 * channel.
 *
 * A second effect fires the optional `onRunCompleted` / `onRunFailed` callbacks off the
 * AUTHORITATIVE detail status (server truth, not the event payload) — the seam the form
 * owner (`Eval3NewContent`, T19) uses to rebaseline the form (`form.reset`) + toast on
 * success, or surface the error while keeping the form dirty. Asymmetric rules:
 * `onRunCompleted` fires only on the observed `Processing → Completed` transition (a
 * switch/load landing on an already-Completed eval stays silent); `onRunFailed` fires on
 * ANY first observation of `Error` — the in-session transition AND arriving at an
 * already-failed eval (switch / reload / deep-link) — because a failed run is a standing
 * state the user must see, not a one-off event. The effect is keyed only on the status
 * observation (not the callback identities), so a fresh callback from the owner can't
 * re-run it — safe because the callback fires synchronously within the status render.
 *
 * @param {object} [options] - Optional completion callbacks (wired by the form owner).
 * @param {Function} [options.onRunCompleted] - Fired once on the viewed run's Processing → Completed transition.
 * @param {Function} [options.onRunFailed] - Fired (message arg) whenever the viewed eval is first observed in Error.
 * @return {EvalRunSync} `{ isCalculating }`, derived from `status === 'Processing'`.
 */
export function useEvalRunSync(options?: {
  onRunCompleted?: () => void;
  onRunFailed?: (message: string) => void;
}): EvalRunSync {
  const { packageId, calculationId } = useEval3Page();
  const queryClient = useQueryClient();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const channel = calcDetail?.notifications?.channel;
  // The completion event name comes from the BE's notifications descriptor (the contract),
  // NOT a hardcoded literal — so a BE rename can't silently leave the run stuck Processing
  // (mirrors the legacy subscriber's `events[0].name`).
  const eventName = calcDetail?.notifications?.events?.[0]?.name;

  // Everything that must go stale once the viewed run reaches a terminal status. Extracted
  // (ONCALL-937) because there are now TWO ways to learn a run settled — the Pusher event and
  // the recovery re-read — and a run recovered by the second must refresh exactly what a run
  // completed by the first does. Letting the recovery path clear only the spinner would leave
  // the pre-run numbers on screen under a settled status, which is worse than the hang it
  // replaced. Idempotent: invalidation marks stale, so running it twice costs nothing.
  const refreshRunScopedReads = useCallback(
    // `includePackageScoped` is false only for a recovery re-read whose eval is no longer the
    // one on screen: the keys below the split carry no calculationId, so invalidating them
    // from a run the user has left would reach into the view they are now looking at. The
    // keys above the split carry it and are always safe.
    (includePackageScoped = true) => {
      for (const readKey of CALC_SCOPED_COMPLETION_KEYS) {
        void queryClient.invalidateQueries({
          queryKey: [readKey, calculationId],
        });
      }
      if (!includePackageScoped) return;
      // The run just produced results: every calc-scoped read (disabled while the
      // calc was Processing — `useCalcLifecycle.canFetch`) must refetch. Invalidation marks
      // them stale NOW; the detail refetch's status flip re-enables them, which
      // triggers the actual refetch. Required for update-in-place, whose cache
      // entries predate the run (incl. `filter_options`' `staleTime: Infinity`).
      // Scoped by packageId (other packages' entries are untouched); THIS package's
      // package-mode entries are included on purpose — the finished run may be the
      // package's new "latest completed calc" baseline.
      // The ValueRollup table + the Visual Summary chart are packageId-scoped +
      // gated on `status === 'Completed'` exactly like master_data, so they join the
      // same invalidate-then-refetch-on-status-flip path. The static
      // `value_rollup_options` catalog is deliberately NOT here (a run doesn't change
      // the vocabulary).
      for (const readKey of PACKAGE_SCOPED_COMPLETION_KEYS) {
        void queryClient.invalidateQueries({
          queryKey: [readKey, packageId],
        });
      }
    },
    [packageId, calculationId, queryClient]
  );

  // Which eval the user is actually looking at, readable from an awaited continuation that
  // may have outlived the effect that started it. See the package-scoped note on
  // `refreshRunScopedReads`: its package-scoped half is the one part that is NOT self-keyed,
  // so it is gated on the viewed id via this ref rather than on effect teardown. Gating it on
  // teardown — a `cancelled` flag — would be unreliable in both directions: cleanup ordering
  // against an awaited continuation is not a guarantee this code should rest correctness on.
  //
  // Reachable in principle, hard to force, and kept for that reason. `reconcile`'s
  // continuation floats past effect teardown, and this ref is reassigned on the switch
  // re-render, so a switch during `await invalidateQueries` does land here with NEW !== OLD.
  // What makes it hard to construct is the second condition: the old query's refetch must
  // still deliver `Completed` after losing its last observer, and today the abort cancels it
  // first — so the continuation finds the OLD cached status and returns at the guard above.
  // (Once the await resolves the rest runs synchronously, so a re-render cannot interleave
  // after that point.)
  //
  // That makes the gate's necessity contingent on react-query's cancellation behaviour, which
  // is not an invariant this hook should rest correctness on. What it prevents is not cosmetic:
  // `refreshRunScopedReads(true)` invalidates PACKAGE_SCOPED_COMPLETION_KEYS, which carry no
  // calculationId, so a completion from an eval the user has left would reach into the view
  // they are now on. No test covers the interleave — `follows the user to the eval they switch
  // to` switches before any re-read is in flight (ONCALL-937 review, #7573).
  const viewedCalcIdRef = useRef(calculationId);
  viewedCalcIdRef.current = calculationId;

  // The authoritative read, and the ONLY thing any trigger does (ONCALL-937). Three things
  // can tell us the viewed run may have moved — the subscription being registered, a
  // `calculation_result` event, and a poll tick — and all three land here. None of them
  // passes any state along: the record answers. The event is a hint, never the data.
  //
  // Nothing here decides anything, and — unlike the legacy page — this one binds no
  // payload-consuming handler of its own, so every status it shows came from the record. A
  // false failure is therefore ruled out by the shape of this page, not by a runtime guard.
  // That property belongs to the PAGE, not to `useJobCompletion`: the module guarantees it
  // only for state routed through `reconcile` (see its JSDoc). It also helps SETTLE the run, because
  // `GET /evaluations/calculations/:id` runs `check_processing_timeout!` as a before_action
  // (calculations_controller.rb) — a run that stopped responding is marked Error by the
  // server on this very read and comes back as a real, server-authored failure. That
  // before_action only fires when someone opens the eval, which is why
  // `Scheduled::EvaluationCalculationWatchdogJob` exists as well. Between the two, terminal
  // status is the server's guarantee; a client-side verdict could only ever be less accurate.
  //
  // The invalidation is unconditional — it runs on every trigger, terminal or not
  // (INS-12398). The detail carries the authoritative status AND the `notifications`
  // descriptor, and since eval-start moved into a job that descriptor CHANGES mid-run: before
  // dispatch the BE names this record's own channel (the eval-calculator's id does not exist
  // yet), after dispatch it names the calculator's. Re-reading is the only way to collect the
  // new one; `useJobCompletion` then re-binds on it. Without that the client would sit on the
  // pre-dispatch channel and never see the calculator's completion.
  //
  // The DETAIL half cannot cross runs: `[CALC_DETAIL_QUERY_KEY, calculationId]` already
  // carries the run's identity, so a read still in flight for run A lands in A's cache entry
  // and can never stamp B's. The same is NOT true of everything this does —
  // `PACKAGE_SCOPED_COMPLETION_KEYS` carry no calculationId at all, which is exactly why
  // `viewedCalcIdRef` gates them above. (An earlier version of this sentence said "the only
  // write is an invalidation of [CALC_DETAIL...]" and so read as a blanket guarantee,
  // contradicting the gate a few lines up.)
  //
  // It depends on this hook's own `useCalcDetail` observer being mounted — `invalidate`
  // refetches ACTIVE queries, and the observer is co-located here, so that holds by
  // construction. If this ever moves away from that observer it needs `refetchType: 'all'`.
  const reconcile = useCallback(async () => {
    if (calculationId === null) return;
    await queryClient.invalidateQueries({
      queryKey: [CALC_DETAIL_QUERY_KEY, calculationId],
    });
    // Reading the cache AFTER the invalidation settles is what tells a finished run from one
    // still going, without believing anything an event said.
    const fresh = queryClient.getQueryData<CalcDetail>([
      CALC_DETAIL_QUERY_KEY,
      calculationId,
    ]);
    if (fresh?.status !== 'Completed' && fresh?.status !== 'Error') {
      return;
    }
    refreshRunScopedReads(viewedCalcIdRef.current === calculationId);
  }, [calculationId, queryClient, refreshRunScopedReads]);

  const isProcessing = calcDetail?.status === 'Processing';
  // The poll's delay runs from the cache entry's own `dataUpdatedAt`, which react-query
  // stamps every time this key is written.
  //
  // `createdAt` is the obvious candidate and is wrong: it is the RECORD's creation date (the
  // Package Overview's "Eval Date"), and a re-run reuses its record — `reuse_existing_
  // calculation` rewrites status and `updated_at` but never `created_at`. On a re-run of a
  // week-old eval it is a week stale, the delay computes to zero, and recovery would hammer
  // the detail from the first second of the most common run there is.
  //
  // `dataUpdatedAt` is immune to that for the same reason a plain arming clock would be, and
  // it is BETTER WHILE THE CACHE ENTRY SURVIVES: within react-query's `gcTime` (5 minutes by
  // default from the moment the last observer unmounts) it also covers RETURNING to a run that
  // has been going a while — the wait already served is carried, rather than a fresh three
  // minutes of silence being bought on every visit.
  //
  // Past that window the entry is evicted, `getQueryState` returns undefined, and
  // `armJobCompletion` falls back to its arming clock — which is precisely the behaviour this
  // paragraph claims to improve on. So: better inside the window, no worse outside it, and
  // never worse than `createdAt`. The advantage is real but bounded, and the bound is short;
  // do not read it as a general property of returning to an old run.
  //
  // The 202 seed is a `setQueryData`, so it stamps the write and a freshly started Calculate
  // still gets its full window — which is the case the delay exists for. It is read once, when
  // the poll arms (`useJobCompletion` keeps it out of its dependencies), so a detail refetch
  // mid-run cannot restart the delay and stop it ever firing.
  const recoveryArmedAt = queryClient.getQueryState([
    CALC_DETAIL_QUERY_KEY,
    calculationId,
  ])?.dataUpdatedAt;

  // Subscribe while the viewed eval is Processing (`notifications` is present iff the BE calc
  // is Processing), and reconcile on all three triggers. No `onExpired`: nothing on this page
  // ever decides a run failed. `RUN_RECOVERY_MAX_MS` only stops TALKING to a server that is
  // not answering — it surfaces nothing and sits past the server's own 30-minute watchdog, so
  // a reachable server always gets to speak first.
  //
  // WHAT HAPPENS AT THE BOUND, since the code alone does not say it: the poll stops, nothing
  // re-arms it, and the user is told nothing. The spinner stays, because `status` is still
  // whatever the last good read said. That is a deliberate choice, not an oversight:
  //   - NOT a re-arm. The server's own watchdog settles the record at 30 minutes, so a record
  //     still Processing at 35 is anomalous rather than slow, and re-arming would be polling
  //     forever by another name — the exact thing the bound exists to prevent.
  //   - NOT a client-side verdict. `onExpired` would have this page invent a failure, which is
  //     the defect the legacy half of ONCALL-937 fixed. A client that decides nothing cannot
  //     be wrong about the run.
  //   - So the gap is SILENT, but narrower than "past 35 minutes": the poll is itself the
  //     mitigation. Each tick refetches the detail, whose action runs
  //     `check_processing_timeout!` (before_action, 30 minutes) — so every tick from 30 to 35
  //     asks the server to settle, and a stuck run comes back `Error` and disarms this effect
  //     BEFORE the bound. Only a double failure (that before_action AND the scheduled
  //     watchdog) reaches it silently. Whether to surface anything there is a product call,
  //     raised rather than guessed — and the honest message would be about CONNECTIVITY, not
  //     the run, since by then the server has settled the record in every normal case.
  //
  // The poll is armed off `isProcessing` rather than off the channel so a settled page
  // schedules nothing at all, however long it sits there — and so a Processing run whose
  // detail somehow arrived without `notifications` still has a way back.
  useJobCompletion({
    channel: calculationId === null ? null : channel,
    events: eventName ? [eventName] : undefined,
    reconcile,
    poll:
      calculationId !== null && isProcessing
        ? {
            delayMs: RUN_RECOVERY_DELAY_MS,
            intervalMs: RUN_RECOVERY_INTERVAL_MS,
            maxMs: RUN_RECOVERY_MAX_MS,
            since: recoveryArmedAt,
          }
        : null,
  });

  // Fire the optional completion callbacks off the AUTHORITATIVE detail status. The
  // effect is intentionally keyed ONLY on calcId/status/error — NOT the callback
  // identities — so a fresh inline callback from the owner can't re-run it (safe: the
  // callback runs SYNCHRONOUSLY in the status-change render, so the captured `options`
  // is current). The two callbacks deliberately have DIFFERENT firing rules:
  //  - `onRunCompleted` fires only on the observed `Processing → Completed` TRANSITION
  //    of the same eval — a switch/load landing directly on a Completed eval must not
  //    spuriously toast "complete" (an eval SWITCH re-baselines without firing).
  //  - `onRunFailed` fires on ANY first observation of `Error` (the transition, a
  //    switch to an errored eval, a load/refresh resolving to one) — a failed run is a
  //    STANDING state that must be announced every time the user arrives at it, not
  //    only when they happened to watch it fail. Repeat observations of the same
  //    settled Error (refetches, re-renders) stay silent.
  const status = calcDetail?.status;
  const error = calcDetail?.error;
  const prevStatusRef = useRef<CalcStatus | undefined>(undefined);
  const prevCalcIdRef = useRef<number | null>(null);
  useEffect(() => {
    // A calcId change is a fresh context: compare against no prior status (so eval A's
    // last status can't read as eval B's transition).
    const prev =
      prevCalcIdRef.current === calculationId
        ? prevStatusRef.current
        : undefined;
    prevCalcIdRef.current = calculationId;
    prevStatusRef.current = status;
    if (status === 'Error' && prev !== 'Error') {
      options?.onRunFailed?.(error ?? 'Calculation failed.');
    } else if (prev === 'Processing' && status === 'Completed') {
      options?.onRunCompleted?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire on the status observation only, not on callback identity
  }, [calculationId, status, error]);

  return { isCalculating: status === 'Processing' };
}

// ─── P8 · Content Export (Slice 1) ───

/**
 * The combined content-export lifecycle status the modal renders from:
 *  - `idle` — nothing in flight.
 *  - `uploading` — capturing charts + uploading the multipart POST (the mutation is in flight).
 *  - `processing` — the 202 is in, the ZIP is building on the BE (awaiting the Pusher event).
 *  - `complete` — the completion event fired; the download was triggered.
 *  - `failed` — the POST failed, or the completion event reported failure.
 */
export type ContentExportStatus =
  'idle' | 'uploading' | 'processing' | 'complete' | 'failed';

/** One chart to capture to PNG at export time (Charts & Visuals now; Reserves in Phase 5). */
export interface ContentExportImageCapture {
  /** The `charts[key]` name (a chart's catalog/instance id, or the fixed reserves key). */
  key: string;
  /** Renders the chart to a PNG/JPEG Blob at the given options (null when it can't capture). */
  exportToBlob: (opts: ExportImageOptions) => Promise<Blob | null>;
}

/** What the connected owner hands `start()` — assembled at Export-click time. */
export interface ContentExportVariables {
  /** The JSON `payload` part (checked data sections + page-state filters/config). */
  payload: ContentExportPayload;
  /** The checked image charts to capture (empty ⇒ payload-only, data sections without images). */
  imageCaptures: ContentExportImageCapture[];
  /** The shared Image Options applied to every captured chart. */
  imageOptions: ExportImageOptions;
}

/** What `useContentExport` returns. */
export interface UseContentExport {
  /** The combined lifecycle status (see {@link ContentExportStatus}). */
  status: ContentExportStatus;
  /** A user-facing error message (POST failure or a `failed` completion event), else null. */
  error: string | null;
  /**
   * True for the whole in-flight window — `uploading` (capturing + POST) AND `processing`
   * (awaiting the completion event, after the modal has closed) — so the title-bar Export
   * trigger can show an in-progress indicator until the export finishes.
   */
  isExporting: boolean;
  /** Kick off an export: capture the charts, POST the multipart body, await completion. */
  start: (variables: ContentExportVariables) => void;
  /** Clear the status/error back to idle (e.g. on modal re-open). */
  reset: () => void;
}

/**
 * The content-export engine — a React Query `useMutation` for the multipart POST plus a
 * Pusher completion effect, mirroring `useCalculate` + `useEvalRunSync` (the house async
 * pattern), NOT the hand-rolled `useServerExport`.
 *
 * The mutation's `mutationFn` captures the checked charts to PNG **sequentially** (the
 * SciChart exporter mutates the live surface then restores it, so concurrent captures corrupt
 * frames — and one chart's failure only skips that key, never aborts the export), builds the
 * `FormData` (`payload` JSON part + `charts[key]` parts), and POSTs via the ky `api` instance
 * (`{ body: formData }` — the instance sets no default Content-Type, so fetch picks the
 * multipart boundary and the CSRF hook still applies), returning the 202.
 *
 * Completion arrives over Pusher (not the HTTP response — a socket wait a mutation can't
 * model): a subscribe-effect keyed on the 202's per-export `notifications.channel` binds the
 * completion event → on `complete` triggers the ZIP download (`downloadFromUrl`), on `failed`
 * surfaces the error. Cleanup unbinds + unsubscribes; a new export re-keys the effect, so a
 * superseded export's late event is ignored (we've unsubscribed its channel). No cache
 * involvement — the export yields a file, not app state. Meant to be owned at an
 * always-mounted node so closing the modal never aborts the in-flight export.
 *
 * @return {UseContentExport} The status, error, `isExporting` flag, and `start` / `reset`.
 */
export function useContentExport(): UseContentExport {
  const { calculationId, packageId } = useEval3Page();

  // The phase AFTER the POST succeeds — driven by the per-export Pusher channel (completion
  // can't be a mutation result). `idle` until a 202 lands. `uploading`/POST-error come from
  // the mutation state and are folded into the combined `status` below.
  const [phase, setPhase] = useState<
    'idle' | 'processing' | 'complete' | 'failed'
  >('idle');
  const [resultError, setResultError] = useState<string | null>(null);

  const mutation = useMutation<
    ContentExport202Response,
    Error,
    ContentExportVariables
  >({
    mutationFn: async (variables) => {
      // Capture SEQUENTIALLY — see the hook JSDoc. A per-chart try/catch keeps one failed
      // capture from sinking the export; a null blob (nothing to capture) is treated the
      // same. Failures are recorded + logged (NOT silently dropped), so a missing chart is
      // diagnosable rather than a silent gap in the ZIP.
      const chartBlobs: ContentExportChartBlob[] = [];
      const missing: string[] = [];
      for (const capture of variables.imageCaptures) {
        try {
          const blob = await withCaptureTimeout(
            capture.exportToBlob(variables.imageOptions),
            capture.key
          );
          if (blob) {
            chartBlobs.push({ key: capture.key, blob });
          } else {
            missing.push(capture.key);
          }
        } catch (error) {
          missing.push(capture.key);
          console.warn(
            `[content-export] chart "${capture.key}" failed to capture`,
            error
          );
        }
      }
      if (missing.length > 0) {
        console.warn(
          `[content-export] captured ${chartBlobs.length}/${
            variables.imageCaptures.length
          } chart(s); not included: ${missing.join(', ')}`
        );
      }
      const formData = buildContentExportFormData(
        variables.payload,
        chartBlobs
      );
      const url = calculationId
        ? `evaluations/calculations/${calculationId}/downloads/content_export`
        : `packages/${packageId}/downloads/content_export`;
      return api
        .post(url, { body: formData, timeout: EVAL3_REQUEST_TIMEOUT_MS })
        .json<ContentExport202Response>();
    },
    onSuccess: () => {
      // 202 accepted — now wait for the Pusher completion event (subscribe effect below).
      setResultError(null);
      setPhase('processing');
    },
  });

  const { mutate, reset: resetMutation } = mutation;
  const channel = mutation.data?.notifications.channel;
  // The completion event name comes from the 202's notifications descriptor (the contract),
  // not a hardcoded literal — a BE rename can't silently strand the export (like useEvalRunSync).
  const eventName = mutation.data?.notifications.events?.[0]?.name;

  // Completion subscribe-effect — keyed on the per-export channel. A new export re-keys this,
  // tearing down the old subscription (its late events ignored), and attaches to the new one.
  useEffect(() => {
    if (!channel || !eventName) {
      return undefined;
    }
    const subscription = subscribe(channel);
    const handleResult = (data?: ContentExportResult) => {
      if (data?.status === 'complete') {
        // Download the signed ZIP in-tab (no popup-blocker risk; the response is an
        // attachment, so the browser saves it without navigating away).
        if (data.download_url) {
          downloadFromUrl(data.download_url);
        }
        setPhase('complete');
      } else if (data?.status === 'failed') {
        setResultError(data.error ?? 'Export failed.');
        setPhase('failed');
      }
    };
    subscription.bind(eventName, handleResult);
    return () => {
      subscription.unbind(eventName, handleResult);
      subscription.unsubscribe();
    };
  }, [channel, eventName]);

  const start = useCallback(
    (variables: ContentExportVariables) => {
      // Drop any prior 202 first so the completion effect (keyed on `mutation.data`'s channel)
      // tears down the previous export's subscription immediately — before the new POST
      // resolves — so a superseded export's late Pusher event can't trigger a stray download.
      resetMutation();
      setPhase('idle');
      setResultError(null);
      mutate(variables);
    },
    [mutate, resetMutation]
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setResultError(null);
    resetMutation();
  }, [resetMutation]);

  // Fold the mutation state (capture+upload / POST error) and the post-202 phase into one
  // status: uploading while the mutation runs, then the Pusher-driven phase, then a bare POST
  // error if the mutation failed before a 202.
  let status: ContentExportStatus = 'idle';
  if (mutation.isPending) {
    status = 'uploading';
  } else if (phase !== 'idle') {
    status = phase;
  } else if (mutation.isError) {
    status = 'failed';
  }

  return {
    status,
    error: resultError ?? mutation.error?.message ?? null,
    isExporting: status === 'uploading' || status === 'processing',
    start,
    reset,
  };
}

/** The default confirm shown before leaving an eval with uncalculated edits. */
export const UNSAVED_CHANGES_MESSAGE =
  "You have unsaved changes that haven't been calculated. Leave anyway?";

/**
 * Confirm-before-leaving guard for uncalculated edits. Registered ONCE at the form owner
 * (`Eval3NewContent`, T19) with the working-config form itself, so it intercepts EVERY
 * in-app route change — the evals tree, the workspace-management table, browser
 * back/forward — not just specific click handlers the way a per-callsite guard would.
 *
 * Uses React Router 5's `history.block` with a prompt FUNCTION that reads
 * `form.state.isDirty` AT TRANSITION TIME (a live read off the form store, not a
 * React-subscribed boolean): dirty → return the message (the Router surfaces it via its
 * default `getUserConfirmation`, `window.confirm`); clean → return `undefined` (history
 * allows the transition silently). The live read is load-bearing — `useCalculate`
 * rebaselines the form on the 202 and pushes the new route in the SAME tick, so
 * Calculate's own navigation must observe the just-cleaned form without waiting for a
 * re-render (a boolean-keyed block's cleanup only runs after the next render and would
 * wrongly prompt). The form handle is identity-stable, so the block registers once. (An
 * in-flight run alone needs no guard — it's server-safe; only uncalculated EDITS do.)
 *
 * Form meta alone is not enough for the interest positions: the mid-run
 * `form.reset` rebaseline (onRunCompleted) keeps concurrent interest edits' VALUES
 * while eating their dirty meta, so a meta-only guard lets navigation silently drop
 * them. The prompt therefore also compares the live `interestItems` against the
 * viewed eval's committed baseline (`interestItemsDiverged`) — except when they
 * match this session's last-submitted run (`lastSubmittedInterestItems`), which
 * proves them persisted and lets Calculate's own fork/draft navigation through
 * (its destination eval's detail isn't the render-captured baseline yet).
 *
 * @param {object} form - The lifted working-config form handle (dirtiness read live).
 * @param {unknown} committedInterestItems - The viewed eval's committed
 *   `configurations.interest_items` (snake_case echo; undefined on a draft).
 * @param {string} [message] - The confirmation message shown before leaving.
 * @return {void}
 */
export function useUnsavedChanges(
  form: WorkingConfigForm['form'],
  committedInterestItems: unknown,
  message: string = UNSAVED_CHANGES_MESSAGE
): void {
  const history = useHistory();
  // Ref, not an effect dep: the committed baseline refreshes per render without
  // re-registering the block, and the prompt still reads the latest at transition time.
  const committedRef = useRef(committedInterestItems);
  committedRef.current = committedInterestItems;
  useEffect(() => {
    // `history.block` returns the unblock fn — cleanup releases it on unmount. The
    // prompt fn returning `undefined` lets a clean-form transition through silently.
    return history.block(() => {
      if (form.state.isDirty) {
        return message;
      }
      const live = form.state.values.interestItems;
      const unsubmittedInterestEdits =
        interestItemsDiverged(live, committedRef.current) &&
        !isEqual(live, lastSubmittedInterestItems);
      return unsubmittedInterestEdits ? message : undefined;
    });
  }, [form, message, history]);
}

// ─── P4 · Cases & Per-Area Overrides ───

/** Query-key prefix for the package's case coverage list (picker + coverage tile). */
export const CASE_COVERAGE_QUERY_KEY = 'eval3-new:case-coverage' as const;

/**
 * A typed `coverage_list` failure. The endpoint returns a structured
 * `{ error: { code, message, retryable } }` envelope (INS-12103): a
 * `query_timeout` is deterministic for the package — retrying can never
 * succeed — so the BE marks it `retryable: false` and the picker drops its
 * Retry affordance. Anything without the envelope (network blip, proxy 502)
 * stays retryable.
 */
export class CoverageListError extends ApiEnvelopeError {
  static override errorName = 'CoverageListError';
}

/**
 * Fetches the package's evaluation-case coverage list
 * (`GET /evaluation_cases/coverage_list?packageId&threshold=0`) — the case
 * picker's tiles AND the main view's coverage tile (the active case's entry),
 * one shared cache entry. Fetched ONCE with `threshold=0` (every case lands in
 * `aboveThreshold`); the picker's Min-coverage slider re-partitions CLIENT-side
 * (`partitionCoverage`), so slider moves never refetch and drill-in round-trips
 * are cache hits. Cases with zero package overlap are omitted by the BE;
 * coverage depends only on the case's own shape-carrying scenarios (never on
 * per-run overrides or the deck), so 5 min staleness is plenty fresh.
 *
 * The working Type Curve scenario rides the request and the cache key: coverage counts
 * LTD places, and WHICH places exist is the scenario's answer (INS-10669) — without it a
 * scenario eval's %s described Legacy's population (PR #7365 review). The case picker's list
 * has no run, so a run's manager folds do not affect it; the eval page's coverage preview
 * takes them (EvaluationCase::RunOverrides).
 *
 * @param {number} packageId - The package the coverage is computed against.
 * @return {object} The React Query result; `data` is the `CaseCoverageListResponse`.
 */
export function useCaseCoverageList(
  packageId: number
): UseQueryResult<CaseCoverageListResponse, Error> {
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  return useQuery<CaseCoverageListResponse, Error>({
    queryKey: [CASE_COVERAGE_QUERY_KEY, packageId, typeCurveScenarioId],
    queryFn: async ({ signal }) => {
      try {
        return await api
          .get('evaluation_cases/coverage_list', {
            searchParams: {
              packageId,
              threshold: 0,
              ...(typeCurveScenarioId !== null && { typeCurveScenarioId }),
            },
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<CaseCoverageListResponse>();
      } catch (err) {
        // Types the envelope so the picker can distinguish a deterministic query_timeout
        // from a transient failure. Cancellations rethrow untouched; see the helper.
        return throwTypedEnvelopeError(err, CoverageListError);
      }
    },
    // Zero automatic retries — the app-wide default (app-shell/app.tsx sets
    // retry: 0 to surface errors explicitly), NOT react-query's library
    // default of 3. Stated here because a per-query `retry` would silently
    // override the client default: on this endpoint an unenveloped failure
    // (Rack::Timeout, ky TimeoutError) can burn EVAL3_REQUEST_TIMEOUT_MS per
    // attempt, so the user's Retry button is the only deliberate retry path.
    retry: false,
    staleTime: 300_000, // 5 min — a case's scenarios change on its case page, not here
    // Case-level coverage answers from a cache the BE warms in the background, with
    // `status: 'calculating'` until every case is counted: poll at the interval it names until it
    // is 'ready'.
    refetchInterval: (query) =>
      calculatingInterval(query.state.data, query.state.status),
  });
}

/**
 * A typed `uncovered` failure: the case-level endpoint answers with the
 * same `{ error: { code, message, retryable } }` envelope as `coverage_list`, so a deterministic
 * `query_timeout` can drop its Retry.
 */
export class UncoveredError extends ApiEnvelopeError {
  static override errorName = 'UncoveredError';
}

/**
 * Reads an evaluation case's detail (`GET /evaluation_cases/:id`) — the active
 * case's name, its own price deck (`priceDeckId` / `priceDeckName`, the deck a
 * run uses with no pick of its own) and its Type Curve. The Scenario assumptions
 * rows come from `run_scenarios` (useRunScenarios), not from here.
 * Enabled only while a case is selected (`caseId !== null` — the working config's
 * `evaluationCaseId`); keyed per case so switching cases is a cache hit on return.
 * Case authoring lives in the separate Case Management app, so 5 min staleness is
 * plenty fresh.
 *
 * @param {number | null} caseId - The selected case id, or null (no case → no fetch).
 * @return {object} The React Query result; `data` is the `EvaluationCaseDetail`.
 */
export function useEvaluationCaseDetail(
  caseId: number | null
): UseQueryResult<EvaluationCaseDetail, Error> {
  // The case page's query for this endpoint (caseQueryOptions): one cache entry per case for both
  // pages, filled by one fetcher, so the coverage tearsheet reads the case the eval page already
  // holds, and a save on the case page (which writes that entry) reaches this page. Unlike the case
  // page, this page takes the client's retry setting, as its other reads do, except that a 4xx is
  // never retried: the server refused, and asking again repeats the refusal.
  const queryClient = useQueryClient();
  return useQuery<EvaluationCaseDetail, Error>({
    ...caseQueryOptions<EvaluationCaseDetail>(caseId),
    retry: retryUnlessClientError(
      queryClient.getDefaultOptions().queries?.retry as RetrySetting
    ),
    staleTime: 300_000, // 5 min — case edits happen in the separate Case Management app
    // The case's own Type Curve drives the Type Curve section, its ledger and the TC Manager, so a
    // case fixed in its own tab must show here on return, as the run_scenarios check does.
    refetchOnWindowFocus: 'always',
  });
}

export { RUN_SCENARIOS_QUERY_KEY };

/** What `useRunScenarios` returns: the query, with the last answer for the same case kept. */
export interface RunScenariosResult {
  /** The answer for this case and these overrides, else the last one for this case. */
  data: RunScenariosResponse | undefined;
  /** `data` is an earlier answer for this case (the overrides changed); never another case's. */
  isPreviousData: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * What a case-level run of the active case would use with this run's
 * overrides, per domain, and whether it can start (`POST /evaluation_cases/:id/run_scenarios`, as
 * EvaluationCase::Readiness decides it). The domain rows, the "Case incomplete" note and the
 * Calculate gate all read it, so they cannot disagree with the server's preflight. Off with no
 * case.
 *
 * While the overrides change, the last answer for the same case stays (`isPreviousData`), so the
 * rows don't flash; a switch to another case shows nothing of the previous case's. It is re-asked
 * when the window regains focus (the case may have been fixed in its own tab) and after a case
 * save (the case page invalidates RUN_SCENARIOS_QUERY_KEY).
 *
 * @param {WorkingConfigForm} workingConfig - The eval settings form.
 * @return {RunScenariosResult} The result.
 */
export function useRunScenarios(
  workingConfig: WorkingConfigForm
): RunScenariosResult {
  const caseId = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseId
  );
  const overrides = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseOverrides
  );
  const priceDeckId = useStore(
    workingConfig.form.store,
    (state) => state.values.priceDeckId
  );
  const asked = JSON.stringify(runScenarioOverrides(overrides, priceDeckId));

  const query = useQuery<RunScenariosResponse, Error>({
    queryKey: [RUN_SCENARIOS_QUERY_KEY, caseId, asked],
    queryFn: async ({ signal }) => {
      try {
        // A POST with the overrides as a JSON object in the body; it still writes nothing.
        return await api
          .post(`evaluation_cases/${caseId}/run_scenarios`, {
            json: { overrides: JSON.parse(asked) },
            signal,
            timeout: EVAL3_REQUEST_TIMEOUT_MS,
          })
          .json<RunScenariosResponse>();
      } catch (err) {
        return throwTypedEnvelopeError(err, ApiEnvelopeError);
      }
    },
    enabled: caseId !== null,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: 'always',
  });

  // The last answer, by case: kept across an overrides change, dropped on a case switch.
  const last = useRef<{
    caseId: number | null;
    data: RunScenariosResponse;
  } | null>(null);
  if (query.data !== undefined) last.current = { caseId, data: query.data };
  const kept =
    query.data === undefined && last.current?.caseId === caseId
      ? last.current.data
      : undefined;

  return {
    data: query.data ?? kept,
    isPreviousData: kept !== undefined,
    isLoading: query.isPending && kept === undefined,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Query-key prefix for a case's uncovered detail (the Uncovered drill-in). */
export const CASE_UNCOVERED_QUERY_KEY = 'eval3-new:case-uncovered' as const;

/**
 * Fetches what a case does NOT cover of the package
 * (`GET /evaluation_cases/:id/uncovered?packageId=`) — the Uncovered drill-in's
 * three tabs (assets / units / slots) plus their count tags. Keyed per
 * (case, package) so a picker PREVIEW of another case caches beside the active
 * case's entry, and a preview → back → re-preview round-trip is a cache hit.
 * Mounted only while the drill-in is open (the component fetches on mount);
 * coverage depends only on the case's own scenarios, so 5 min staleness is plenty.
 *
 * @param {number} caseId - The case whose uncovered rows to load.
 * @param {number} packageId - The package the coverage is computed against.
 * @return {object} The React Query result; `data` is the `UncoveredResponse`.
 */
export function useCaseUncovered(
  caseId: number,
  packageId: number
): UseQueryResult<UncoveredResult, Error> {
  // Scenario rides along for the same reason as useCaseCoverageList: the uncovered
  // slots tab enumerates the scenario's LTD population.
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  return useQuery<UncoveredResult, Error>({
    queryKey: [
      CASE_UNCOVERED_QUERY_KEY,
      caseId,
      packageId,
      typeCurveScenarioId,
    ],
    queryFn: async ({ signal }) => {
      const request = api
        .get(`evaluation_cases/${caseId}/uncovered`, {
          searchParams: {
            packageId,
            ...(typeCurveScenarioId !== null && { typeCurveScenarioId }),
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<UncoveredResult>();

      try {
        return await request;
      } catch (err) {
        // Types the envelope so a query_timeout reads as not retryable.
        return throwTypedEnvelopeError(err, UncoveredError);
      }
    },
    staleTime: 300_000, // 5 min — same posture as the coverage list
    // Case-level coverage may still be computing ('calculating', no rows): poll until it is
    // ready.
    refetchInterval: (query) =>
      calculatingInterval(query.state.data, query.state.status),
  });
}

/** Query-key prefix for the coverage drill-in (the affected-slots list). */
export const AFFECTED_SLOTS_QUERY_KEY = 'eval3-new:affected-slots' as const;

/**
 * Fetches the paceable slots behind the coverage counts — the unscheduled +
 * unvalued rows, each labelled with its one reason
 * (`GET /evaluations/calculations/:id/unscheduled_slots`). This is the in-page
 * drill-in behind the coverage readout's "View" affordances (07-COV §2.1,
 * INS-12496): it fetches JSON (`api` sends `Accept: application/json`, so the
 * controller serves the jbuilder) rather than navigating, which is what made the
 * old `<a href>` 406. Mounted only while a drill-in is open; a completed run's
 * affected slots are immutable, so 5 min staleness is plenty.
 *
 * @param {number} calculationId - The calculation whose affected slots to load.
 * @return {object} The React Query result; `data` is the `AffectedSlotsResponse`.
 */
export function useAffectedSlots(
  calculationId: number
): UseQueryResult<AffectedSlotsResponse, Error> {
  return useQuery<AffectedSlotsResponse, Error>({
    queryKey: [AFFECTED_SLOTS_QUERY_KEY, calculationId],
    queryFn: ({ signal }) =>
      api
        .get(`evaluations/calculations/${calculationId}/unscheduled_slots`, {
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<AffectedSlotsResponse>(),
    staleTime: 300_000, // 5 min — a completed run's affected slots don't change
  });
}

/** Query-key prefix for the active price-deck list (the case-global deck dropdown). */
export const PRICE_DECKS_QUERY_KEY = 'eval3-new:price-decks' as const;

/**
 * Endpoint path for the price-deck query. No leading slash — `api` (ky) sets
 * `prefixUrl`, so paths are relative.
 */
export const PRICE_DECKS_URL = 'queries/price_decks';

/**
 * Fetches the ACTIVE price decks for the case-global Price Deck dropdown
 * (`POST /queries/price_decks`). Mirrors the case-editor's request exactly —
 * the body is wrapped under `query` (the controller `wrap_parameters :query` +
 * `params.accept(:query)`), camelCase keys the app deep-underscores
 * (`sortAttribute` → `sort_attribute`, `includeId` → `include_id`, …). The
 * response envelope's `results` are the options; the FE keeps only `{ id, name }`
 * (`PriceDeckOption`). `includeId` forces a specific deck into the results even
 * when archived / off-page (the BE unshifts it) so the currently-selected deck
 * always has an option to render against. Callers pass a STABLE id (the case's
 * own deck), NOT the live selection — a deck picked from the open list is active
 * and already present, so it needs no forcing-in, and keeping `includeId` stable
 * means picking never re-fires this query. The list is package/case-independent
 * (all active decks) and session-stable, so 30 min staleness; keyed by
 * `includeId` so the forced-in deck can't be served from a list fetched without
 * it. Gated (via `options.enabled`) so no fetch runs before a case is selected.
 *
 * @param {number | null} includeId - The deck to force into the results, or null.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true; the caller gates on a case being selected).
 * @return {object} The React Query result; `data` is the `PriceDeckOption[]`.
 */
export function usePriceDecks(
  includeId: number | null,
  options?: { enabled?: boolean }
): UseQueryResult<PriceDeckOption[], Error> {
  return useQuery<PriceDeckOption[], Error>({
    queryKey: [PRICE_DECKS_QUERY_KEY, includeId ?? null],
    queryFn: ({ signal }) =>
      api
        .post(PRICE_DECKS_URL, {
          json: {
            query: {
              status: ['active'],
              sortAttribute: 'updated_at',
              sortDirection: 'DESC',
              pageSize: 500,
              ...(includeId !== null ? { includeId } : {}),
            },
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<{ results: PriceDeckOption[] }>()
        .then((response) => response.results ?? []),
    enabled: options?.enabled ?? true,
    staleTime: 1_800_000, // 30 min — active decks are session-stable
  });
}

/** Query-key prefix for a per-domain scenario list (the scenario-picker options). */
export const DOMAIN_SCENARIOS_QUERY_KEY = 'eval3-new:domain-scenarios' as const;

/**
 * Fetches one domain's scenario options (`POST /queries/{domain}_scenarios`) — the
 * scenario-picker choices for a (area × domain), AND the list the area rows resolve
 * an OVERRIDE's scenario id to a display name against. The endpoint is chosen by
 * `CASE_DOMAIN_ENDPOINTS[domain]`; the body wraps under `query` (the controllers'
 * `wrap_parameters :query` + `params.accept(:query)`, same as `price_decks`),
 * camelCase keys the app deep-underscores. The response envelope's `results` are the
 * options; the FE keeps the `DomainScenarioOption` subset (`{ id, name, summary,
 * updatedAt }` — every domain's jbuilder emits those). Keyed by `domain` alone (the
 * list is package/case-independent) and 30 min stale, so the picker (T14) shares this
 * cache and the areas list resolves override names from it. Gated (via
 * `options.enabled`) so the prefetches don't run before a case is selected.
 *
 * @param {CaseDomain} domain - Which domain's scenarios to load.
 * @param {object} [options] - Query options.
 * @param {boolean} [options.enabled] - Whether to fetch (default true; the caller gates on a case being selected).
 * @return {object} The React Query result; `data` is the `DomainScenarioOption[]`.
 */
export function useDomainScenarios(
  domain: CaseDomain,
  options?: {
    enabled?: boolean;
    /**
     * Per-observer override of the app-wide `false`. 'always' re-reads on every
     * window focus even inside `staleTime` — for a reader whose answer the user
     * fixes in ANOTHER tab (the expense-coverage callout links out to the
     * scenario), where a 30-minute cache would keep a fixed gap on screen. A
     * function decides per focus, from the query.
     */
    refetchOnWindowFocus?: QueryObserverOptions<
      DomainScenarioOption[],
      Error
    >['refetchOnWindowFocus'];
  }
): UseQueryResult<DomainScenarioOption[], Error> {
  return useQuery<DomainScenarioOption[], Error>({
    queryKey: [DOMAIN_SCENARIOS_QUERY_KEY, domain],
    queryFn: ({ signal }) =>
      api
        .post(CASE_DOMAIN_ENDPOINTS[domain], {
          json: {
            query: {
              sortAttribute: 'updated_at',
              sortDirection: 'DESC',
              pageSize: 500,
            },
          },
          signal,
          timeout: EVAL3_REQUEST_TIMEOUT_MS,
        })
        .json<{ results: DomainScenarioOption[] }>()
        .then((response) => response.results ?? []),
    enabled: options?.enabled ?? true,
    staleTime: 1_800_000, // 30 min — scenario lists are session-stable
    // Only set when asked for. An explicit `undefined` would be spread over the
    // QueryClient's `refetchOnWindowFocus: false` default and read as "refetch
    // when stale" for every domain list.
    ...(options?.refetchOnWindowFocus !== undefined && {
      refetchOnWindowFocus: options.refetchOnWindowFocus,
    }),
  });
}
